import React, {useState, useMemo, useEffect, useRef, useCallback, forwardRef, useImperativeHandle} from 'react';
import PropTypes from 'prop-types';
import {useNavigate, useParams, useLocation} from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Mermaid from '../Mermaid';
import MiaPlatformSelector from './MiaPlatformSelector';
import CreateProjectModal from './CreateProjectModal';
import CreateServiceModal from './CreateServiceModal';
import ProjectResourcesTree from './ProjectResourcesTree';
import CollectionEditor from './CollectionEditor';
import PreviewAutoSyncPanel from './PreviewAutoSyncPanel';
import PreviewLogs from './PreviewLogs';
import HelmPreviewPanel from './HelmPreviewPanel';
import FlowVisualization from './FlowVisualization';
import Modal from './Modal';
import ExternalLinkButton from './design/ExternalLinkButton';
import Icon from './icons/Icon';
import EnhancedStatusBar from './EnhancedStatusBar';
import CodeViewWithLogger from './CodeViewWithLogger';
import SplitViewToggle from './SplitViewToggle';
import DynamicTaskPanel from './DynamicTaskPanel';
import UnifiedDashboard from './UnifiedDashboard';
import UnifiedTopBar from './UnifiedTopBar';
import BackgroundAgentsManager from './BackgroundAgentsManager';
import PushModal from './PushModal';
import DashboardView from './DashboardView';
import {getFileIconName} from './icons/fileIcons';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import miaPlatformAuth from '../auth/MiaPlatformAuth';
import syncService from '../services/syncService';
import indexedDbFileStorage, {GIT_STATE} from '../utils/indexedDbFileStorage';
import { useAnalytics } from '../hooks/useAnalytics';
import { DEFAULT_MICROSERVICE_BRANCH } from '../constants/gitConstants';
import indexedDbCollectionStorage from '../utils/indexedDbCollectionStorage';
import {syncAllCollections} from '../utils/crudServiceSync';
import {applyUnifiedDiff, parseUnifiedDiff} from '../utils/diffParser';
import {scheduleAutoCapture, autoCaptureThumbnail, getProjectThumbnail} from '../utils/screenshotCapture';
import {getBffHeaders} from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import {injectErrorCapture as injectIframeErrorCapture} from '../utils/iframeErrorCapture';
import {FRAMEWORK_CONFIG, getLanguageIcon} from '../utils/frameworkConfig';
import {
  detectBackendFramework,
  detectServiceType,
  getContainerRoutingInfo
} from '../utils/frameworkDetection';
import { getGitRef, getResourceGitBranch } from '../utils/gitBranchStorage';
import config from '../config';
import RequirementsDLCPanel from './RequirementsDLC/RequirementsDLCPanel';
import './Canvas.css';

// ============================================================================
// Local Storage Helpers for userId Persistence
// ============================================================================

/**
 * Get userId from localStorage for a given project-service combination
 * @param {string} projectId - Project ID
 * @param {string} serviceName - Service name
 * @returns {string|null} - Stored userId or null if not found
 */
const getUserIdFromStorage = (projectId, serviceName) => {
  try {
    const serviceKey = projectId ? `${projectId}-${serviceName}` : serviceName;
    const storageKey = `preview-userId-${serviceKey}`;
    return localStorage.getItem(storageKey);
  } catch (error) {
    console.error('Error reading userId from localStorage:', error);
    return null;
  }
};

/**
 * Save userId to localStorage for a given project-service combination
 * @param {string} projectId - Project ID
 * @param {string} serviceName - Service name
 * @param {string} userId - User ID to store
 */
const saveUserIdToStorage = (projectId, serviceName, userId) => {
  try {
    const serviceKey = projectId ? `${projectId}-${serviceName}` : serviceName;
    const storageKey = `preview-userId-${serviceKey}`;
    localStorage.setItem(storageKey, userId);
    // eslint-disable-next-line no-console
    console.log(`💾 Saved userId to localStorage: ${storageKey} = ${userId}`);
  } catch (error) {
    console.error('Error saving userId to localStorage:', error);
  }
};

/**
 * Get or create a stable userId for a project-service combination
 * Uses localStorage to persist across page reloads
 * @param {string} projectId - Project ID
 * @param {string} serviceName - Service name
 * @returns {string} - Existing or newly generated userId
 */
const getOrCreateUserId = (projectId, serviceName) => {
  // Try to get existing userId from localStorage
  let userId = getUserIdFromStorage(projectId, serviceName);
  
  if (!userId) {
    // Generate new userId if not found
    userId = crypto.randomUUID();
    saveUserIdToStorage(projectId, serviceName, userId);
    // eslint-disable-next-line no-console
    console.log(`🔑 Generated new User ID for ${projectId || 'no-project'}-${serviceName}: ${userId}`);
  } else {
    // eslint-disable-next-line no-console
    console.log(`♻️  Reusing stored User ID for ${projectId || 'no-project'}-${serviceName}: ${userId}`);
  }
  
  return userId;
};

/**
 * Generate a unique session ID based on tenant, project, service, and user
 * This ensures proper isolation:
 * - Same user + tenant + project + service = same session (even across tabs)
 * - Different users = different sessions
 * - Different projects or tenants = different sessions
 * @param {string} tenantId - Tenant ID
 * @param {string} projectId - Project ID
 * @param {string} serviceName - Service name
 * @param {string} userId - User ID
 * @returns {string} - Session ID
 */
const generateSessionId = (tenantId, projectId, serviceName, userId) => {
  // Session ID format: tenantId-projectId-serviceName-userId
  // This ensures unique sessions per user, per project/tenant combination
  if (tenantId && projectId && serviceName && userId) {
    return `${tenantId}-${projectId}-${serviceName}-${userId}`;
  } else if (projectId && serviceName && userId) {
    return `${projectId}-${serviceName}-${userId}`;
  } else if (serviceName && userId) {
    return `${serviceName}-${userId}`;
  }
  return serviceName || 'default';
};

/**
 * Get currently selected session from localStorage
 * @returns {Object|null} - Selected session data or null
 */
const getSelectedSession = () => {
  try {
    const sessionData = localStorage.getItem('mia_selectedSession');
    if (sessionData) {
      return JSON.parse(sessionData);
    }
    return null;
  } catch (error) {
    console.error('Error reading selected session from localStorage:', error);
    return null;
  }
};

/**
 * Save currently selected session to localStorage
 * @param {Object} sessionData - Session data to store
 */
const setSelectedSession = (sessionData) => {
  try {
    localStorage.setItem('mia_selectedSession', JSON.stringify(sessionData));
    console.log('💾 Saved selected session:', sessionData);
  } catch (error) {
    console.error('Error saving selected session to localStorage:', error);
  }
};

/**
 * Get complete session data from localStorage
 * @param {string} projectId - Project ID
 * @param {string} serviceName - Service name
 * @param {string} userId - User ID
 * @returns {Object|null} - Session data {sessionId, previewUrl, containerPort, language} or null
 */
const getSessionFromStorage = (projectId, serviceName, userId) => {
  try {
    const sessionId = projectId ? `${projectId}-${serviceName}-${userId}` : `${serviceName}-${userId}`;
    const storageKey = `preview-session-${sessionId}`;
    const sessionData = localStorage.getItem(storageKey);
    if (sessionData) {
      return JSON.parse(sessionData);
    }
    return null;
  } catch (error) {
    console.error('Error reading session from localStorage:', error);
    return null;
  }
};

/**
 * Clear all preview-session-* keys from localStorage
 * Used when switching projects to avoid confusion with stale session data
 * Note: This does NOT terminate the actual render sessions - they remain alive
 * so users can reuse them when returning to a project
 */
const clearAllPreviewSessions = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('preview-session-')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    // eslint-disable-next-line no-console
    console.log(`🧹 Cleared ${keysToRemove.length} preview session(s) from localStorage`);
  } catch (error) {
    console.error('Error clearing preview sessions:', error);
  }
};

/**
 * Save complete session data to localStorage
 * @param {string} projectId - Project ID
 * @param {string} serviceName - Service name
 * @param {string} userId - User ID
 * @param {Object} sessionData - Session data to store {sessionId, previewUrl, containerPort, language}
 */
const saveSessionToStorage = (projectId, serviceName, userId, sessionData) => {
  try {
    const sessionId = projectId ? `${projectId}-${serviceName}-${userId}` : `${serviceName}-${userId}`;
    const storageKey = `preview-session-${sessionId}`;
    localStorage.setItem(storageKey, JSON.stringify(sessionData));
    // eslint-disable-next-line no-console
    console.log(`💾 Saved session to localStorage: ${storageKey}`, sessionData);
    
    // Also update mia_selectedSession to track the currently active session
    setSelectedSession({
      ...sessionData,
      projectId,
      serviceName,
      userId,
      lastAccessed: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error saving session to localStorage:', error);
  }
};

/**
 * Helper to determine if running in production environment
 * @returns {boolean} - true if in production
 */
const isProduction = () => {
  return typeof window !== 'undefined' && 
         window.location.origin && 
         !window.location.origin.includes('localhost') && 
         !window.location.origin.includes('127.0.0.1');
};

/**
 * Get the container API URL (direct or through BFF proxy)
 * @param {number} containerPort - Container port (3001 for react, 3011 for node, etc.)
 * @param {string} endpoint - API endpoint path (e.g., '/api/render')
 * @returns {string} - Full API URL
 */
const getContainerApiUrl = (containerPort, endpoint) => {
  // Check if K8s Dynamic Mode is enabled (default: false)
  const k8sDynamicMode = localStorage.getItem('k8sDynamicMode') === 'true';

  const containerMap = {
    3001: 'react-container-live',
    3002: 'nextjs-container-live',
    3003: 'reactnative-container-live',
    3004: 'angular-container-live',
    3005: 'vue-container-live',
    3006: 'jquery-container-live',
    3007: 'aspnet-frontend-container-live',
    3008: 'docusaurus-container-live',
    3011: 'node-container-live',
    3012: 'go-container-live',
    3013: 'python-container-live',
    3014: 'java-container-live',
    3015: 'dotnet-container-live',
    3016: 'rust-container-live',
    3017: 'php-container-live',
    3018: 'kotlin-container-live',
    3019: 'cpp-container-live',
    3020: 'qsharp-container-live',
    3021: 'elixir-container-live',
    3022: 'helm-chart-container-live',
    3030: 'crud-service-universal',
  };

  const containerName = containerMap[containerPort] || 'react-container-live';

  if (isProduction()) {
    // K8s Dynamic Mode: Use /api/bff/preview/* endpoints
    if (k8sDynamicMode) {
      return `${config.bffUrl}/preview${endpoint}`;
    }
    
    // Single Container Mode (Default): Use container-specific endpoints
    // React container uses direct public URL, others use BFF proxy
    //if (containerPort === 3001) {
    // Direct access to react-container-live via public URL
    const publicBaseUrl = config.publicBaseUrl || 'http://localhost:3001';
    return `${publicBaseUrl}/api/preview/${containerName}${endpoint}`;
    //}
    
    // All other containers use BFF proxy
    //return `${config.bffUrl}/${containerName}${endpoint}`;
  }
  
  // In development
  if (k8sDynamicMode) {
    // K8s Dynamic Mode: Use BFF preview endpoints
    return `http://localhost:3100/api/bff/preview${endpoint}`;
  }
  
  // Single Container Mode: Use direct localhost access
  return `/${containerName}${endpoint}`;
};

/**
 * Poll session build status until completion
 * @param {string} containerApiBaseUrl - Base URL for container API
 * @param {string} sessionId - Session ID to poll
 * @param {Function} onProgress - Callback for progress updates (phase, progress, logs)
 * @param {number} maxWaitTime - Maximum time to wait in ms (default 7 minutes for large projects)
 * @returns {Promise<Object>} - Final session result
 */
const pollRenderStatus = async (containerApiBaseUrl, sessionId, onProgress, maxWaitTime = 420000) => {
  const startTime = Date.now();
  const pollInterval = 3000; // Poll every 3 seconds for faster updates
  let consecutiveStartingCount = 0; // Track how long we've been in 'starting' phase
  let lastProgress = 0;
  
  while (Date.now() - startTime < maxWaitTime) {
    try {
      const statusUrl = `${containerApiBaseUrl}/api/preview/${sessionId}/status`;
      const response = await getAuthorizedFetch()(statusUrl, {
        method: 'GET',
        headers: getBffHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`Status check failed: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Status check failed');
      }
      
      const session = data.session;
      
      // Debug: Log what we receive from backend
      console.log('[pollRenderStatus] session data:', {
        buildPhase: session.buildPhase,
        buildProgress: session.buildProgress,
        buildLogsCount: session.buildLogs?.length,
        buildLogs: session.buildLogs
      });
      
      // Call progress callback
      if (onProgress) {
        onProgress(session.buildPhase, session.buildProgress, session.buildLogs);
      }
      
      // Enhanced logging for transparency
      const phaseEmojis = {
        'initializing': '🎬',
        'copying': '📦',
        'installing': '📥',
        'starting': '🚀',
        'complete': '✅',
        'ready': '✅'
      };
      const emoji = phaseEmojis[session.buildPhase] || '⏳';
      // eslint-disable-next-line no-console
      console.log(`${emoji} [Preview] ${(session.buildPhase || 'unknown').toUpperCase()}: ${session.buildProgress}%`, session.buildLogs?.slice(-1)[0]?.message || '');
      
      // FLEXIBLE COMPLETION DETECTION:
      // 1. Explicit completion status
      if (session.buildStatus === 'completed' || session.buildStatus === 'ready') {
        // eslint-disable-next-line no-console
        console.log('✅ Build completed with status:', session.buildStatus);
        return {
          previewUrl: session.previewUrl,
          sessionId: session.sessionId,
          previewPort: session.previewPort,
          ...session
        };
      }
      
      // 2. Phase is 'complete' or 'ready'
      if ((session.buildPhase === 'complete' || session.buildPhase === 'ready') && session.buildProgress >= 100) {
        // eslint-disable-next-line no-console
        console.log('✅ Build completed with phase:', session.buildPhase);
        return {
          previewUrl: session.previewUrl,
          sessionId: session.sessionId,
          previewPort: session.previewPort,
          ...session
        };
      }
      
      // 2. Explicit failure or error - CHECK THIS FIRST before assuming build is complete
      if (session.buildStatus === 'failed' || session.buildStatus === 'error' || session.buildPhase === 'error') {
        const errorMessage = session.buildError || 'Build failed';
        throw new Error(errorMessage);
      }
      
      // 3. Container is in 'starting' phase at high progress and has a preview URL
      // This indicates the server is running even if status isn't explicitly "complete"
      if (session.buildPhase === 'starting' && session.buildProgress >= 85 && session.previewUrl) {
        // Track how long we've been stuck at high progress
        if (session.buildProgress === lastProgress) {
          consecutiveStartingCount++;
        } else {
          consecutiveStartingCount = 0;
          lastProgress = session.buildProgress;
        }
        
        // If we've been stuck at 85%+ for 15+ seconds (5 polls), consider it ready
        if (consecutiveStartingCount >= 5) {
          // eslint-disable-next-line no-console
          console.log('✅ Assuming build complete - stuck at starting phase with preview URL for 15+ seconds');
          return {
            previewUrl: session.previewUrl,
            sessionId: session.sessionId,
            previewPort: session.previewPort,
            ...session
          };
        }
      }
      
      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      
    } catch (error) {
      console.error('❌ Error polling render status:', error);
      throw error;
    }
  }
  
  // eslint-disable-next-line no-console
  console.error('⏱️ Render job timeout after', Math.floor(maxWaitTime / 1000), 'seconds');
  throw new Error(`Render job timeout - exceeded maximum wait time of ${Math.floor(maxWaitTime / 1000)} seconds. Large projects with 600+ files may require extended installation time (5-7 minutes). The build process may still be running in the background.`);
};

/**
 * Detect if source code is a Next.js project by checking package.json
 * @param {Object} sourceCode - Object with file paths as keys and content as values
 * @returns {boolean} - true if Next.js is detected
 */
/**
 * LogViewer Component - Auto-scrolling log viewer with scroll detection
 * Automatically scrolls to bottom unless user has scrolled up
 */
const LogViewer = ({logs = []}) => {
  const logContainerRef = useRef(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const previousLogsLength = useRef(0);

  // Auto-scroll to bottom when new logs arrive and auto-scroll is enabled
  useEffect(() => {
    if (shouldAutoScroll && logContainerRef.current && logs.length > previousLogsLength.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
    previousLogsLength.current = logs.length;
  }, [logs, shouldAutoScroll]);

  // Detect user scroll and disable auto-scroll if user scrolls up
  const handleScroll = useCallback(() => {
    if (!logContainerRef.current) return;
    
    const {scrollTop, scrollHeight, clientHeight} = logContainerRef.current;
    const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 10; // 10px threshold
    
    // Enable auto-scroll only if user is at the bottom
    setShouldAutoScroll(isAtBottom);
  }, []);

  return (
    <div 
      ref={logContainerRef}
      onScroll={handleScroll}
      style={{
        marginTop: '15px',
        padding: '10px',
        background: '#1e1e1e',
        borderRadius: '4px',
        height: '20vh',
        overflowY: 'auto',
        fontFamily: 'monospace',
        fontSize: '12px',
        lineHeight: '1.4'
      }}
    >
      {logs.map((log, idx) => {
        // Handle both string logs and object logs with timestamp/message
        const logText = typeof log === 'string' ? log : log.message || String(log);
        const timestamp = log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : '';
        
        return (
          <div key={idx} style={{marginBottom: '4px', color: '#ccc'}}>
            {timestamp && `${timestamp}: `}{logText}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Helper function to extract GitLab project ID from repository URL
 */
const extractGitLabProjectId = (repoUrl) => {
  if (!repoUrl) {
    console.warn('extractGitLabProjectId: No repository URL provided');
    return null;
  }
  
  console.log('🔍 Extracting GitLab project ID from:', repoUrl);
  
  try {
    // Handle HTTPS URLs: https://gitlab.com/group/subgroup/project.git
    // Supports nested groups: https://gitlab.com/group/subgroup/nested/project.git
    const httpsMatch = repoUrl.match(/https?:\/\/[^/]+\/(.+?)(?:\.git)?$/);
    if (httpsMatch) {
      const projectPath = httpsMatch[1];
      console.log('✅ Extracted project path (HTTPS):', projectPath);
      return encodeURIComponent(projectPath);
    }
    
    // Handle SSH URLs: git@gitlab.com:group/subgroup/project.git
    // Supports nested groups: git@gitlab.com:group/subgroup/nested/project.git
    const sshMatch = repoUrl.match(/git@[^:]+:(.+?)(?:\.git)?$/);
    if (sshMatch) {
      const projectPath = sshMatch[1];
      console.log('✅ Extracted project path (SSH):', projectPath);
      return encodeURIComponent(projectPath);
    }
    
    console.warn('⚠️ URL format not recognized:', repoUrl);
  } catch (error) {
    console.error('❌ Error extracting GitLab project ID:', error);
  }
  
  return null;
};

/**
 * Canvas Component - Full-screen document and code viewer with Lovable-style tabs
 * Features: Preview, Code (file explorer + editor), Cloud (Mia Platform deployment)
 */
const Canvas = forwardRef(({content, isVisible, onClose, generatedFiles, onProjectContextChange, chatSidebarWidth = 400, onNavigateHome, projectContext, initialMicroservice, initialBranch, initialTab, onSelectionChange, onNewConversation, onShowHistory, onAskAI}, ref) => {
  // Hooks for navigation and URL management
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  
  // Default to 'preview' tab on first load, or use initial tab from URL
  const [activeTab, setActiveTab] = useState(initialTab || 'preview');
  const [selectedFile, setSelectedFile] = useState(null);
  const [lastOpenedFile, setLastOpenedFile] = useState(() => {
    // Restore last opened file from localStorage
    const key = `lastOpenedFile_${params.projectId}_${params.microservice}`;
    const storedPath = localStorage.getItem(key);
    
    // Validate: reject if it looks like a corrupted URL path
    if (storedPath && (storedPath.startsWith('chat/') || (!storedPath.includes('.') && !storedPath.includes('/')))) {
      console.warn('🚨 Detected corrupted localStorage path, clearing:', storedPath);
      localStorage.removeItem(key);
      return null;
    }
    
    return storedPath || null;
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  const previewContainerRef = useRef(null);
  const loadingPreviewRef = useRef(new Set()); // Track services currently being loaded
  const lastLoadedServiceRef = useRef(null); // Track last successfully loaded service
  const errorCountRef = useRef({}); // Track consecutive error count per service
  const autoTriggeredPullRef = useRef(new Set()); // Track services for which we've auto-triggered pull to prevent duplicates
  const lastProjectContextRef = useRef(null); // Track last sent project context to prevent redundant updates
  const receivingContextFromParentRef = useRef(false); // Track if we're currently receiving context from parent
  const handlingIframeErrorRef = useRef(new Set()); // Track if we're already handling an iframe error to prevent duplicate renders
  const previewLoadTimeoutRef = useRef(null); // Debounce timeout for preview loading
  const lastProjectIdRef = useRef(null); // Track last project ID to detect project switches

    // Mia Platform tenant and project state
    const [tenants, setTenants] = useState([]);
    const [projects, setProjects] = useState([]);
    const [showDashboardTab, setShowDashboardTab] = useState(false);
    const [showRequirementsTab, setShowRequirementsTab] = useState(false);

    // Load dashboard tab visibility setting
    useEffect(() => {
    // Initial load
    const loadDashboardSetting = () => {
      const saved = localStorage.getItem('showDashboardTab');
      if (saved !== null) {
        setShowDashboardTab(saved === 'true');
      }
    };

    loadDashboardSetting();

    // Listen for changes
    const handleToggleChange = (event) => {
      if (event.detail && event.detail.showDashboardTab !== undefined) {
        setShowDashboardTab(event.detail.showDashboardTab);
      }
    };

    window.addEventListener('featureToggleChanged', handleToggleChange);
    return () => {
      window.removeEventListener('featureToggleChanged', handleToggleChange);
    };
    }, []);

    // Load requirements tab visibility setting
    useEffect(() => {
    const loadRequirementsSetting = () => {
      const saved = localStorage.getItem('showRequirementsTab');
      if (saved !== null) {
        setShowRequirementsTab(saved === 'true');
      }
    };

    loadRequirementsSetting();

    const handleRequirementsToggleChange = (event) => {
      if (event.detail && event.detail.showRequirementsTab !== undefined) {
        setShowRequirementsTab(event.detail.showRequirementsTab);
      }
    };

    window.addEventListener('featureToggleChanged', handleRequirementsToggleChange);
    return () => {
      window.removeEventListener('featureToggleChanged', handleRequirementsToggleChange);
    };
    }, []);

    const [projectsPagination, setProjectsPagination] = useState(null);
  const [projectsCurrentPage, setProjectsCurrentPage] = useState(1);
  const [projectsSearchQuery, setProjectsSearchQuery] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(() => {
    const saved = localStorage.getItem('mia_selectedTenant');
    if (!saved) return null;
    
    try {
      const tenant = JSON.parse(saved);
      // Ensure tenant has required id field
      if (tenant && !tenant.id && tenant.tenantId) {
        tenant.id = tenant.tenantId;
      }
      return tenant;
    } catch (error) {
      console.error('Failed to parse saved tenant:', error);
      return null;
    }
  });

  const [selectedProject, setSelectedProject] = useState(() => {
    const saved = localStorage.getItem('mia_selectedProject');
    if (!saved) return null;
    
    try {
      const project = JSON.parse(saved);
      // Ensure project has required id field
      if (project && !project.id && (project.projectId || project._id)) {
        project.id = project.projectId || project._id;
      }
      return project;
    } catch (error) {
      console.error('Failed to parse saved project:', error);
      return null;
    }
  });
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [tenantsError, setTenantsError] = useState(null);
  const [projectsError, setProjectsError] = useState(null);
  
  // Create project modal state
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
  const [isCreateServiceModalOpen, setIsCreateServiceModalOpen] = useState(false);
  
  // Mia Platform revisions and microservices state
  const [revisions, setRevisions] = useState([]);
  const [selectedRevision, setSelectedRevision] = useState(() => {
    const saved = localStorage.getItem('mia_selectedRevision');
    return saved ? JSON.parse(saved) : null;
  });
  const [revisionsLoading, setRevisionsLoading] = useState(false);
  const [revisionsError, setRevisionsError] = useState(null);
  
  const [configuration, setConfiguration] = useState(null); // Full project configuration
  const [selectedResource, setSelectedResource] = useState(null); // Selected resource (service, endpoint, etc.)
  const [selectedResourceGitBranch, setSelectedResourceGitBranch] = useState(null); // Current git branch for selected resource
  const [configLoading, setConfigLoading] = useState(false);
  const [pendingResourceReselect, setPendingResourceReselect] = useState(null); // Track resource to re-select after refresh
  
  // Full project configuration from Console API (includes dashboards, links, etc.)
  const [fullProjectConfig, setFullProjectConfig] = useState(() => {
    // Try to load from localStorage on init
    const saved = localStorage.getItem('mia_fullProjectConfig');
    return saved ? JSON.parse(saved) : null;
  });
  
  // Collections state
  const [collections, setCollections] = useState({}); // Merged collections (config + localStorage)
  const [selectedCollection, setSelectedCollection] = useState(null); // Selected collection for editing
  
  // Frontend preview state - Multi-tab support
  const [previewTabs, setPreviewTabs] = useState([]); // Array of {serviceName, url, sessionId, containerPort, framework, loading, error, iframeKey, service}
  const [activePreviewTab, setActivePreviewTab] = useState(0); // Index of active preview tab
  const [previewNeedsRefresh, setPreviewNeedsRefresh] = useState(false); // FIX #3: Track if preview needs refresh for NEW files
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewSessionId, setPreviewSessionId] = useState(null);
  const [previewContainerPort, setPreviewContainerPort] = useState(3001); // Track container port (Frontend: 3001=React, 3002=Next.js, 3003=ReactNative, 3004=Angular, 3005=Vue, 3006=jQuery, 3007=ASP.NET; Backend: 3011=Node, 3012=Go, 3013=Python, 3014=Java, 3015=.NET, 3016=Rust, 3017=PHP, 3018=Kotlin, 3019=C++, 3020=Q#, 3021=Elixir, 3030=CRUD)
  
  // Track currently selected session
  const [selectedSession, setSelectedSessionState] = useState(() => {
    return getSelectedSession();
  });
  const [containerLogs, setContainerLogs] = useState({}); // Store logs for each container by sessionId
  const [expandedContainer, setExpandedContainer] = useState(null); // Track which container's logs are expanded
  const [logErrorCount, setLogErrorCount] = useState(0); // Track error count from preview logs
  const [logsExpanded, setLogsExpanded] = useState(() => {
    // Initialize from localStorage, default to false (minimized)
    try {
      const saved = localStorage.getItem('mia_logsExpanded');
      return saved !== null ? JSON.parse(saved) : false;
    } catch (e) {
      return false;
    }
  }); // Track if preview logs panel is expanded
  const [previewLoading, setPreviewLoading] = useState(false);
  const [compilationStatus, setCompilationStatus] = useState(null); // 'syncing', 'compiling', 'ready'
  const [previewError, setPreviewError] = useState(null);
  const [isCapturingThumbnail, setIsCapturingThumbnail] = useState(false); // Track thumbnail capture state
  const [buildErrorModal, setBuildErrorModal] = useState({ isOpen: false, error: null, serviceName: null }); // Build error modal state
  // Tracks which "serviceName:sessionId" keys have already shown the build error modal.
  // Lives in Canvas (not in PreviewLogs) so it survives PreviewLogs unmount/remount
  // when the user switches tabs, preventing the modal from immediately reopening.
  const buildErrorShownRef = useRef(new Set());

  // When a tab starts a new build (loading → true), clear its shown-key so the
  // error modal can appear again if the new build also has errors.
  useEffect(() => {
    previewTabs.forEach(tab => {
      if (tab.loading) {
        buildErrorShownRef.current.delete(`${tab.serviceName}:${tab.sessionId || 'no-session'}`);
      }
    });
  }, [previewTabs]);
  
  // Note: userId is now persisted in localStorage (see helper functions above)
  // This ensures consistent session IDs across page reloads and eliminates session mismatch warnings
  const [configError, setConfigError] = useState(null);
  const [forceGitFetch, setForceGitFetch] = useState(false); // Flag to force git fetch
  const [iframeKey, setIframeKey] = useState(Date.now()); // Key to force iframe reload
  const [previewUrlInput, setPreviewUrlInput] = useState(''); // Editable preview URL
  const [isEditingPreviewUrl, setIsEditingPreviewUrl] = useState(false); // URL editing mode
  const [tabUrlInputs, setTabUrlInputs] = useState({}); // Editable URLs per tab
  const [editingTabUrl, setEditingTabUrl] = useState(null); // Which tab URL is being edited
  const [activeBackendService, setActiveBackendService] = useState(null); // Track backend service for incremental updates
  
  // Markdown preview state for project configuration
  const [markdownFiles, setMarkdownFiles] = useState([]); // List of markdown files in project
  const [selectedMarkdownFile, setSelectedMarkdownFile] = useState(null); // Selected markdown file to preview
  const [markdownContent, setMarkdownContent] = useState(''); // Content of selected markdown file
  const [selectedSidebarView, setSelectedSidebarView] = useState('dashboard'); // Track selected sidebar view ('dashboard' or 'documentation')

  // Auto-select Dashboard when entering Cloud or AI tabs
  useEffect(() => {
    if (activeTab === 'cloud' || activeTab === 'ai') {
      setSelectedSidebarView('dashboard');
    }
  }, [activeTab]);

  // Helper function to extract path from full URL (excluding container preview base path)
  const getUrlPath = (fullUrl) => {
    try {
      const url = new URL(fullUrl);
      const fullPath = url.pathname + url.search + url.hash;
      // Remove container preview base path (e.g., /preview-50000)
      const match = fullPath.match(/^\/preview-\d+(\/.*)?$/);
      if (match) {
        return match[1] || '/';
      }
      return fullPath || '/';
    } catch (e) {
      return '/';
    }
  };

  // Helper function to get base URL (protocol + host + container path)
  const getBaseUrl = (fullUrl) => {
    try {
      const url = new URL(fullUrl);
      const fullPath = url.pathname;
      // Extract container preview base path (e.g., /preview-50000)
      const match = fullPath.match(/^(\/preview-\d+)/);
      if (match) {
        return url.origin + match[1];
      }
      return url.origin;
    } catch (e) {
      return fullUrl;
    }
  };

  // Helper function to construct full URL from base and path
  const constructFullUrl = (baseUrl, path) => {
    if (!path.startsWith('/')) {
      path = '/' + path;
    }
    return baseUrl + path;
  };
  const iframeRef = useRef(null);
  
  // Ref for CodeViewWithLogger to call git operations
  const codeViewRef = useRef(null);
  
  // Activity logs for status bar
  const [activityLogs, setActivityLogs] = useState([]);
  const addActivityLog = (message) => {
    setActivityLogs(prev => [
      ...prev.slice(-9), // Keep last 9, add 1 new = 10 max
      { timestamp: Date.now(), message }
    ]);
  };

  // Deployment state
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [deploymentLoading, setDeploymentLoading] = useState(false);
  const [deploymentError, setDeploymentError] = useState(null);
  const [deploymentPipelineId, setDeploymentPipelineId] = useState(null);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [pipelinePolling, setPipelinePolling] = useState(false);
  const pipelinePollingInterval = useRef(null);
  
  // Cloud Observability tabs state
  const [cloudTab, setCloudTab] = useState('agents');

  // Auto-select first available cloud tab
  useEffect(() => {
    if (activeTab === 'ai' && configuration) {
      // Determine first available tab
      if (collections && Object.keys(collections).length > 0) {
        setCloudTab('collections');
      } else if (previewTabs.length > 0) {
        setCloudTab('containers');
      } else {
        setCloudTab('agents');
      }
    }
  }, [activeTab, configuration, collections, previewTabs]);

  // Pods status state
  const [showPodsModal, setShowPodsModal] = useState(false);
  const [podsEnvironment, setPodsEnvironment] = useState(null);
  const [podsData, setPodsData] = useState([]);
  const [podsLoading, setPodsLoading] = useState(false);
  const [podsError, setPodsError] = useState(null);

  // Pipeline modal state
  const [showPipelineModal, setShowPipelineModal] = useState(false);
  const [currentPipelineStatus, setCurrentPipelineStatus] = useState(null);
  const [servicePipelineStatus, setServicePipelineStatus] = useState(null);
  const [showServicePipelineList, setShowServicePipelineList] = useState(false);
  const [servicePipelines, setServicePipelines] = useState([]);
  const [loadingServicePipelines, setLoadingServicePipelines] = useState(false);

  // Push modal state
  const [pushModalOpen, setPushModalOpen] = useState(false);

  // Port mappings state
  const [portMappings, setPortMappings] = useState([]);
  const [portMappingsLoading, setPortMappingsLoading] = useState(false);
  const [portMappingsError, setPortMappingsError] = useState(null);
  const [killingPort, setKillingPort] = useState(null);

  // Split view mode state
  const [splitViewMode, setSplitViewMode] = useState('editor'); // 'editor' | 'split' | 'preview'
  
  // Resizable panels state
  const [treeWidth, setTreeWidth] = useState(() => {
    const saved = localStorage.getItem('canvas-tree-width');
    return saved ? parseInt(saved, 10) : 250;
  });
  const [splitRatio, setSplitRatio] = useState(() => {
    const saved = localStorage.getItem('canvas-split-ratio');
    return saved ? parseFloat(saved) : 50;
  });
  const [isResizingTree, setIsResizingTree] = useState(false);
  const [isResizingSplit, setIsResizingSplit] = useState(false);

  // Tree refresh key for forcing ProjectResourcesTree re-render
  const [treeRefreshKey, setTreeRefreshKey] = useState(0);

  // Track if there are any modified or new files for enabling/disabling Push button
  const [hasModifications, setHasModifications] = useState(false);

  // Memoize selectedProject with environments to prevent unnecessary re-renders in DashboardView
  const memoizedSelectedProject = useMemo(() => {
    if (!selectedProject) return null;
    return {
      ...selectedProject,
      environments: fullProjectConfig?.environments || configuration?.environments || []
    };
  }, [selectedProject, fullProjectConfig?.environments, configuration?.environments]);

  // Handle tree resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizingTree) {
        const newWidth = Math.max(180, Math.min(500, e.clientX - (isFullscreen ? 0 : chatSidebarWidth)));
        setTreeWidth(newWidth);
        localStorage.setItem('canvas-tree-width', newWidth.toString());
      }
    };

    const handleMouseUp = () => {
      setIsResizingTree(false);
    };

    if (isResizingTree) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingTree, isFullscreen, chatSidebarWidth]);

  // Persist logs expanded state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('mia_logsExpanded', JSON.stringify(logsExpanded));
    } catch (e) {
      console.error('Failed to save logsExpanded state:', e);
    }
  }, [logsExpanded]);

  // Handle split view resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizingSplit) {
        const container = document.querySelector('.code-layout.split-layout');
        if (container) {
          const rect = container.getBoundingClientRect();
          const newRatio = Math.max(20, Math.min(80, ((e.clientX - rect.left) / rect.width) * 100));
          setSplitRatio(newRatio);
          localStorage.setItem('canvas-split-ratio', newRatio.toString());
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizingSplit(false);
    };

    if (isResizingSplit) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingSplit]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setPreviewFullscreen(false);
      }
    };

    const handleEscKey = (e) => {
      if (e.key === 'Escape' && previewFullscreen) {
        exitPreviewFullscreen();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [previewFullscreen]);

  // FIX #3: Refresh preview when switching to preview tab to include NEW files from IndexedDB
  useEffect(() => {
    if (activeTab === 'preview' && selectedResource?.type === 'service' && previewNeedsRefresh) {
      // eslint-disable-next-line no-console
      console.log('🔄 Preview tab activated, checking for NEW files in IndexedDB...');
      
      const refreshPreviewFiles = async () => {
        try {
          const serviceName = selectedResource.data.name;
          const changedFiles = await indexedDbFileStorage.getChangedFilesForPreview(
            selectedProject?.id,
            serviceName
          );
          
          if (changedFiles.length > 0) {
            // eslint-disable-next-line no-console
            console.log(`📝 Found ${changedFiles.length} pending changes (including NEW files), triggering sync...`);
            
            // Trigger immediate sync for any pending files
            window.dispatchEvent(new CustomEvent('previewSyncNewFile', {
              detail: {
                projectId: selectedProject?.id,
                serviceName: serviceName,
                filePath: 'multiple' // Indicates multiple files
              }
            }));
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error checking for NEW files:', error);
        }
        
        // Reset refresh flag
        setPreviewNeedsRefresh(false);
      };
      
      refreshPreviewFiles();
    }
  }, [activeTab, selectedResource, selectedProject, previewNeedsRefresh]);

  // Fetch markdown files when project configuration is selected
  useEffect(() => {
    const fetchMarkdownFiles = async () => {
      if (selectedResource?.type === 'configuration' && selectedProject?.id) {
        try {
          // The service name for configuration in IndexedDB is "Project Configuration"
          const serviceName = 'Project Configuration';
          
          console.log('🔍 Fetching markdown files from IndexedDB:', {
            projectId: selectedProject.id,
            serviceName: serviceName
          });
          
          // Get all files from IndexedDB for the project configuration
          const allFiles = await indexedDbFileStorage.getAllFiles(
            selectedProject.id,
            serviceName
          );

          console.log('📦 Raw allFiles structure:', {
            isArray: Array.isArray(allFiles),
            length: Array.isArray(allFiles) ? allFiles.length : Object.keys(allFiles || {}).length,
            firstItem: Array.isArray(allFiles) && allFiles.length > 0 ? allFiles[0] : null
          });
          
          // Handle both array and object structures
          let fileList = [];
          if (Array.isArray(allFiles)) {
            // allFiles is an array of file objects with 'path' property
            fileList = allFiles
              .map(file => file.path || file.filePath || file.name)
              .filter(path => path); // Remove undefined/null
            console.log('📋 Sample file paths (from array):', fileList.slice(0, 5));
          } else {
            // allFiles is an object keyed by file paths
            fileList = Object.keys(allFiles || {});
            console.log('📋 Sample file paths (from object):', fileList.slice(0, 5));
          }

          // Filter for markdown files
          const mdFiles = fileList
            .filter(path => {
              const isMarkdown = path.endsWith('.md') || path.endsWith('.markdown');
              if (isMarkdown) {
                console.log('✅ Found markdown:', path);
              }
              return isMarkdown;
            })
            .sort();

          console.log('📄 Found markdown files:', mdFiles);
          setMarkdownFiles(mdFiles);
        } catch (error) {
          console.error('❌ Error fetching markdown files from IndexedDB:', error);
          setMarkdownFiles([]);
        }
      } else {
        setMarkdownFiles([]);
        setSelectedMarkdownFile(null);
        setMarkdownContent('');
      }
    };

    fetchMarkdownFiles();
  }, [selectedResource?.type, selectedProject]);

  // Fetch markdown content when a file is selected
  useEffect(() => {
    const fetchMarkdownContent = async () => {
      if (selectedMarkdownFile && selectedResource?.type === 'configuration' && selectedProject?.id) {
        try {
          // The service name for configuration in IndexedDB is "Project Configuration"
          const serviceName = 'Project Configuration';
          
          console.log('📖 Fetching content for:', selectedMarkdownFile);
          
          // Get file content from IndexedDB
          const fileData = await indexedDbFileStorage.getFile(
            selectedProject.id,
            serviceName,
            selectedMarkdownFile
          );

          if (fileData && fileData.content) {
            setMarkdownContent(fileData.content);
          } else {
            setMarkdownContent(`# File Not Found\n\nThe markdown file "${selectedMarkdownFile}" could not be loaded from IndexedDB.\n\n💡 **Tip:** Visit the Code tab first to load the repository files.`);
          }
        } catch (error) {
          console.error('❌ Error fetching markdown content from IndexedDB:', error);
          setMarkdownContent(`# Error\n\nFailed to load markdown file: ${error.message}`);
        }
      }
    };

    fetchMarkdownContent();
  }, [selectedMarkdownFile, selectedResource, selectedProject]);

  // Listen for file creation events to mark preview for refresh
  useEffect(() => {
    const handleFileOperation = (event) => {
      // FIX #3: Mark that preview needs refresh when a file operation occurs
      if (event.detail?.operation === 'create') {
        setPreviewNeedsRefresh(true);
      }
    };

    window.addEventListener('fileOperation', handleFileOperation);
    
    return () => {
      window.removeEventListener('fileOperation', handleFileOperation);
    };
  }, []);
  
  // Listen for explicit context refresh requests from parent (when AI calls request_project_context)
  useEffect(() => {
    const handleContextRefresh = async () => {
      // eslint-disable-next-line no-console
      console.log('🔄 Explicit context refresh requested - forcing update');
      
      // Clear the receivingContextFromParentRef flag to allow update
      receivingContextFromParentRef.current = false;
      
      // Force context key to be different by adding timestamp
      lastProjectContextRef.current = null;
      
      // Trigger update by modifying a dependency (just toggle a counter)
      setTreeRefreshKey(prev => prev + 1);
    };
    
    window.addEventListener('requestContextRefresh', handleContextRefresh);
    
    // Removed periodic auto-refresh interval to prevent event loops causing excessive tree fetching
    // Context will be refreshed on explicit requests via 'requestContextRefresh' event only
    
    return () => {
      window.removeEventListener('requestContextRefresh', handleContextRefresh);
    };
  }, [selectedProject, selectedResource, onProjectContextChange]);
  
  // Initialize selections from URL parameters
  useEffect(() => {
    if (!configuration || !initialMicroservice) return;
    
    // Find and select microservice from URL
    const services = configuration.services || {};
    const service = Object.values(services).find(s => s.name === decodeURIComponent(initialMicroservice));
    
    if (service && (!selectedResource || selectedResource.data?.name !== service.name)) {
      console.log('🔗 Initializing microservice from URL:', service.name);
      setSelectedResource({type: 'service', data: service, name: service.name});
      
      // If initialTab is 'preview', ensure activeTab is also set to 'preview'
      // This triggers the preview load effect
      if (initialTab === 'preview' && activeTab !== 'preview') {
        console.log('🔗 Setting activeTab to preview to trigger load');
        setActiveTab('preview');
      }
    }
  }, [configuration, initialMicroservice, initialTab, activeTab]);
  
  // Initialize revision/branch from URL parameters
  useEffect(() => {
    if (!revisions || revisions.length === 0 || !initialBranch) return;
    
    const revision = revisions.find(r => r.name === decodeURIComponent(initialBranch));
    
    if (revision && (!selectedRevision || selectedRevision.name !== revision.name)) {
      console.log('🔗 Initializing branch from URL:', revision.name);
      setSelectedRevision(revision);
    }
  }, [revisions, initialBranch, selectedRevision]);
  
  // Load git branch from localStorage when selectedResource changes
  useEffect(() => {
    if (!selectedResource || selectedResource.type !== 'service' || !selectedProject?.id) {
      setSelectedResourceGitBranch(null);
      return;
    }

    const serviceName = selectedResource.data?.name || selectedResource.name;
    if (!serviceName) {
      setSelectedResourceGitBranch(null);
      return;
    }

    // Use utility to get git ref from localStorage
    const gitBranch = getGitRef(selectedProject.id, serviceName, 'main');
    setSelectedResourceGitBranch(gitBranch);

    // eslint-disable-next-line no-console
    console.log(`🌿 Loaded git branch for ${serviceName}:`, gitBranch);
  }, [selectedResource, selectedProject?.id]);

  // Sync activeTab with initialTab from URL
  useEffect(() => {
    if (initialTab && activeTab !== initialTab) {
      console.log('🔗 Syncing activeTab from URL:', initialTab);
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  /**
   * Handle file selection from file tree
   * Note: URL updates are now handled by RepositoryTree itself
   */
  const handleFileSelect = useCallback((filePathOrData, fileDataIfTwo) => {
    // Handle both calling conventions:
    // 1. handleFileSelect(fileData) - direct call with file object
    // 2. handleFileSelect(filePath, fileData) - call from CodeViewWithLogger
    let fileData;
    if (typeof filePathOrData === 'string' && fileDataIfTwo) {
      // Called with (filePath, fileData)
      fileData = fileDataIfTwo;
    } else if (typeof filePathOrData === 'object') {
      // Called with (fileData)
      fileData = filePathOrData;
    } else {
      return;
    }
    
    // Normalize: fileData may have 'path' or 'filePath' property
    const normalizedPath = fileData?.path || fileData?.filePath;
    if (!fileData || !normalizedPath) {
      return;
    }
    
    // Ensure fileData has 'path' property for consistency
    const normalizedFileData = {
      ...fileData,
      path: normalizedPath
    };
    
    setSelectedFile(normalizedFileData);
    
    // Store as last opened file
    if (selectedProject && selectedResource) {
      const projectId = selectedProject.id || selectedProject._id;
      const serviceName = selectedResource.data?.name;
      const key = `lastOpenedFile_${projectId}_${serviceName}`;
      localStorage.setItem(key, normalizedPath);
      setLastOpenedFile(normalizedPath);
    }
  }, [selectedProject, selectedResource]);

  /**
   * Load file from path (used for URL-based file opening and restoring last opened file)
   */
  const loadFileFromPath = useCallback(async (filePath) => {
    if (!filePath || !selectedProject || !selectedResource) return;
    
    try {
      const projectId = selectedProject.id || selectedProject._id;
      const serviceName = selectedResource.data?.name;
      
      // Check IndexedDB first for cached changes
      let fileContent = null;
      try {
        const cachedFile = await indexedDbFileStorage.getFile(projectId, serviceName, filePath);
        if (cachedFile) {
          fileContent = cachedFile.content;
          // eslint-disable-next-line no-console
          console.log('📂 Loaded file from IndexedDB:', filePath);
        }
      } catch (dbError) {
        // eslint-disable-next-line no-console
        console.warn('Could not load from IndexedDB, will try GitLab:', dbError);
      }
      
      // If not in IndexedDB, fetch from GitLab
      if (!fileContent) {
        const repoUrl = selectedResource.data?.repositoryUrl || selectedResource.data?.repoUrl;
        if (!repoUrl) {
          console.error('No repository URL found');
          return;
        }
        
        // Extract GitLab project ID
        const gitlabProjectId = extractGitLabProjectId(repoUrl);
        if (!gitlabProjectId) {
          console.error('Could not extract GitLab project ID');
          return;
        }
        
        const branch = selectedRevision?.name || params.branch || 'main';
        
        // Fetch file from GitLab
        const unifiedGitDataService = (await import('../services/UnifiedGitDataService.js')).default;
        const fileData = await unifiedGitDataService.getFileContent(
          gitlabProjectId,
          repoUrl,
          filePath,
          branch
        );
        
        fileContent = atob(fileData.content);
        // eslint-disable-next-line no-console
        console.log('📂 Loaded file from GitLab:', filePath);
      }
      
      // Detect language from file extension
      const ext = filePath.split('.').pop()?.toLowerCase();
      const languageMap = {
        'js': 'javascript', 'jsx': 'javascript', 'ts': 'typescript', 'tsx': 'typescript',
        'py': 'python', 'json': 'json', 'html': 'html', 'css': 'css', 'scss': 'scss',
        'md': 'markdown', 'yml': 'yaml', 'yaml': 'yaml', 'xml': 'xml', 'sql': 'sql',
        'sh': 'shell', 'bash': 'shell', 'java': 'java', 'go': 'go', 'rs': 'rust',
        'cpp': 'cpp', 'c': 'c', 'h': 'cpp', 'php': 'php', 'rb': 'ruby', 'swift': 'swift'
      };
      const language = languageMap[ext] || 'text';
      
      // Create file data object
      const fileData = {
        path: filePath,
        filePath: filePath,  // Include both for compatibility
        content: fileContent,
        language: language,
        name: filePath.split('/').pop()
      };
      
      // Use handleFileSelect to properly update state and URL
      handleFileSelect(fileData);
      
      // Scroll to line number if specified in hash
      const lineNumber = location.hash ? parseInt(location.hash.replace('#L', '')) : null;
      if (lineNumber) {
        // Monaco editor will handle scroll via lineNumber prop
        // Dispatch event for CodeViewWithLogger to scroll to line
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('scrollToLine', {
            detail: { lineNumber }
          }));
        }, 100);
      }
      
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load file:', error);
    }
  }, [selectedProject, selectedResource, selectedRevision, params.branch, location.hash, handleFileSelect]);

  // URL-based file loading is now handled by RepositoryTree component
  
  // Restore last opened file - DISABLED: RepositoryTree handles this via URL
  // useEffect(() => {
  //   if (activeTab === 'code' && !selectedFile && lastOpenedFile) {
  //     // Check if there's a file in the URL first
  //     if (!params['*']) {
  //       // Additional validation: ensure it's a valid file path
  //       if (!lastOpenedFile.startsWith('chat/') && (lastOpenedFile.includes('.') || lastOpenedFile.includes('/'))) {
  //         console.log('🔗 Restoring last opened file:', lastOpenedFile);
  //         loadFileFromPath(lastOpenedFile);
  //       } else {
  //         console.warn('🚨 Skipping invalid lastOpenedFile:', lastOpenedFile);
  //       }
  //     }
  //   }
  // }, [activeTab, selectedFile, lastOpenedFile, params, loadFileFromPath]);

  // CRITICAL FIX: Listen for AI content updates even when Monaco is not open
  // This ensures diffs are applied to IndexedDB regardless of editor visibility
  useEffect(() => {
    const handleAIContentUpdate = async (event) => {
      const {filePath, updateType, diffContent, projectId: eventProjectId, serviceName: eventServiceName} = event.detail || {};
      
      // Determine the correct service name to use
      // If in preview mode with active tab, use that tab's service name
      let targetServiceName = eventServiceName;
      if (activeTab === 'preview' && previewTabs.length > 0 && activePreviewTab >= 0) {
        const activeTab = previewTabs[activePreviewTab];
        if (activeTab && activeTab.serviceName) {
          targetServiceName = activeTab.serviceName;
          // eslint-disable-next-line no-console
          console.log(`📋 Using preview tab service name: ${targetServiceName}`);
        }
      }
      
      // CRITICAL FIX: If projectId or serviceName is missing, try to get from context
      let finalProjectId = eventProjectId;
      let finalServiceName = targetServiceName;
      
      if (!finalProjectId && project?.id) {
        finalProjectId = project.id;
        // eslint-disable-next-line no-console
        console.log(`📌 Canvas: Using projectId from context: ${finalProjectId}`);
      }
      
      // Get service name from selectedResource (the actual state variable, not the derived one)
      if (!finalServiceName) {
        // Try localStorage first - but parse it correctly if it's JSON
        const storedResource = localStorage.getItem('mia_selectedResource');
        if (storedResource) {
          try {
            // Try to parse as JSON first (in case it's an object)
            const parsed = JSON.parse(storedResource);
            if (parsed && typeof parsed === 'object') {
              // Extract the name from the object structure
              finalServiceName = parsed.data?.name || parsed.name || null;
            } else {
              // It's already a string
              finalServiceName = storedResource;
            }
          } catch (e) {
            // Not JSON, use as-is (it's a plain string)
            finalServiceName = storedResource;
          }
          
          if (finalServiceName) {
            // eslint-disable-next-line no-console
            console.log(`📌 Canvas: Using serviceName from localStorage: ${finalServiceName}`);
          }
        }
        
        // Fall back to selectedResource state if localStorage didn't work
        if (!finalServiceName && selectedResource?.type === 'service' && selectedResource?.data?.name) {
          finalServiceName = selectedResource.data.name;
          // eslint-disable-next-line no-console
          console.log(`📌 Canvas: Using serviceName from selectedResource: ${finalServiceName}`);
        } else if (!finalServiceName && selectedResource?.name) {
          finalServiceName = selectedResource.name;
          // eslint-disable-next-line no-console
          console.log(`📌 Canvas: Using serviceName from selectedResource.name: ${finalServiceName}`);
        }
      }
      
      // eslint-disable-next-line no-console
      console.log('🔔 Canvas: Received aiContentUpdate event:', {
        filePath,
        updateType,
        hasDiff: !!diffContent,
        diffLength: diffContent?.length || 0,
        projectId: finalProjectId,
        serviceName: finalServiceName,
        originalProjectId: eventProjectId,
        originalServiceName: eventServiceName,
        timestamp: new Date().toISOString()
      });
      
      // Only handle diffs when Monaco editor is NOT handling them
      // (Monaco handles open files, we handle background files)
      // eslint-disable-next-line no-console
      console.log('🔍 Canvas: Checking condition for diff processing:', {
        updateTypeIsDiff: updateType === 'diff',
        hasDiffContent: !!diffContent,
        hasProjectId: !!finalProjectId,
        hasServiceName: !!finalServiceName,
        willProcess: updateType === 'diff' && diffContent && finalProjectId && finalServiceName
      });
      
      if (updateType === 'diff' && diffContent && finalProjectId && finalServiceName) {
        // Add timeout protection to prevent hanging
        const timeoutId = setTimeout(() => {
          // eslint-disable-next-line no-console
          console.error(`⏱️ Canvas: Diff application timeout for ${filePath} after 30s`);
        }, 30000);
        
        try {
          // Parse the diff to check operation type
          const parsed = parseUnifiedDiff(diffContent);
          
          if (!parsed.isValid) {
            // eslint-disable-next-line no-console
            console.warn(`⚠️ Invalid diff for ${filePath}, skipping background processing`);
            return;
          }
          
          // Handle file deletion
          if (parsed.isFileDeletion) {
            // eslint-disable-next-line no-console
            console.log(`🗑️ Canvas: Deleting file from IndexedDB: ${filePath}`);
            
            await indexedDbFileStorage.deleteFile(finalProjectId, finalServiceName, filePath);
            
            // eslint-disable-next-line no-console
            console.log(`✅ Canvas: File deleted from IndexedDB: ${filePath}`);
            return;
          }
          
          // Get current content from IndexedDB
          const stored = await indexedDbFileStorage.getFile(finalProjectId, finalServiceName, filePath);
          const currentContent = stored?.content || '';
          
          // Apply the diff
          const result = await applyUnifiedDiff(diffContent, currentContent);
          
          if (result.success) {
            // Save to IndexedDB
            const originalContentToSave = stored?.originalContent || currentContent;
            await indexedDbFileStorage.saveFile(
              finalProjectId,
              finalServiceName,
              filePath,
              result.newContent,
              {
                originalContent: originalContentToSave,
                fileName: filePath.split('/').pop(),
                appliedInBackground: true,
                isNew: parsed.operation === 'create'
              }
            );
            
            // Track file operation with language
            const fileExtension = filePath.split('.').pop();
            const language = fileExtension || 'unknown';
            if (window.analytics?.trackLanguage) {
              window.analytics.trackLanguage(language, {
                action: parsed.operation === 'create' ? 'create' : 'edit',
                filePath,
                projectId: finalProjectId,
                serviceName: finalServiceName
              });
            }
            if (window.analytics?.trackFile) {
              window.analytics.trackFile(parsed.operation === 'create' ? 'create' : 'edit', {
                fileName: filePath.split('/').pop(),
                filePath,
                language,
                projectId: finalProjectId
              });
            }
            
            // Dispatch monacoFileChanged to trigger auto-sync
            window.dispatchEvent(new CustomEvent('monacoFileChanged', {
              detail: {
                projectId: finalProjectId,
                serviceName: finalServiceName,
                filePath: filePath
              }
            }));
            
            // Broadcast file update to other collaborators via WebSocket
            try {
              await syncService.sendFileUpdate(filePath, finalServiceName, result.newContent);
              // eslint-disable-next-line no-console
              console.log(`📡 Canvas: Broadcasted AI file update to collaborators: ${filePath}`);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error(`❌ Canvas: Failed to broadcast file update:`, error);
            }
            
            // eslint-disable-next-line no-console
            console.log(`✅ Canvas: Applied diff in background for ${filePath}`);
            
            // If it's a new file, trigger immediate sync
            if (parsed.operation === 'create') {
              window.dispatchEvent(new CustomEvent('previewSyncNewFile', {
                detail: {
                  projectId: finalProjectId,
                  serviceName: finalServiceName,
                  filePath: filePath
                }
              }));
            }
          } else {
            // eslint-disable-next-line no-console
            console.error(`❌ Canvas: Failed to apply diff for ${filePath}:`, result.error);
            // eslint-disable-next-line no-console
            console.error(`   Diff length: ${diffContent?.length || 0} chars`);
            // eslint-disable-next-line no-console
            console.error(`   ProjectId: ${finalProjectId}, Service: ${finalServiceName}`);
            
            // Dispatch diffApplicationFailed event for AI notification
            window.dispatchEvent(new CustomEvent('diffApplicationFailed', {
              detail: {
                projectId: finalProjectId,
                serviceName: finalServiceName,
                filePath: filePath,
                fileName: filePath.split('/').pop(),
                currentContent: currentContent,
                currentLineCount: currentContent.split('\n').length,
                failedDiff: diffContent,
                error: result.error,
                isBackgroundDiff: true
              }
            }));
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`❌ Canvas: Error processing AI content update for ${filePath}:`, error);
          // eslint-disable-next-line no-console
          console.error(`   Stack trace:`, error.stack);
        } finally {
          clearTimeout(timeoutId);
          // eslint-disable-next-line no-console
          console.log(`✓ Canvas: Finished processing aiContentUpdate for ${filePath}`);
        }
      }
    };

    window.addEventListener('aiContentUpdate', handleAIContentUpdate);

    return () => {
      window.removeEventListener('aiContentUpdate', handleAIContentUpdate);
    };
  }, [activeTab, previewTabs, activePreviewTab]);

  // Fullscreen management functions
  const togglePreviewFullscreen = async () => {
    if (!previewContainerRef.current) {return;}

    try {
      if (!previewFullscreen) {
        await previewContainerRef.current.requestFullscreen();
        setPreviewFullscreen(true);
      } else {
        await document.exitFullscreen();
        setPreviewFullscreen(false);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Fullscreen error:', error);
    }
  };

  const exitPreviewFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
      setPreviewFullscreen(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Exit fullscreen error:', error);
    }
  };

  // Function to fetch port mappings from container-live
  const fetchPortMappings = useCallback(async () => {
    try {
      setPortMappingsLoading(true);
      setPortMappingsError(null);
      
      // Fetch from React container (port 3001) as it manages all port mappings
      const apiUrl = getContainerApiUrl(3001, '/api/port-mappings');
      const response = await getAuthorizedFetch()(apiUrl, {
        headers: getBffHeaders(),
      });
      const data = await response.json();
      
      if (data.success) {
        setPortMappings(data.mappings || []);
      } else {
        setPortMappingsError(data.message || 'Failed to fetch port mappings');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch port mappings:', error);
      setPortMappingsError(error.message);
    } finally {
      setPortMappingsLoading(false);
    }
  }, []);

  // Function to kill process on a specific port
  const killProcessOnPort = useCallback(async (sessionId, port) => {
    try {
      setKillingPort(port);
      
      // Call kill API on React container (port 3001) to kill all processes and free the port
      const apiUrl = getContainerApiUrl(3001, `/api/preview/${sessionId}/kill`);
      const response = await getAuthorizedFetch()(apiUrl, {
        method: 'POST',
        headers: {
          ...getBffHeaders(),
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Show success notification
        console.log(`✓ All processes killed and port ${port} freed successfully`);
        // Refresh port mappings after a short delay
        setTimeout(() => {
          fetchPortMappings();
        }, 1000);
      } else {
        console.error(`Failed to kill process on port ${port}:`, data.message);
        alert(`Failed to kill process: ${data.message}`);
      }
    } catch (error) {
      console.error(`Error killing process on port ${port}:`, error);
      alert(`Error killing process: ${error.message}`);
    } finally {
      setKillingPort(null);
    }
  }, [fetchPortMappings]);

  // Function to refresh preview iframe after file updates
  const refreshPreview = useCallback(async () => {
    // Check if there was a compilation error on the active tab
    // If so, trigger a full re-render instead of just refreshing the iframe
    if (previewTabs.length > 0 && activePreviewTab >= 0) {
      const activeTab = previewTabs[activePreviewTab];
      
      // Check if the tab has an error or if buildErrorModal is open for this service
      const hasError = activeTab?.error || 
                      (buildErrorModal.isOpen && buildErrorModal.serviceName === activeTab?.serviceName);
      
      if (hasError) {
        // eslint-disable-next-line no-console
        console.log('🔄 Compilation error detected, triggering full re-render instead of refresh');
        
        // Close the build error modal
        setBuildErrorModal({ isOpen: false, error: null, serviceName: null });
        
        // Clear the error from the tab
        const updatedTabs = [...previewTabs];
        updatedTabs[activePreviewTab] = {
          ...updatedTabs[activePreviewTab],
          error: null,
          loading: true
        };
        setPreviewTabs(updatedTabs);
        
        // Trigger autoRenderOnPreviewNotFound which will do a full rebuild
        try {
          await autoRenderOnPreviewNotFound(activeTab);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('❌ Failed to re-render after error fix:', error);
        }
        return;
      }
    }
    
    // No error - just refresh the iframe normally
    // Update key to force iframe remount
    setIframeKey(Date.now());
    // Also update the active tab's iframe key
    if (previewTabs.length > 0 && activePreviewTab >= 0) {
      const updatedTabs = [...previewTabs];
      updatedTabs[activePreviewTab] = {
        ...updatedTabs[activePreviewTab],
        iframeKey: Date.now()
      };
      setPreviewTabs(updatedTabs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewTabs, activePreviewTab, buildErrorModal]);

  // Function to fetch logs for a container
  const fetchContainerLogs = useCallback(async (sessionId, containerPort) => {
    try {
      const apiUrl = getContainerApiUrl(containerPort, `/api/preview/${sessionId}/logs`);
      const response = await getAuthorizedFetch()(apiUrl, {
        headers: getBffHeaders(),
      });
      const data = await response.json();
      
      if (data.success) {
        setContainerLogs(prev => ({
          ...prev,
          [sessionId]: {
            stdout: data.logs.stdout || [],
            stderr: data.logs.stderr || [],
            lastFetch: new Date().toISOString()
          }
        }));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch container logs:', error);
    }
  }, []);

  // Function to control container (stop/start/restart)
  const controlContainer = useCallback(async (sessionId, containerPort, action) => {
    try {
      const apiUrl = getContainerApiUrl(containerPort, `/api/preview/${sessionId}/${action}`);
      const response = await getAuthorizedFetch()(apiUrl, {
        method: 'POST',
        headers: getBffHeaders(),
      });
      const data = await response.json();
      
      if (data.success) {
        // Update the preview tab status
        setPreviewTabs(prev => prev.map(tab => (
          tab.sessionId === sessionId ? { 
            ...tab, 
            loading: action === 'start' || action === 'restart',
            url: action === 'stop' ? null : tab.url
          } : tab
        )));

        // If starting/restarting, wait a bit and check if URL is available
        if (action === 'start' || action === 'restart') {
          setTimeout(() => {
            setPreviewTabs(prev => prev.map(tab => (
              tab.sessionId === sessionId ? { 
                ...tab, 
                loading: false,
                url: data.previewUrl || `http://localhost:${tab.containerPort}`
              } : tab
            )));
          }, 2000);
        }

        return true;
      }
      return false;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to ${action} container:`, error);
      return false;
    }
  }, []);
  
  /**
   * Auto-render when preview session is not found
   * This handles the case when the container restarts and loses session state
   * 1. Gets files from IndexedDB
   * 2. If no files, pulls from Git and saves to IndexedDB
   * 3. Calls /api/render with all files
   * 4. Updates Monaco editor with the code via gitPullCompleted event
   */
  const autoRenderOnPreviewNotFound = async (tab) => {
    const serviceName = tab.serviceName;
    const sessionKey = `${selectedProject?.id}-${serviceName}`;
    const pullKey = `${selectedProject?.id}-${serviceName}`;

    // ── PRE-FLIGHT: check tool enablement & authentication FIRST ──────────────
    // Do this before any IndexedDB or git API calls so we never hit 401s
    {
      const svc = configuration?.services?.[serviceName];
      const repoUrlForCheck = svc?.repositoryUrl || svc?.repoUrl || svc?.sshUrl || tab?.repoUrl;
      if (repoUrlForCheck) {
        const gitStatus = await checkGitToolStatus(repoUrlForCheck);
        if (!gitStatus.toolEnabled || !gitStatus.authenticated) {
          const blockedError = !gitStatus.toolEnabled
            ? `__GIT_TOOL_DISABLED__:${gitStatus.providerName}`
            : `__GIT_NOT_AUTHENTICATED__:${gitStatus.providerName}`;
          // eslint-disable-next-line no-console
          console.warn(`🚫 [AutoRender] Pre-flight blocked (${blockedError}) for`, serviceName);
          setPreviewTabs(prevTabs => prevTabs.map(t =>
            t.serviceName === serviceName
              ? { ...t, loading: false, pullingCode: false, error: blockedError }
              : t
          ));
          return;
        }
      }
    }
    // ─────────────────────────────────────────────────────────────────────────

    // Prevent duplicate render attempts
    if (handlingIframeErrorRef.current.has(sessionKey)) {
      // eslint-disable-next-line no-console
      console.log('⏸️ Already handling iframe error for', serviceName, '- skipping duplicate');
      return;
    }
    
    handlingIframeErrorRef.current.add(sessionKey);
    
    try {
      // eslint-disable-next-line no-console
      console.log('🔄 Preview session not found - initiating auto-render for', serviceName);
      
      // Get files from IndexedDB
      let allFiles = await indexedDbFileStorage.getAllFilesForPreview(
        selectedProject?.id,
        serviceName,
        {}
      );
      
      // If no files in IndexedDB, try to pull from Git
      if (Object.keys(allFiles).length === 0) {
        // Check if another process is already pulling this service
        if (autoTriggeredPullRef.current.has(pullKey)) {
          // eslint-disable-next-line no-console
          console.log('⏸️ Another process is already pulling', serviceName, '- skipping duplicate');
          // Wait a bit and try to get files again
          await new Promise(resolve => setTimeout(resolve, 2000));
          allFiles = await indexedDbFileStorage.getAllFilesForPreview(
            selectedProject?.id,
            serviceName,
            {}
          );
          if (Object.keys(allFiles).length === 0) {
            throw new Error('Pull in progress but no files available yet');
          }
        } else {
          // Mark that we're pulling this service
          autoTriggeredPullRef.current.add(pullKey);
          
          // eslint-disable-next-line no-console
          console.log('📥 No files in IndexedDB - pulling from Git...');
        
          const service = configuration?.services?.[serviceName];
          if (!service) {
            throw new Error(`Service ${serviceName} not found in configuration`);
          }
          
          const repoUrl = service.repositoryUrl || service.repoUrl || service.sshUrl;
          if (!repoUrl) {
            throw new Error(`No repository URL found for service ${serviceName}`);
          }

          // ── PRE-FLIGHT: check tool enablement & authentication ─────────────
          const gitStatus = await checkGitToolStatus(repoUrl);
          if (!gitStatus.toolEnabled) {
            setPreviewTabs(prevTabs => prevTabs.map(t =>
              t.serviceName === serviceName
                ? { ...t, loading: false, pullingCode: false, error: `__GIT_TOOL_DISABLED__:${gitStatus.providerName}` }
                : t
            ));
            return;
          }
          if (!gitStatus.authenticated) {
            setPreviewTabs(prevTabs => prevTabs.map(t =>
              t.serviceName === serviceName
                ? { ...t, loading: false, pullingCode: false, error: `__GIT_NOT_AUTHENTICATED__:${gitStatus.providerName}` }
                : t
            ));
            return;
          }
          // ──────────────────────────────────────────────────────────────────

          const projectId = extractProjectIdFromUrl(repoUrl);
          if (!projectId) {
            throw new Error('Could not extract project ID from repository URL');
          }
          
          // Update preview tab to show pulling state
          setPreviewTabs(prevTabs => prevTabs.map(t => 
            t.serviceName === serviceName 
              ? {...t, pullingCode: true, renderPhase: 'Pulling code from repository', renderProgress: 0}
              : t
          ));
          
          // Fetch files from Git with progress tracking
          const gitFiles = await fetchAllRepositoryFiles(projectId, repoUrl, (progress) => {
            // Update preview tab with progress
            setPreviewTabs(prevTabs => prevTabs.map(t => 
              t.serviceName === serviceName 
                ? {
                    ...t, 
                    renderPhase: `Pulling code from repository (${progress.percentage}%)`,
                    renderProgress: progress.percentage,
                    renderLogs: [
                      ...(t.renderLogs || []).slice(-10), // Keep last 10 logs
                      `Downloading ${progress.current}/${progress.total}: ${progress.currentFile}`
                    ]
                  }
                : t
            ));
          });
          
          // Save to IndexedDB for future use
          // eslint-disable-next-line no-console
          console.log(`💾 Saving ${Object.keys(gitFiles).length} files to IndexedDB...`);
          for (const [filePath, content] of Object.entries(gitFiles)) {
            await indexedDbFileStorage.saveFile(
              selectedProject?.id,
              serviceName,
              filePath,
              content,
              {
                originalContent: content,
                fromGit: true // These files come from Git, so they're ORIGINAL
              }
            );
          }
          
          allFiles = gitFiles;
          
          // Dispatch event to refresh Monaco editor and repository tree
          window.dispatchEvent(new CustomEvent('gitPullCompleted', {
            detail: {
              projectId: selectedProject?.id,
              serviceName,
              fromAutoRender: true
            }
          }));
        } // Close the else block for autoTriggeredPullRef check
      } // Close the if (Object.keys(allFiles).length === 0) block
      
      if (Object.keys(allFiles).length === 0) {
        throw new Error('No source code files found for rendering');
      }
      
      // eslint-disable-next-line no-console
      console.log(`📤 Auto-rendering with ${Object.keys(allFiles).length} files`);
      
      // Get routing info using centralized utility
      const routingInfo = getContainerRoutingInfo(
        tab.service || { name: serviceName },
        allFiles,
        { previewSessionId: tab.sessionId }
      );
      
      if (!routingInfo) {
        throw new Error('Failed to detect framework for auto-render');
      }
      
      const { containerPort, containerName, frameworkDisplay: framework, isBackend: isBackendService } = routingInfo;
      
      // eslint-disable-next-line no-console
      console.log(`🎯 Auto-render detected ${isBackendService ? 'backend' : 'frontend'}: ${routingInfo.framework} (${containerName})`);
      
      const userId = getOrCreateUserId(selectedProject?.id, serviceName);
      
      // Show loading state
      const tabIndex = previewTabs.findIndex(t => t.serviceName === serviceName);
      if (tabIndex >= 0) {
        setPreviewTabs(currentTabs => {
          const updatedTabs = [...currentTabs];
          updatedTabs[tabIndex] = {
            ...updatedTabs[tabIndex],
            framework: framework,
            loading: true,
            error: null,
            renderPhase: 'starting',
            renderProgress: 0,
            renderLogs: ['🔄 Auto-rendering after session loss...']
          };
          return updatedTabs;
        });
      }
      
      // Call /api/render
      const renderApiUrl = getContainerApiUrl(containerPort, '/api/render');
      const renderResponse = await getAuthorizedFetch()(renderApiUrl, {
        method: 'POST',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          tenantId: selectedTenant?.tenantId || selectedTenant?.id,
          serviceName: serviceName,
          sourceCode: allFiles,
          projectId: selectedProject?.id,
          revisionId: selectedRevision?.id,
          userId: userId,
        }),
      });
      
      if (!renderResponse.ok) {
        throw new Error(`Failed to call /api/render: ${renderResponse.status}`);
      }
      
      const renderResult = await renderResponse.json();
      if (!renderResult.success || !renderResult.sessionId) {
        throw new Error(renderResult.message || 'Failed to start render job');
      }
      
      // eslint-disable-next-line no-console
      console.log(`🎬 Auto-render job started with ID: ${renderResult.sessionId}`);
      
      // Poll for completion
      const containerBaseUrl = renderApiUrl.replace('/api/render', '');
      const pollResult = await pollRenderStatus(
        containerBaseUrl,
        renderResult.sessionId,
        (phase, progress, logs) => {
          // Debug: Log what we're about to set on the tab
          console.log('[onProgress callback] Updating tab with:', {
            phase,
            progress,
            logsCount: logs?.length,
            logs
          });
          
          // Use functional setState to avoid stale closure
          setPreviewTabs(currentTabs => {
            const currentTabIndex = currentTabs.findIndex(t => t.serviceName === serviceName);
            if (currentTabIndex >= 0) {
              const updatedTabs = [...currentTabs];
              // Replace logs entirely (API returns complete log history, not incremental)
              updatedTabs[currentTabIndex] = {
                ...updatedTabs[currentTabIndex],
                loading: true,
                renderPhase: phase,
                renderProgress: progress,
                renderLogs: logs || []
              };
              return updatedTabs;
            }
            return currentTabs;
          });
        }
      );
      
      // Success - update tab with new session
      if (tabIndex >= 0) {
        const expectedSessionId = `${selectedProject?.id}-${serviceName}-${userId}`;
        // Use the previewUrl from pollResult instead of tab.url which is null
        const basePreviewUrl = pollResult?.previewUrl || `http://localhost:${containerPort}/preview-${containerPort}`;
        const finalPreviewUrl = basePreviewUrl.split('?')[0] + `?sessionId=${expectedSessionId}`;
        
        // eslint-disable-next-line no-console
        console.log('🔄 Updating tab after auto-render completion:', {
          serviceName,
          tabIndex,
          basePreviewUrl,
          finalPreviewUrl,
          expectedSessionId,
          pollResultPreviewUrl: pollResult?.previewUrl
        });
        
        setPreviewTabs(currentTabs => {
          const updatedTabs = [...currentTabs];
          const oldTab = updatedTabs[tabIndex];
          updatedTabs[tabIndex] = {
            ...updatedTabs[tabIndex],
            url: finalPreviewUrl,
            sessionId: expectedSessionId,
            userId: userId,
            loading: false,
            // Clear render progress state to hide the loading bar
            renderPhase: undefined,
            renderProgress: undefined,
            renderLogs: undefined,
            iframeKey: Date.now()
          };
          // eslint-disable-next-line no-console
          console.log('✅ Tab state updated:', {
            serviceName: updatedTabs[tabIndex].serviceName,
            wasLoading: oldTab.loading,
            nowLoading: updatedTabs[tabIndex].loading,
            hadUrl: !!oldTab.url,
            hasUrl: !!updatedTabs[tabIndex].url,
            url: updatedTabs[tabIndex].url
          });
          return updatedTabs;
        });
        
        // Save session to localStorage
        saveSessionToStorage(selectedProject?.id, serviceName, userId, {
          sessionId: expectedSessionId,
          previewUrl: finalPreviewUrl,
          containerPort: containerPort,
          language: tab.language,
          framework: framework,
          tenantId: selectedTenant?.id || selectedTenant?.tenantId,
          projectId: selectedProject?.id
        });
        
        // Log the URL that was set
        // eslint-disable-next-line no-console
        console.log('✅ Auto-render URL set:', finalPreviewUrl);
      }
      
      // eslint-disable-next-line no-console
      console.log('✅ Auto-render completed successfully - Preview is now 100% ready');
      
      // Check if there's a pending initial prompt from new project creation
      // Dispatch it NOW after render is complete (100%)
      const pendingPrompt = sessionStorage.getItem('mia_pending_initial_prompt');
      const pendingMode = sessionStorage.getItem('mia_pending_initial_mode');
      const pendingFiles = sessionStorage.getItem('mia_pending_initial_files');
      
      if (pendingPrompt) {
        // eslint-disable-next-line no-console
        console.log('🚀 [Preview] Render complete at 100%, triggering initial prompt:', pendingPrompt.substring(0, 100));
        
        // Clear the pending flags
        sessionStorage.removeItem('mia_pending_initial_prompt');
        sessionStorage.removeItem('mia_pending_initial_mode');
        sessionStorage.removeItem('mia_pending_initial_files');
        
        // Wait for selectedResource AND send context directly before prompt
        const waitForContextWithService = async () => {
          // eslint-disable-next-line no-console
          console.log('⏳ [Auto-render] Checking context state... selectedResource:', selectedResource?.data?.name, 'receivingFromParent:', receivingContextFromParentRef.current);
          
          // Check if we have selectedResource and we're not in the middle of receiving from parent
          if (selectedResource && selectedResource.data && selectedResource.data.name && !receivingContextFromParentRef.current) {
            // eslint-disable-next-line no-console
            console.log('✅ [Auto-render] Service selected and context ready:', selectedResource.data.name);
            
            // FORCE context send - critical for home-created projects
            // Temporarily clear the receiving flag to ensure context is sent
            const wasReceiving = receivingContextFromParentRef.current;
            receivingContextFromParentRef.current = false;
            
            // Directly send context to parent now (no waiting for useEffect)
            const contextSent = await sendContextToParent();
            
            // Restore flag
            receivingContextFromParentRef.current = wasReceiving;
            
            if (contextSent) {
              // eslint-disable-next-line no-console
              console.log('✅ [Auto-render] Context FORCED and sent directly to App');
            } else {
              // eslint-disable-next-line no-console
              console.warn('⚠️ [Auto-render] Context send failed, retrying...');
              setTimeout(waitForContextWithService, 150);
              return;
            }
            
            // Wait briefly for React state updates to propagate
            setTimeout(() => {
              // Dispatch custom event to populate chat input and auto-send (for home-created projects)
              const populateInputEvent = new CustomEvent('populate-chat-input', {
                detail: {
                  message: pendingPrompt,
                  autoSend: true, // Auto-send for home-created projects
                  setAskAboutComponents: true
                }
              });
              
              window.dispatchEvent(populateInputEvent);
              // eslint-disable-next-line no-console
              console.log('✅ [Auto-render] Dispatched populate-chat-input event with autoSend after direct context send');
            }, 300); // Shorter delay since we sent context directly
          } else {
            // Not ready yet, check again in 150ms
            setTimeout(waitForContextWithService, 150);
          }
        };
        
        // Start checking
        waitForContextWithService();
      }
      
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Auto-render failed:', error);
      
      const autoRenderErrorMessage = `Auto-render failed: ${error.message}`;

      // Show the Build Error Modal so the user is not left with a white screen
      setBuildErrorModal({
        isOpen: true,
        error: autoRenderErrorMessage,
        serviceName,
      });

      // Also mark the tab as errored so it doesn't stay in loading state
      const tabIndex = previewTabs.findIndex(t => t.serviceName === serviceName);
      if (tabIndex >= 0) {
        setPreviewTabs(currentTabs => {
          const updatedTabs = [...currentTabs];
          updatedTabs[tabIndex] = {
            ...updatedTabs[tabIndex],
            loading: false,
            error: autoRenderErrorMessage
          };
          return updatedTabs;
        });
      }
    } finally {
      // Remove from processing set after a delay to prevent rapid re-triggers
      setTimeout(() => {
        handlingIframeErrorRef.current.delete(sessionKey);
      }, 5000);
    }
  };
  
  /**
   * Handle iframe load event to detect "Preview session not found" errors
   * When the container returns this error, automatically trigger a full re-render
   */
  const handleIframeLoad = async (event, tab) => {
    try {
      const iframe = event.target;
      const iframeWindow = iframe.contentWindow;

      // eslint-disable-next-line no-console
      console.log('📡 iframe onLoad triggered for:', {
        url: iframe.src,
        serviceName: tab?.serviceName
      });
      
      // Try to read the iframe content to detect error responses
      try {
        const iframeDoc = iframeWindow.document;
        const bodyText = iframeDoc.body?.textContent || '';
        const bodyHTML = iframeDoc.body?.innerHTML || '';
        const documentTitle = iframeDoc.title || '';
        
        // Inject error capture script into iframe
        try {
          // eslint-disable-next-line no-use-before-define
          injectIframeErrorCapture(iframe);
        } catch (injectError) {
          // eslint-disable-next-line no-console
          console.log('Could not inject error capture (cross-origin?):', injectError.message);
        }
        
        // eslint-disable-next-line no-console
        console.log('📄 iframe content:', {
          bodyLength: bodyText.length,
          htmlLength: bodyHTML.length,
          title: documentTitle,
          preview: bodyText.substring(0, 300),
          htmlPreview: bodyHTML.substring(0, 300)
        });
        
        // Check for various error indicators:
        // 1. JSON error about preview session not found
        // 2. Chrome's "temporarily down" error page
        // 3. Empty or minimal content (< 100 chars likely means error)
        // 4. Session not found in error messages
        // 5. Chrome error pages
        const isPreviewSessionError = 
          bodyText.includes('Preview session not found') ||
          bodyText.includes('"success":false') ||
          bodyText.includes('might be temporarily down') ||
          bodyText.includes('may have moved permanently') ||
          (bodyText.includes('Session') && bodyText.includes('not found')) ||
          (bodyText.length < 100 && !bodyHTML.includes('<div id=')) ||
          bodyHTML.includes('ERR_CONNECTION') ||
          bodyHTML.includes('chrome-error://') ||
          documentTitle.includes('Error');
        
        if (isPreviewSessionError) {
          // eslint-disable-next-line no-console
          console.log('🚨 Detected preview error in iframe:', {
            bodyLength: bodyText.length,
            preview: bodyText.substring(0, 200),
            serviceName: tab.serviceName,
            triggers: {
              hasSessionNotFound: bodyText.includes('Preview session not found'),
              hasSuccessFalse: bodyText.includes('"success":false'),
              hasTempDown: bodyText.includes('might be temporarily down'),
              hasMovedPermanently: bodyText.includes('may have moved permanently'),
              hasSessionError: bodyText.includes('Session') && bodyText.includes('not found'),
              isEmptyContent: bodyText.length < 100 && !bodyHTML.includes('<div id='),
              hasErrConnection: bodyHTML.includes('ERR_CONNECTION'),
              hasChromeError: bodyHTML.includes('chrome-error://'),
              hasErrorTitle: documentTitle.includes('Error')
            }
          });
          
          // Trigger auto-render
          await autoRenderOnPreviewNotFound(tab);
        } else {
          // eslint-disable-next-line no-console
          console.log('✅ Preview loaded successfully (has content)');
          
          // Clear render logs now that iframe has successfully loaded
          const tabIndex = previewTabs.findIndex(t => t.serviceName === tab.serviceName);
          if (tabIndex >= 0) {
            setPreviewTabs(currentTabs => {
              const updatedTabs = [...currentTabs];
              updatedTabs[tabIndex] = {
                ...updatedTabs[tabIndex],
                renderLogs: undefined,
                renderPhase: undefined,
                renderProgress: undefined,
                iframeLoaded: true, // Mark iframe as loaded to hide loading overlay
                loading: false // Ensure loading is false
              };
              return updatedTabs;
            });
            // eslint-disable-next-line no-console
            console.log('🧹 Cleared render logs after successful iframe load');
          }
          
          // Auto-capture thumbnail after successful load
          if (selectedProject?._id && selectedTenant?.tenantId) {
            // eslint-disable-next-line no-console
            console.log('📸 Scheduling auto-capture for project:', selectedProject.name);
            scheduleAutoCapture(
              iframe,
              selectedProject._id,
              selectedTenant.tenantId,
              3000 // Wait 3 seconds after load for content to fully render
            );
          }
        }
      } catch (crossOriginError) {
        // Cross-origin iframe - cannot read content
        // This is expected for successful previews running on same origin but different protocol/port
        // eslint-disable-next-line no-console
        console.log('🔒 Cross-origin iframe (expected for working preview):', crossOriginError.message);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error handling iframe load:', error);
    }
  };
  
  /**
   * Manually capture thumbnail from current preview
   */
  const handleCaptureThumbnail = async () => {
    // Debug logging
    // eslint-disable-next-line no-console
    console.log('🔍 Capture thumbnail debug:');
    // eslint-disable-next-line no-console
    console.log('  iframeRef.current:', iframeRef.current);
    // eslint-disable-next-line no-console
    console.log('  selectedProject:', selectedProject);
    // eslint-disable-next-line no-console
    console.log('  selectedProject._id:', selectedProject?._id);
    // eslint-disable-next-line no-console
    console.log('  selectedProject.id:', selectedProject?.id);
    // eslint-disable-next-line no-console
    console.log('  selectedTenant:', selectedTenant);
    // eslint-disable-next-line no-console
    console.log('  selectedTenant.tenantId:', selectedTenant?.tenantId);
    // eslint-disable-next-line no-console
    console.log('  previewTabsCount:', previewTabs.length);
    // eslint-disable-next-line no-console
    console.log('  activePreviewTab:', activePreviewTab);

    // Try to get iframe from ref first, then from DOM
    let iframe = iframeRef.current;
    
    if (!iframe) {
      // Fallback: find the active iframe from DOM
      const iframes = document.querySelectorAll('.frontend-preview-iframe, .split-preview-iframe');
      // eslint-disable-next-line no-console
      console.log('🔍 Found iframes in DOM:', iframes.length);
      
      if (iframes.length > 0) {
        // Get the visible iframe (not display:none)
        for (const iframeElement of iframes) {
          const style = window.getComputedStyle(iframeElement.parentElement);
          if (style.display !== 'none') {
            iframe = iframeElement;
            // eslint-disable-next-line no-console
            console.log('✅ Found visible iframe');
            break;
          }
        }
      }
    }

    // Use selectedProject.id instead of selectedProject._id
    const projectId = selectedProject?.id || selectedProject?._id;
    
    if (!iframe || !projectId || !selectedTenant?.tenantId) {
      // eslint-disable-next-line no-console
      console.warn('⚠️  Cannot capture thumbnail - missing:');
      // eslint-disable-next-line no-console
      console.warn('  hasIframe:', !!iframe);
      // eslint-disable-next-line no-console
      console.warn('  hasProjectId:', !!projectId);
      // eslint-disable-next-line no-console
      console.warn('  hasTenant.tenantId:', !!selectedTenant?.tenantId);
      addActivityLog('⚠️ Cannot capture: missing preview or project');
      return;
    }

    setIsCapturingThumbnail(true);
    try {
      // eslint-disable-next-line no-console
      console.log('📸 Manually capturing thumbnail for project:', selectedProject.name);
      
      const result = await autoCaptureThumbnail(
        iframe,
        projectId,
        selectedTenant.tenantId,
        { delay: 500, quality: 0.9 } // Short delay, high quality for manual capture
      );
      
      if (result.success) {
        // Track screenshot capture
        if (window.analytics?.trackAction) {
          window.analytics.trackAction('preview', 'screenshot', { 
            serviceName: selectedResource?.data?.name 
          });
        }
        
        // Track feature usage
        if (window.analytics?.trackFeature) {
          window.analytics.trackFeature('screenshot_capture');
        }
        
        addActivityLog('✅ Thumbnail captured successfully');
        // eslint-disable-next-line no-console
        console.log('✅ Thumbnail captured:', result.thumbnailUrl);
        
        // Trigger a custom event to notify other components (like Projects page)
        window.dispatchEvent(new CustomEvent('thumbnail-updated', { 
          detail: { projectId, thumbnailUrl: result.thumbnailUrl } 
        }));
      } else {
        addActivityLog('⚠️ Thumbnail capture failed');
        // eslint-disable-next-line no-console
        console.error('❌ Thumbnail capture failed:', result.error);
      }
    } catch (error) {
      addActivityLog('❌ Thumbnail capture error');
      // eslint-disable-next-line no-console
      console.error('❌ Error capturing thumbnail:', error);
    } finally {
      setIsCapturingThumbnail(false);
    }
  };
  
  /**
   * Extract project ID from GitLab repository URL
   */
  const extractProjectIdFromUrl = (repositoryUrl) => {
    try {
      const url = new URL(repositoryUrl.replace('.git', ''));
      const pathParts = url.pathname.split('/').filter(Boolean);
      if (pathParts.length >= 2) {
        return pathParts.join('/');
      }
      return null;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to parse repository URL:', err);
      return null;
    }
  };

  /**
   * Check if the git provider tool is enabled on the backend and if the user is authenticated.
   * Used as a pre-flight check before any git fetch (preview, code tab, etc.)
   * @param {string} repoUrl - Repository URL
   * @returns {Promise<{toolEnabled: boolean, authenticated: boolean, providerName: string}>}
   */
  const checkGitToolStatus = async (repoUrl) => {
    if (!repoUrl) return { toolEnabled: false, authenticated: false, providerName: 'Git' };
    try {
      const { default: unifiedGitSvc } = await import('../services/UnifiedGitDataService');
      const provider = unifiedGitSvc.detectProvider(repoUrl);
      const providerName = provider === 'github' ? 'GitHub' : provider === 'azuredevops' ? 'Azure DevOps' : 'GitLab';

      // 1. Check tool enablement on the server
      let toolEnabled = true;
      try {
        const { getBffHeaders } = await import('../utils/apiHeaders');
        const bffUrl = config.bffUrl || window.location.origin;
        const tenantId = selectedTenant?.tenantId || selectedTenant?.id;
        const toolsParams = tenantId ? `?tenantId=${tenantId}` : '';
        const toolsRes = await getAuthorizedFetch()(`${bffUrl}/api/catalog/ai-tools${toolsParams}`, { headers: getBffHeaders() });
        if (toolsRes.ok) {
          const toolsData = await toolsRes.json();
          const allTools = toolsData?.data?.items || [];
          const providerTools = allTools.filter(t => {
            const cat = (t.category || '').toLowerCase();
            const name = (t.name || '').toLowerCase();
            const runtimeName = (t.runtimeName || '').toLowerCase();
            if (provider === 'github') return cat.includes('github') || name.includes('github') || runtimeName.includes('github');
            if (provider === 'azuredevops') return cat.includes('azure') || name.includes('azure') || runtimeName.includes('azure_devops');
            return cat.includes('gitlab') || name.includes('gitlab') || runtimeName.includes('gitlab');
          });
          if (providerTools.length > 0) {
            toolEnabled = providerTools.some(t => t.enabled !== false);
          }
        }
      } catch (toolsErr) {
        // eslint-disable-next-line no-console
        console.warn('⚠️ [Preview] Could not check tool status, assuming enabled:', toolsErr.message);
        toolEnabled = true;
      }

      // 2. Check authentication status
      const authStatus = await unifiedGitSvc.checkAuthentication(provider);
      return { toolEnabled, authenticated: authStatus.authenticated, providerName };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('❌ [Preview] Error checking git tool status:', err);
      return { toolEnabled: true, authenticated: false, providerName: 'Git' };
    }
  };

  /**
   * Fetch all files from GitLab/GitHub repository using a single recursive tree call.
   * The API always returns the full recursive tree in one request (recursive=true),
   * so we avoid redundant sub-directory calls by processing the flat list directly.
   */
  const fetchAllRepositoryFiles = async (projectId, repoUrl = null, onProgress = null) => {
    const unifiedGitDataService = (await import('../services/UnifiedGitDataService')).default;
    const sourceCode = {};
    // Use a blacklist of known binary/compiled extensions instead of a whitelist,
    // so backend projects (Java, Python, Go, …) are fully included.
    const binaryExtensions = new Set([
      '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.ico', '.webp', '.tiff',
      '.pdf', '.zip', '.tar', '.gz', '.rar', '.7z',
      '.exe', '.dll', '.so', '.dylib', '.class', '.jar', '.war', '.ear',
      '.ttf', '.otf', '.woff', '.woff2', '.eot',
      '.mp3', '.mp4', '.avi', '.mov', '.mkv', '.wav', '.ogg'
    ]);
    const skipDirs = new Set(['node_modules', '.git', 'build', 'dist', '.next', 'target', 'vendor']);

    // Get project info to find default branch
    let ref = 'main';
    try {
      const info = await unifiedGitDataService.getProject(projectId, repoUrl);
      ref = info.default_branch || 'main';
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Could not get default branch, using main:', err);
    }

    // Single API call – the service always uses recursive=true so we get the full tree at once
    let allItems = [];
    try {
      allItems = await unifiedGitDataService.getRepositoryTree(projectId, repoUrl, '', ref);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching repository tree:', err);
      throw err;
    }

    // Filter out binary files and files inside ignored directories.
    // Files without an extension (e.g. .gitignore, .mia-template) are treated as text.
    const fileItems = allItems.filter(item => {
      if (item.type !== 'blob') return false;
      // Skip files inside ignored directories
      const parts = item.path.split('/');
      if (parts.some(part => skipDirs.has(part))) return false;
      // Skip known binary extensions; include everything else (text/code)
      const dotIdx = item.name.lastIndexOf('.');
      const ext = dotIdx !== -1 ? item.name.slice(dotIdx).toLowerCase() : '';
      return !binaryExtensions.has(ext);
    });

    const totalFiles = fileItems.length;
    let fetchedFiles = 0;

    // Fetch file contents sequentially with progress tracking
    for (const item of fileItems) {
      try {
        const fileData = await unifiedGitDataService.getFileContent(projectId, repoUrl, item.path, ref);
        sourceCode[item.path] = fileData.decodedContent || '';
        fetchedFiles++;

        if (onProgress) {
          const progress = Math.round((fetchedFiles / totalFiles) * 100);
          onProgress({
            current: fetchedFiles,
            total: totalFiles,
            percentage: progress,
            currentFile: item.path
          });
        }
      } catch (fileErr) {
        // eslint-disable-next-line no-console
        console.warn(`Could not fetch file ${item.path}:`, fileErr);
        fetchedFiles++; // Still count as processed
      }
    }

    return sourceCode;
  };
  
  const selectedMicroservice = selectedResource?.type === 'service' ? selectedResource.data : (selectedResource?.type === 'configuration' ? selectedResource.data : null);
  
  // Track previous values to prevent unnecessary URL updates
  const prevMicroserviceBranchTabRef = useRef({});
  
  // Notify parent of selection changes (preserve existing file path in URL)
  useEffect(() => {
    if (!onSelectionChange) return;
    
    const microserviceName = selectedMicroservice?.name || null;
    const branchName = selectedRevision?.name || null;
    const tab = activeTab;
    
    // Only call if microservice, branch, or tab changed (ignore file changes)
    const prev = prevMicroserviceBranchTabRef.current;
    if (prev.microserviceName === microserviceName &&
        prev.branchName === branchName &&
        prev.tab === tab) {
      return; // Nothing changed, skip
    }
    
    // Update ref
    prevMicroserviceBranchTabRef.current = { microserviceName, branchName, tab };
    
    // Preserve file path from current URL to avoid removing it
    // RepositoryTree manages file selection, Canvas should not overwrite it
    const currentFilePath = window.location.pathname.match(/\/code\/(.+)$/)?.[1];
    const decodedFilePath = currentFilePath ? decodeURIComponent(currentFilePath) : null;
    
    // Pass along existing file path to preserve it in URL
    onSelectionChange(microserviceName, branchName, tab, decodedFilePath, null);
  }, [selectedMicroservice, selectedRevision, activeTab, onSelectionChange]);
  
  // Get repository URL - normalize different field names (repoUrl, repositoryUrl, sshUrl)
  const repositoryUrl = useMemo(() => {
    if (!selectedMicroservice) return null;
    const url = selectedMicroservice.repositoryUrl || selectedMicroservice.repoUrl || selectedMicroservice.sshUrl || null;
    // eslint-disable-next-line no-console
    console.log('🔍 [Canvas] Getting repository URL:', {
      serviceName: selectedMicroservice.name,
      type: selectedResource?.type,
      repositoryUrl: selectedMicroservice.repositoryUrl,
      repoUrl: selectedMicroservice.repoUrl,
      sshUrl: selectedMicroservice.sshUrl,
      resolved: url
    });
    return url;
  }, [selectedMicroservice, selectedResource]);
  
  // Repository view state - controls whether to show repository tree or service details
  const [showRepositoryView, setShowRepositoryView] = useState(false);
  
  // Tree panel visibility state
  const [showTreePanel, setShowTreePanel] = useState(true);
  
  // Repository actions dropdown state
  const [showRepoDropdown, setShowRepoDropdown] = useState(false);
  const repoDropdownRef = useRef(null);

  // Auto-select first file when generatedFiles change
  useEffect(() => {
    if (generatedFiles && generatedFiles.length > 0 && !selectedFile) {
      setSelectedFile(generatedFiles[0]);
    }
  }, [generatedFiles, selectedFile]);

  // Persist Mia Platform selections to localStorage
  useEffect(() => {
    if (selectedTenant) {
      localStorage.setItem('mia_selectedTenant', JSON.stringify(selectedTenant));
    } else {
      localStorage.removeItem('mia_selectedTenant');
    }
  }, [selectedTenant]);

  useEffect(() => {
    if (selectedProject) {
      localStorage.setItem('mia_selectedProject', JSON.stringify(selectedProject));
    } else {
      localStorage.removeItem('mia_selectedProject');
    }
  }, [selectedProject]);
  
  // Persist selected resource for URL reconstruction
  useEffect(() => {
    if (selectedResource) {
      // Include projectId so we can validate it matches when reconstructing URL
      const resourceWithProject = {
        ...selectedResource,
        projectId: selectedProject?.id || selectedProject?.projectId || selectedProject?._id
      };
      localStorage.setItem('mia_selectedResource', JSON.stringify(resourceWithProject));
    } else {
      localStorage.removeItem('mia_selectedResource');
    }
  }, [selectedResource, selectedProject]);
  
  // Persist active tab for URL reconstruction
  useEffect(() => {
    if (activeTab) {
      localStorage.setItem('mia_activeTab', activeTab);
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedRevision) {
      localStorage.setItem('mia_selectedRevision', JSON.stringify(selectedRevision));
    } else {
      localStorage.removeItem('mia_selectedRevision');
    }
  }, [selectedRevision]);

  // Load files from IndexedDB when a service is selected
  // This ensures files are available for AI file requests immediately
  useEffect(() => {
    const loadFilesForSelectedService = async () => {
      if (!selectedResource || selectedResource.type !== 'service' || !selectedProject?.id) {
        return;
      }

      const serviceName = selectedResource.data?.name;
      if (!serviceName) {
        return;
      }

      // Prevent loading files multiple times for the same service
      const serviceKey = `${selectedProject.id}|${serviceName}`;
      if (lastLoadedServiceRef.current === serviceKey) {
        return;
      }

      try {
        // eslint-disable-next-line no-console
        console.log(`📂 Auto-loading files for selected service: ${serviceName}`);
        
        const filesFromDb = await indexedDbFileStorage.getAllFiles(
          selectedProject.id,
          serviceName
        );

        if (filesFromDb && filesFromDb.length > 0) {
          const allFilesWithContent = filesFromDb.map(file => ({
            path: file.filePath,
            content: file.content
          }));

          // Mark this service as loaded BEFORE calling onProjectContextChange
          lastLoadedServiceRef.current = serviceKey;

          // Update project context with loaded files
          if (onProjectContextChange) {
            onProjectContextChange({
              tenant: selectedTenant,
              project: selectedProject,
              revision: selectedRevision,
              selectedResource: selectedResource,
              selectedMicroservice: {
                name: serviceName,
                type: selectedResource.data?.type,
                repositoryUrl: selectedResource.data?.repoUrl || selectedResource.data?.sshUrl || selectedResource.data?.repositoryUrl
              },
              allFiles: allFilesWithContent,
              selectedFile: selectedFile,
              configuration: configuration,
              collections: collections,
              projectId: selectedProject.id
            });
          }

          // eslint-disable-next-line no-console
          console.log(`✅ Loaded ${filesFromDb.length} files from IndexedDB for AI context`);
        } else {
          // eslint-disable-next-line no-console
          console.log(`⚠️ No files found in IndexedDB for service ${serviceName} - user needs to Pull files first`);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load files from IndexedDB:', error);
      }
    };

    loadFilesForSelectedService();
  }, [selectedResource, selectedProject]);

  // Close repo dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (repoDropdownRef.current && !repoDropdownRef.current.contains(event.target)) {
        setShowRepoDropdown(false);
      }
    };

    if (showRepoDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showRepoDropdown]);

  // Close repo dropdown when resource changes
  useEffect(() => {
    if (selectedResource) {
      setShowRepoDropdown(false);
    }
  }, [selectedResource]);

  // Load tenants when canvas is opened
  useEffect(() => {
    if (isVisible && tenants.length === 0 && !tenantsLoading) {
      loadTenants();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  // Load project context from parent (when project is selected from Projects page)
  useEffect(() => {
    if (projectContext && isVisible) {
      console.log('📋 Received project context from parent:', {
        projectId: projectContext.projectId,
        projectName: projectContext.projectName,
        tenantId: projectContext.tenant?.tenantId
      });
      
      // Mark that we're receiving context from parent
      receivingContextFromParentRef.current = true;
      
      // Set tenant if available and valid (only if actually different)
      if (projectContext.tenant && projectContext.tenant.tenantId) {
        const newTenantId = projectContext.tenant.tenantId;
        const currentTenantId = selectedTenant?.id || selectedTenant?.tenantId;
        
        if (newTenantId !== currentTenantId) {
          setSelectedTenant({
            id: projectContext.tenant.tenantId,
            tenantId: projectContext.tenant.tenantId,
            name: projectContext.tenant.name
          });
        }
      }
      
      // Set project if available (only if actually different)
      if (projectContext.project) {
        const newProjectId = projectContext.project._id || projectContext.project.projectId || projectContext.project.id;
        const currentProjectId = selectedProject?.id;
        
        if (newProjectId && newProjectId !== currentProjectId) {
          // Project is changing - clear selected resource from previous project
          setSelectedResource(null);
          
          setSelectedProject({
            id: newProjectId,
            name: projectContext.project.name,
            description: projectContext.project.description,
            tenantId: projectContext.project.tenantId || projectContext.tenant?.tenantId,
            raw: projectContext.project
          });
        }
      }
      
      // Set revision if available (only if actually different)
      if (projectContext.revision) {
        const newRevisionId = projectContext.revision.revisionId;
        const currentRevisionId = selectedRevision?.id;
        
        if (newRevisionId && newRevisionId !== currentRevisionId) {
          setSelectedRevision({
            id: projectContext.revision.revisionId,
            name: projectContext.revision.name || projectContext.revision.revisionId
          });
        }
      }
      
      // Clear the flag after a brief delay to allow state updates to trigger dependent useEffects
      setTimeout(() => {
        receivingContextFromParentRef.current = false;
        // eslint-disable-next-line no-console
        console.log('✅ Cleared receivingContextFromParentRef flag - Canvas can now respond to updates');
      }, 200); // Short delay for responsiveness
    }
  }, [projectContext, isVisible]);

  // Load projects when tenant is selected
  useEffect(() => {
    if (selectedTenant && !projectsLoading) {
      // eslint-disable-next-line no-console
      console.log('🏢 Loading projects for tenant:', selectedTenant);
      
      // Validate tenant has required ID before proceeding
      if (!selectedTenant.id) {
        console.error('❌ Tenant selected but ID is missing!', selectedTenant);
        // Clear invalid tenant to prevent loops
        setSelectedTenant(null);
        return;
      }
      
      loadProjects(selectedTenant.id, '', 1, false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant]);
  
  // Load revisions when project is selected
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('🔄 useEffect for revisions triggered. selectedProject:', selectedProject, 'revisionsLoading:', revisionsLoading);
    
    if (selectedProject && selectedProject.id && !revisionsLoading) {
      // Detect project switch and cleanup UI state
      const isProjectSwitch = lastProjectIdRef.current && lastProjectIdRef.current !== selectedProject.id;
      
      if (isProjectSwitch) {
        // eslint-disable-next-line no-console
        console.log('🔄 Project switch detected, cleaning up state...', {
          from: lastProjectIdRef.current,
          to: selectedProject.id
        });
        
        // Clear preview tabs immediately (UI state only)
        setPreviewTabs([]);
        
        // Clear configuration from previous project so dropdown shows empty until new config loads
        setConfiguration(null);
        
        // Clear selected revision IMMEDIATELY to prevent config loading with stale revision
        setSelectedRevision(null);
        
        // Note: We keep render sessions alive so they can be reused
        // when the user returns to this project
        
        // Clear loading and error tracking
        loadingPreviewRef.current.clear();
        errorCountRef.current = {};
        autoTriggeredPullRef.current.clear();
        lastLoadedServiceRef.current = null;
        
        // Clear configuration to prevent auto-selection from old project's services
        setConfiguration({
          endpoints: {},
          collections: {},
          groups: [],
          secrets: [],
          cmsCategories: {},
          services: {}
        });
      }
      
      // Update the last project ID tracker
      lastProjectIdRef.current = selectedProject.id;
      
      // eslint-disable-next-line no-console
      console.log('📝 Loading revisions for project:', selectedProject);
      
      // Clear selected resource when switching projects to force auto-selection of new project's service
      setSelectedResource(null);
      
      loadRevisions(selectedProject.id);
      // Force git fetch when a new project is selected
      setForceGitFetch(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, selectedProject?.id]);
  
  // Fetch project thumbnail when project is selected
  useEffect(() => {
    // Only fetch if we don't already have a thumbnail
    if (selectedProject?.id && selectedProject?.tenantId && !selectedProject?.thumbnail) {
      getProjectThumbnail(selectedProject.id, selectedProject.tenantId).then(thumbnailUrl => {
        if (thumbnailUrl) {
          setSelectedProject(prev => ({
            ...prev,
            thumbnail: thumbnailUrl
          }));
        }
      }).catch(error => {
        console.debug('Failed to fetch thumbnail:', error);
      });
    }
  }, [selectedProject?.id, selectedProject?.tenantId, selectedProject?.thumbnail]);
  
  // Load configuration when revision is selected
  useEffect(() => {
    if (selectedProject && selectedProject.id && selectedRevision && selectedRevision.id && !configLoading) {
      // eslint-disable-next-line no-console
      console.log('🔧 Loading configuration for project:', selectedProject.id, 'revision:', selectedRevision.id);
      loadConfiguration(selectedProject.id, selectedRevision.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRevision?.id, selectedProject?.id]);
  
  // Auto-select first service when configuration is loaded (unless already have a selection or URL param)
  useEffect(() => {
    if (!configuration || !configuration.services) return;
    
    const services = Object.values(configuration.services);
    if (services.length === 0) return;
    
    // Only auto-select if:
    // 1. No service is currently selected, OR
    // 2. The currently selected service doesn't exist in the new configuration
    const currentServiceExists = selectedResource?.type === 'service' && 
      configuration.services[selectedResource.name];
    
    if (!selectedResource || !currentServiceExists) {
      // Don't auto-select if we have an initialMicroservice (URL will handle it)
      if (initialMicroservice) return;
      
      // Check if there's a previously selected resource from localStorage
      const storedResource = localStorage.getItem('mia_selectedResource');
      const storedProjectId = selectedProject?.id || selectedProject?.projectId || selectedProject?._id;
      
      if (storedResource && storedProjectId) {
        try {
          const parsed = JSON.parse(storedResource);
          // Only restore if it's for the same project and the resource still exists
          if (parsed.projectId === storedProjectId) {
            if (parsed.type === 'service' && configuration.services[parsed.data?.name]) {
              console.log('🔄 Restoring last selected service:', parsed.data?.name);
              setSelectedResource(parsed);
              return;
            } else if (parsed.type === 'configuration') {
              console.log('🔄 Restoring last selected configuration');
              setSelectedResource(parsed);
              return;
            }
          }
        } catch (e) {
          console.warn('Failed to restore last selected resource:', e);
        }
      }
      
      // First-time entry: If user is in preview or code view, try to find a frontend service
      if (activeTab === 'preview' || activeTab === 'code') {
        // Find first frontend service with preview capability
        const frontendService = services.find(service => {
          const detection = detectServiceType(service);
          return detection.isFrontend && (service.repoUrl || service.sshUrl || service.repositoryUrl);
        });
        
        if (frontendService) {
          console.log('🎯 Auto-selecting frontend service for preview:', frontendService.name);
          setSelectedResource({type: 'service', data: frontendService, name: frontendService.name});
          return;
        }
      }
      
      // Fallback: Auto-select project configuration (configs) by default
      console.log('🎯 Auto-selecting project configuration (configs)');
      const configurationRepo = {
        name: 'Project Configuration',
        repositoryUrl: fullProjectConfig?.repositoryUrl || fullProjectConfig?.configurationGitPath || selectedProject?.repositoryUrl || selectedProject?.configurationGitPath,
        repoUrl: fullProjectConfig?.repositoryUrl || fullProjectConfig?.configurationGitPath || selectedProject?.repositoryUrl || selectedProject?.configurationGitPath,
        type: 'configuration'
      };
      setSelectedResource({type: 'configuration', data: configurationRepo});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration]);
  
  // Notify parent when selected file or resource changes
  useEffect(() => {
    const updateProjectContext = async () => {
      // Skip if we're currently receiving context from parent to prevent feedback loop
      if (receivingContextFromParentRef.current) {
        console.log('⏭️ Skipping context update (receiving from parent)');
        return;
      }
      
      // Skip if we don't have a project yet (but allow updates without revision for file requests)
      if (!selectedProject) {
        // eslint-disable-next-line no-console
        console.log('⏭️ Skipping context update (no project selected)');
        return;
      }
      
      if (onProjectContextChange) {
        // Get all files from IndexedDB for the selected microservice
        let allFiles = null;
        if (selectedProject && selectedProject.id && selectedMicroservice && selectedMicroservice.name) {
          const filesArray = await indexedDbFileStorage.getAllFiles(selectedProject.id, selectedMicroservice.name);
          allFiles = await Promise.all(filesArray.map(async (fileObj) => {
            const filePath = fileObj.filePath;
            const gitState = fileObj.gitState || fileObj.state; // Support both new and legacy format
            return {
              path: filePath,
              name: fileObj.fileName,
              content: fileObj.content || '',
              isNew: gitState === GIT_STATE.NEW || gitState === 'new',
              modified: gitState !== GIT_STATE.ORIGINAL && gitState !== 'original'
            };
          }));
          
          // eslint-disable-next-line no-console
          console.log(`📦 Canvas: Loaded ${allFiles.length} files from IndexedDB for ${selectedMicroservice.name}`);
          
          // Auto-trigger Pull if no files are loaded in IndexedDB
          if (!allFiles || allFiles.length === 0) {
            const serviceKey = `${selectedProject.id}|${selectedMicroservice.name}`;
            
            // Only auto-trigger once per service to prevent infinite loop
            if (!autoTriggeredPullRef.current.has(serviceKey)) {
              // eslint-disable-next-line no-console
              console.warn(`⚠️ No files in IndexedDB for project ${selectedProject.id}, service ${selectedMicroservice.name}. Auto-triggering Pull from GitLab...`);
              
              // Mark that we've triggered pull for this service
              autoTriggeredPullRef.current.add(serviceKey);
              
              // Dispatch event to trigger Pull operation
              window.dispatchEvent(new CustomEvent('autoTriggerGitPull', {
                detail: {
                  projectId: selectedProject.id,
                  serviceName: selectedMicroservice.name,
                  reason: 'No files in IndexedDB'
                }
              }));
            } else {
              // eslint-disable-next-line no-console
              console.log(`⏭️ Skipping auto-trigger for ${selectedMicroservice.name} (already triggered)`);
            }
          } else {
            // Check for files that need to be pushed using getChangedFilesForPreview
            const changedFiles = await indexedDbFileStorage.getChangedFilesForPreview(
              selectedProject.id,
              selectedMicroservice.name
            );
            
            // eslint-disable-next-line no-console
            console.log(`📂 Project context updated with ${allFiles.length} files from ${selectedMicroservice.name} (${changedFiles.length} files need to be pushed)`);
            
            // Update hasModifications state for Push button
            setHasModifications(changedFiles.length > 0);
          }
        }
      
      // Get file content for selected file
      const selectedFileContent = selectedFile && selectedMicroservice ? (await indexedDbFileStorage.getFile(
        selectedProject.id,
        selectedMicroservice.name,
        selectedFile.path
      ))?.content || selectedFile.content : null;
      
      // Build the new context object
      const newContext = {
        tenant: selectedTenant,
        project: selectedProject,
        revision: selectedRevision,
        selectedResource: selectedResource,
        selectedMicroservice: selectedMicroservice ? {
          name: selectedMicroservice.name,
          type: selectedMicroservice.type,
          repositoryUrl: repositoryUrl
        } : null,
        selectedFile: selectedFile ? {
          name: selectedFile.name,
          path: selectedFile.path,
          content: selectedFileContent,
          language: selectedFile.language
        } : null,
        allFiles: allFiles,
        configuration: configuration,
        collections: collections,
        projectId: selectedProject?.id
      };
      
      // Only send update if context actually changed (compare essential fields)
      const contextKey = JSON.stringify({
        tenantId: selectedTenant?.id,
        projectId: selectedProject?.id,
        revisionId: selectedRevision?.id,
        resourceName: selectedResource?.data?.name,
        resourceType: selectedResource?.type,
        microserviceName: selectedMicroservice?.name,
        filePath: selectedFile?.path,
        fileCount: allFiles?.length,
        configHash: configuration ? Object.keys(configuration).sort().join(',') : null,
        collectionsHash: collections ? Object.keys(collections).sort().join(',') : null
      });
      
      // Only send if context actually changed
      if (lastProjectContextRef.current !== contextKey) {
        lastProjectContextRef.current = contextKey;
        
        if (allFiles && allFiles.length > 0) {
          // eslint-disable-next-line no-console
          console.log('📤 Sending updated project context to parent:', {
            tenant: selectedTenant?.name,
            project: selectedProject?.name,
            revision: selectedRevision?.name,
            resource: selectedResource?.data?.name,
            fileCount: allFiles?.length
          });
        } else {
          // eslint-disable-next-line no-console
          console.log('📤 Sending updated project context to parent (no files):', {
            tenant: selectedTenant?.name,
            project: selectedProject?.name,
            revision: selectedRevision?.name,
            resource: selectedResource?.data?.name
          });
        }
        onProjectContextChange(newContext);
      } else {
        // eslint-disable-next-line no-console
        console.log('⏭️ Skipping context update (no changes)');
      }
      }
    };
    
    updateProjectContext();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // OPTIMIZATION: Removed selectedFile from dependencies to prevent full project reload on file selection
  // File changes are still captured in the context update, just not triggering a full reload
  }, [selectedMicroservice, repositoryUrl, selectedTenant, selectedProject, selectedRevision, selectedResource, configuration, collections]);
  
  // Extract context sending logic for reuse (direct calls)
  const sendContextToParent = useCallback(async () => {
    // Skip if we're receiving context from parent
    if (receivingContextFromParentRef.current) {
      console.log('⏭️ Skipping direct context send (receiving from parent)');
      return false;
    }
    
    // Skip if we don't have required data
    if (!selectedProject || !onProjectContextChange) {
      console.log('⏭️ Skipping direct context send (no project or callback)');
      return false;
    }
    
    // Get all files from IndexedDB for the selected microservice
    let allFiles = null;
    if (selectedProject && selectedProject.id && selectedMicroservice && selectedMicroservice.name) {
      const filesArray = await indexedDbFileStorage.getAllFiles(selectedProject.id, selectedMicroservice.name);
      allFiles = await Promise.all(filesArray.map(async (fileObj) => {
        const filePath = fileObj.filePath;
        const gitState = fileObj.gitState || fileObj.state; // Support both new and legacy format
        return {
          path: filePath,
          name: fileObj.fileName,
          content: fileObj.content || '',
          isNew: gitState === GIT_STATE.NEW || gitState === 'new',
          modified: gitState !== GIT_STATE.ORIGINAL && gitState !== 'original'
        };
      }));
    }
    
    // Get file content for selected file
    const selectedFileContent = selectedFile ? (await indexedDbFileStorage.getFile(
      selectedProject.id,
      selectedMicroservice.name,
      selectedFile.path
    ))?.content || selectedFile.content : null;
    
    // Build the context object
    const newContext = {
      tenant: selectedTenant,
      project: selectedProject,
      revision: selectedRevision,
      selectedResource: selectedResource,
      selectedMicroservice: selectedMicroservice ? {
        name: selectedMicroservice.name,
        type: selectedMicroservice.type,
        repositoryUrl: repositoryUrl
      } : null,
      selectedFile: selectedFile ? {
        name: selectedFile.name,
        path: selectedFile.path,
        content: selectedFileContent,
        language: selectedFile.language
      } : null,
      allFiles: allFiles,
      configuration: configuration,
      collections: collections,
      projectId: selectedProject?.id
    };
    
    // Send context to parent
    console.log('📤 [Direct Send] Sending context to parent:', {
      project: selectedProject?.name,
      service: selectedMicroservice?.name,
      fileCount: allFiles?.length
    });
    
    onProjectContextChange(newContext);
    return true;
  }, [selectedProject, selectedTenant, selectedRevision, selectedResource, selectedMicroservice, selectedFile, configuration, collections, repositoryUrl, onProjectContextChange]);

  // Load source code for preview from IndexedDB
  const loadSourceCode = useCallback(async () => {
    if (selectedProject && selectedResource?.data?.name) {
      try {
        // Validate we have valid parameters
        if (!selectedProject.id || !selectedResource.data.name) {
          // eslint-disable-next-line no-console
          console.warn('⚠️ Cannot load source code: invalid project ID or service name', {
            projectId: selectedProject.id,
            serviceName: selectedResource.data.name
          });
          return;
        }

        const files = await indexedDbFileStorage.getFilesForAIContext(
          selectedProject.id,
          selectedResource.data.name
        );
        // getFilesForAIContext returns an object, not an array
        const fileCount = files ? Object.keys(files).length : 0;
        // eslint-disable-next-line no-console
        console.log(`📂 Loaded ${fileCount} files from IndexedDB for tree view`);
        
        // Force tree refresh by incrementing the key
        setTreeRefreshKey(prev => prev + 1);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load source code from IndexedDB:', error);
      }
    }
  }, [selectedProject?.id, selectedResource?.data?.name]);

  useEffect(() => {
    loadSourceCode();
  }, [loadSourceCode]);

  // Close repo dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showRepoDropdown && !event.target.closest('.repo-dropdown-wrapper')) {
        setShowRepoDropdown(false);
      }
    };

    if (showRepoDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showRepoDropdown]);

  // Function to load tenants
  const loadTenants = async () => {
    try {
      setTenantsLoading(true);
      setTenantsError(null);
      
      const tenantsData = await miaPlatformDataService.getTenants();
      // eslint-disable-next-line no-console
      console.log('🏢 Tenants received:', tenantsData);
      const formattedTenants = tenantsData.map(tenant => 
        miaPlatformDataService.formatTenant(tenant)
      );
      // eslint-disable-next-line no-console
      console.log('🏢 Formatted tenants:', formattedTenants);
      setTenants(formattedTenants);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load tenants:', error);
      setTenantsError(error.message || 'Failed to load tenants');
    } finally {
      setTenantsLoading(false);
    }
  };

  // Function to load projects with optional search and pagination
  const loadProjects = async (tenantId, searchQuery = '', page = 1, append = false) => {
    try {
      // eslint-disable-next-line no-console
      console.log('📁 Loading projects with tenantId:', tenantId, 'search:', searchQuery, 'page:', page);
      setProjectsLoading(true);
      setProjectsError(null);
      
      const result = await miaPlatformDataService.getProjects(tenantId, searchQuery, page);
      // eslint-disable-next-line no-console
      console.log('📁 Projects received:', result);
      
      const formattedProjects = result.data.map(project => 
        miaPlatformDataService.formatProject(project)
      );
      
      if (append) {
        // Append to existing projects for infinite scroll
        setProjects(prev => [...prev, ...formattedProjects]);
      } else {
        // Replace projects for new search or initial load
        setProjects(formattedProjects);
      }
      
      // Store pagination info
      setProjectsPagination(result.pagination);
      
      // eslint-disable-next-line no-console
      console.log('📊 Pagination info:', result.pagination);
      // eslint-disable-next-line no-console
      console.log('📊 Has more projects:', result.pagination?.has_more);
      // eslint-disable-next-line no-console
      console.log('📊 Current page:', result.pagination?.page, 'of', result.pagination?.total_pages);
      
      // Auto-select first project if only one is available
      if (!append && formattedProjects.length === 1) {
        setSelectedProject(formattedProjects[0]);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load projects:', error);
      setProjectsError(error.message || 'Failed to load projects');
    } finally {
      setProjectsLoading(false);
    }
  };

  // Handle project search
  const handleProjectSearch = useCallback((searchQuery) => {
    if (selectedTenant) {
      // Reset to page 1 when searching
      setProjectsCurrentPage(1);
      // Only clear selection if user is actively searching (not when clearing search)
      // Don't clear when searchQuery is empty (programmatic clear after selection)
      if (searchQuery && searchQuery !== projectsSearchQuery) {
        setSelectedProject(null);
        setSelectedRevision(null);
        setRevisions([]);
      }
      setProjectsSearchQuery(searchQuery);
      loadProjects(selectedTenant.id, searchQuery, 1, false);
    }
  }, [selectedTenant, projectsSearchQuery]);

  // Handle load more for infinite scroll
  const handleLoadMoreProjects = useCallback(() => {
    if (selectedTenant && projectsPagination?.has_more && !projectsLoading) {
      const nextPage = projectsCurrentPage + 1;
      setProjectsCurrentPage(nextPage);
      loadProjects(selectedTenant.id, projectsSearchQuery, nextPage, true);
    }
  }, [selectedTenant, projectsPagination, projectsLoading, projectsCurrentPage, projectsSearchQuery]);

  // Handle tenant selection
  const handleTenantSelect = (tenant) => {
    // Ensure tenant object has required fields
    const formattedTenant = {
      id: tenant.id || tenant.tenantId,
      tenantId: tenant.id || tenant.tenantId, // Backward compatibility
      name: tenant.name || 'Unnamed Tenant'
    };
    setSelectedTenant(formattedTenant);
    setSelectedProject(null); // Reset project when tenant changes
    setProjects([]); // Clear previous projects
    // Reset pagination state
    setProjectsPagination(null);
    setProjectsCurrentPage(1);
    setProjectsSearchQuery('');
    // Clear center panel
    setSelectedRevision(null);
    setRevisions([]);
    setConfiguration(null);
    setSelectedResource(null);
    setSelectedFile(null);
    
    // Notify parent about context change (tenant selected, no project yet)
    if (onProjectContextChange) {
      onProjectContextChange({
        tenant: tenant,
        project: null,
        revision: null
      });
    }
  };

  // Handle project selection
  const handleProjectSelect = (project) => {
    // eslint-disable-next-line no-console
    console.log('🎯 Project selected:', project);
    
    /**
     * RACE CONDITION FIX:
     * Don't send project context update here with revision:null.
     * Wait for revisions to load first, then the updateProjectContext useEffect
     * will send the complete context with the selected revision.
     * This fixes the issue where creating a project from home would fail to
     * get file and project context until chat was manually stopped and continued.
     */
    
    // Ensure project object has required fields
    const formattedProject = {
      id: project.id || project._id || project.projectId,
      name: project.name,
      description: project.description,
      tenantId: project.tenantId,
      raw: project.raw || project
    };
    
    setSelectedProject(formattedProject);
    
    // CRITICAL FIX: Sync tenant with project's tenantId
    // If project belongs to a different tenant, update selectedTenant
    if (project.tenantId && (!selectedTenant || selectedTenant.id !== project.tenantId)) {
      const projectTenant = tenants.find(t => t.tenantId === project.tenantId || t.id === project.tenantId);
      if (projectTenant) {
        // eslint-disable-next-line no-console
        console.log('🔄 Syncing tenant with project - updating from', selectedTenant?.name, 'to', projectTenant.name);
        const formattedTenant = {
          id: projectTenant.id || projectTenant.tenantId,
          tenantId: projectTenant.id || projectTenant.tenantId,
          name: projectTenant.name
        };
        setSelectedTenant(formattedTenant);
      } else {
        // eslint-disable-next-line no-console
        console.warn('⚠️ Could not find tenant for project tenantId:', project.tenantId);
      }
    }
    
    // Reset revision and configuration when project changes
    setSelectedRevision(null);
    setRevisions([]);
    setConfiguration(null);
    // Clear center panel
    setSelectedResource(null);
    setSelectedFile(null);
    // Clear preview session and URL
    setPreviewSessionId(null);
    setPreviewUrl(null);
    
    // Clear all stored userIds when switching projects
    // This prevents tabs from reusing userIds from previous project
    serviceUserIds.current = {};
    // eslint:disable-next-line no-console
    console.log('🧹 Cleared service userIds for new project');
    
    // Don't send context update here - wait for revisions to load
    // This prevents race condition where context is sent with revision:null
    // and then the revisions load later but context is not updated
  };
  
  // Handle create project button click
  const handleCreateProjectClick = () => {
    if (!selectedTenant) {
      alert('Please select an organization first');
      return;
    }
    setIsCreateProjectModalOpen(true);
  };
  
  // Handle create project submission
  const handleCreateProject = async (projectData) => {
    try {
      // Call the service to create project via BFF
      const result = await miaPlatformDataService.createProject(projectData);
      
      // Refresh projects list
      await loadProjects(selectedTenant.id, projectsSearchQuery, 1, false);
      
      // Auto-select the newly created project
      const newProject = result.project;
      if (newProject) {
        const formattedProject = {
          id: newProject._id || newProject.id,
          name: newProject.name,
          tenantId: newProject.tenantId,
          ...newProject
        };
        handleProjectSelect(formattedProject);
      }
      
      return result;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to create project:', error);
      // Enhance error message for permissions issues
      if (error.message.includes('FORBIDDEN')) {
        throw new Error('Permission denied: The service account does not have permission to create projects in this organization. Please contact your Mia Platform administrator to grant project creation permissions.');
      }
      throw error;
    }
  };
  
  // Function to load revisions
  const loadRevisions = async (projectId) => {
    try {
      setRevisionsLoading(true);
      setRevisionsError(null);
      
      const revisionsData = await miaPlatformDataService.getRevisions(projectId);
      // eslint-disable-next-line no-console
      console.log('📝 Revisions received:', revisionsData);
      const formattedRevisions = revisionsData.map(revision => 
        miaPlatformDataService.formatRevision(revision)
      );
      setRevisions(formattedRevisions);
      
      // Auto-select default revision if available
      const defaultRevision = formattedRevisions.find(rev => rev.isDefault);
      if (defaultRevision) {
        setSelectedRevision(defaultRevision);
      } else if (formattedRevisions.length > 0) {
        setSelectedRevision(formattedRevisions[0]);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load revisions:', error);
      setRevisionsError(error.message || 'Failed to load revisions');
    } finally {
      setRevisionsLoading(false);
    }
  };
  
  // Function to load configuration (microservices and other resources)
  const loadConfiguration = async (projectId, revisionId) => {
    try {
      setConfigLoading(true);
      setConfigError(null);
      
      const config = await miaPlatformDataService.getMicroservices(
        projectId,
        revisionId
      );
      // eslint-disable-next-line no-console
      console.log('🔧 Configuration received:', config);
      
      setConfiguration(config);
      
      // Merge collections from config with IndexedDB
      const configCollections = config?.collections || {};
      
      // Migrate from localStorage if needed (first time)
      await indexedDbCollectionStorage.migrateFromLocalStorage(projectId);
      
      const mergedCollections = await indexedDbCollectionStorage.mergeWithConfiguration(
        projectId,
        configCollections
      );
      setCollections(mergedCollections);
      
      // eslint-disable-next-line no-console
      console.log('📊 Collections merged:', {
        fromConfig: Object.keys(configCollections).length,
        total: Object.keys(mergedCollections).length
      });
      
      // Sync all merged collections (including Console API collections) to crud-service-universal
      if (Object.keys(mergedCollections).length > 0) {
        syncAllCollections(projectId, mergedCollections).catch(err => {
          console.warn('⚠️ Failed to sync merged collections to crud-service-universal:', err); /* eslint-disable-line no-console */
        });
      }
      
      // Fetch full project configuration from BFF
      await loadFullProjectConfiguration(projectId);
      
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load configuration:', error);
      setConfigError(error.message || 'Failed to load configuration');
    } finally {
      setConfigLoading(false);
      
      // Clear the project switching flag now that Canvas configuration is fully loaded
      // This allows URL syncing to resume after initial project load is complete
      const switchingToProject = sessionStorage.getItem('mia_switching_to_project');
      if (switchingToProject === projectId) {
        sessionStorage.removeItem('mia_switching_to_project');
        // eslint-disable-next-line no-console
        console.log('✅ Canvas: Cleared project switching flag after config load:', projectId);
      }
    }
  };
  
  // Function to load full project configuration from Console API via BFF
  const loadFullProjectConfiguration = async (projectId) => {
    try {
      const fullConfig = await miaPlatformDataService.getFullProjectConfiguration(projectId);
      
      // Store in state and localStorage
      setFullProjectConfig(fullConfig);
      localStorage.setItem('mia_fullProjectConfig', JSON.stringify(fullConfig));
      
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to load full project configuration:', error);
      // Don't fail the whole configuration load if this fails
    }
  };
  
  // Handle revision selection
  const handleRevisionSelect = (revision) => {
    setSelectedRevision(revision);
    setConfiguration(null); // Clear configuration when revision changes
    
    // Notify parent about revision context change
    if (onProjectContextChange) {
      onProjectContextChange({
        tenant: selectedTenant,
        project: selectedProject,
        revision: revision,
        selectedFile: null, // Clear file when revision changes
        selectedResource: null // Clear resource when revision changes
      });
    }
  };
  
  // Automatically detect all previewable services and create tabs when configuration loads
  useEffect(() => {
    if (!configuration || !configuration.services || !selectedProject) {
      return;
    }

    const servicesArray = Object.values(configuration.services);
    const previewableServices = servicesArray.filter(service => {
      const detection = detectServiceType(service);
      const hasRepo = service.repoUrl || service.sshUrl || service.repositoryUrl;
      return (detection.isFrontend || detection.isBackend) && hasRepo;
    });

    if (previewableServices.length === 0) {
      setPreviewTabs([]);
      return;
    }

    // eslint-disable-next-line no-console
    console.log(`🎯 Found ${previewableServices.length} previewable service(s):`, previewableServices.map(s => s.name));

    // Don't automatically create tabs or select services
    // Let the user explicitly select a service from the dropdown
    // This prevents loading all services at once and causing loops
    
    // Only initialize preview tabs if we're on the preview tab and have a selected service
    if (activeTab === 'preview' && selectedResource?.type === 'service') {
      const service = selectedResource.data;
      const repoUrl = service.repoUrl || service.sshUrl || service.repositoryUrl;

      // Run git pre-flight asynchronously before creating the tab, so we
      // never show "Rendering … preview…" spinner for a blocked provider.
      (async () => {
        // 1. Check if we already have a tab (avoid duplicate creation)
        // We'll do the real setPreviewTabs below after the async check.

        // 2. Check IndexedDB for cached files — if present, git isn't needed now
        let hasLocalFiles = false;
        try {
          const { default: idbStorage } = await import('../utils/indexedDbFileStorage');
          const localFiles = await idbStorage.getAllFiles(selectedProject.id, service.name);
          hasLocalFiles = localFiles.length > 0;
        } catch { /* ignore */ }

        // 3. If no local files, run pre-flight against the git provider
        let blockedError = null;
        if (!hasLocalFiles && repoUrl) {
          const gitStatus = await checkGitToolStatus(repoUrl);
          if (!gitStatus.toolEnabled || !gitStatus.authenticated) {
            blockedError = !gitStatus.toolEnabled
              ? `__GIT_TOOL_DISABLED__:${gitStatus.providerName}`
              : `__GIT_NOT_AUTHENTICATED__:${gitStatus.providerName}`;
            // eslint-disable-next-line no-console
            console.warn(`🚫 [TabCreate] Blocked (${blockedError}) for`, service.name);
          }
        }

        // 4. Create / update tab with the correct initial state
        setPreviewTabs(currentTabs => {
          const existingTabIndex = currentTabs.findIndex(tab => tab.serviceName === service.name);

          if (existingTabIndex === -1) {
            // ── New tab ────────────────────────────────────────────────────────
            const userId = getOrCreateUserId(selectedProject?.id, service.name);
            const existingSession = getSessionFromStorage(selectedProject?.id, service.name, userId);
            const hasExistingSession = existingSession && existingSession.previewUrl;

            // Use saved session values when available (correct port/language/framework from a
            // previous render). For brand-new sessions leave them null — the real values will
            // be filled in once source code is fetched and analysed (~line 4383).
            const newTab = {
              serviceName: service.name,
              service: service,
              url: hasExistingSession ? existingSession.previewUrl : null,
              sessionId: hasExistingSession ? existingSession.sessionId : null,
              userId: hasExistingSession ? userId : null,
              containerPort: hasExistingSession ? existingSession.containerPort : null,
              language: hasExistingSession ? existingSession.language : null,
              framework: hasExistingSession ? existingSession.framework : null,
              // If blocked: show error immediately — no spinner
              loading: !hasExistingSession && !blockedError,
              iframeLoaded: hasExistingSession,
              error: hasExistingSession ? null : (blockedError || null),
              iframeKey: Date.now()
            };

            // eslint-disable-next-line no-console
            console.log('📝 Creating new tab for:', service.name,
              hasExistingSession ? '(existing session)' : blockedError ? '(blocked)' : '(new session)');
            setActivePreviewTab(currentTabs.length);
            return [...currentTabs, newTab];
          } else {
            // ── Existing tab ───────────────────────────────────────────────────
            // eslint-disable-next-line no-console
            console.log('♻️ Tab already exists for:', service.name, 'at index:', existingTabIndex);

            const existingTab = currentTabs[existingTabIndex];

            // If blocked and tab has no URL/error yet, update it to show the error
            if (blockedError && !existingTab.url && !existingTab.error) {
              const updatedTabs = [...currentTabs];
              updatedTabs[existingTabIndex] = {
                ...existingTab,
                loading: false,
                error: blockedError
              };
              setActivePreviewTab(existingTabIndex);
              return updatedTabs;
            }

            if (!existingTab.url && !existingTab.error) {
              const userId = getOrCreateUserId(selectedProject?.id, service.name);
              const existingSession = getSessionFromStorage(selectedProject?.id, service.name, userId);
              if (existingSession && existingSession.previewUrl) {
                // eslint-disable-next-line no-console
                console.log('✨ Found existing session for tab, loading immediately!');
                const updatedTabs = [...currentTabs];
                updatedTabs[existingTabIndex] = {
                  ...existingTab,
                  url: existingSession.previewUrl,
                  sessionId: existingSession.sessionId,
                  userId: userId,
                  tenantId: selectedTenant?.id || selectedTenant?.tenantId,
                  projectId: selectedProject?.id,
                  containerPort: existingSession.containerPort,
                  language: existingSession.language,
                  framework: existingSession.framework,
                  loading: false,
                  error: null
                };
                setActivePreviewTab(existingTabIndex);
                return updatedTabs;
              }
            }

            setActivePreviewTab(existingTabIndex);
            return currentTabs;
          }
        });
      })();
    }
    // Note: previewTabs is intentionally excluded from dependencies to prevent infinite loops
    // We only want this effect to run when configuration, project, activeTab, or selectedResource changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration, selectedProject, activeTab, selectedResource]);
  
  // Check if selected resource is a frontend/backend service and load preview
  // Only auto-load if user is already on preview tab
  useEffect(() => {
    let isCancelled = false; // Track if effect was cleaned up
    
    const loadServicePreview = async () => {
      // Only proceed if we have a selected service resource
      // Skip if it's configuration/project config (no preview needed)
      if (!selectedResource || selectedResource.type !== 'service' || selectedResource.type === 'configuration') {
        setPreviewUrl(null);
        return;
      }

      // NEW: Only auto-load preview if user is already on the preview tab
      // This prevents automatic tab switching when clicking services in sidebar
      if (activeTab !== 'preview') {
        // eslint-disable-next-line no-console
        console.log('⏭️ Skipping auto-preview load (not on preview tab)');
        return;
      }

      const service = selectedResource.data;
      
      // CRITICAL: Skip if we're receiving context from parent (prevents cascade)
      // EXCEPT for home-created projects where we need to pull files and start preview
      const isHomeCreatedProject = sessionStorage.getItem('mia_pending_initial_prompt');
      if (receivingContextFromParentRef.current && !isHomeCreatedProject) {
        // eslint-disable-next-line no-console
        console.log('⏭️ Skipping preview load (receiving from parent)');
        return;
      }
      
      if (receivingContextFromParentRef.current && isHomeCreatedProject) {
        // eslint-disable-next-line no-console
        console.log('🆕 Home-created project detected - proceeding with preview load despite receiving from parent');
      }
      
      // Check if there's already an error for this service in preview tabs
      // Don't retry if the error is about BFF connection
      if (previewTabs.length > 0) {
        const existingTab = previewTabs.find(tab => tab.serviceName === service.name);
        if (existingTab && existingTab.error && 
            (existingTab.error.includes('BFF server') || existingTab.error.includes('No source files'))) {
          // Don't retry loading if we already have a BFF connection error
          return;
        }
      }
      
      // Prevent concurrent loads of the same service
      // BUT: Check if the loading state is stale (stuck for more than 3 minutes)
      if (loadingPreviewRef.current.has(service.name)) {
        // Check if the corresponding tab is actually loading
        const correspondingTab = previewTabs.find(tab => tab.serviceName === service.name);
        
        // Clear stale loading state if:
        // 1. Tab is loading but has an error (failed build/render)
        // 2. Tab is loading but has no URL (stuck in initial state)
        // 3. No corresponding tab exists (orphaned loading state)
        if (!correspondingTab || 
            (correspondingTab.loading && correspondingTab.error) ||
            (correspondingTab.loading && !correspondingTab.url)) {
          // eslint-disable-next-line no-console
          console.log('🔄 Clearing stale loading state for:', service.name, {
            hasTab: !!correspondingTab,
            hasError: correspondingTab?.error,
            hasUrl: correspondingTab?.url
          });
          loadingPreviewRef.current.delete(service.name);
          // Continue with loading
        } else {
          // eslint-disable-next-line no-console
          console.log('⏸️  Service is already loading, skipping:', service.name);
          return;
        }
      }
      
      // Circuit breaker: Stop trying after 3 consecutive failures
      const errorCount = errorCountRef.current[service.name] || 0;
      if (errorCount >= 3) {
        console.warn(`⚠️ Circuit breaker: Too many errors for ${service.name}, stopping retries`);
        return;
      }
      
      // Prevent reloading the same service if nothing relevant changed
      // Check if this is the same service as last time AND neither selectedRevision nor forceGitFetch changed
      const serviceKey = `${selectedProject?.id}|${service.name}|${selectedRevision || 'latest'}|${forceGitFetch ? 'force' : 'normal'}`;
      if (lastLoadedServiceRef.current === serviceKey) {
        // Same service, same revision, same fetch mode - skip reload
        return;
      }
      
      // Mark as loading
      loadingPreviewRef.current.add(service.name);
      
      // eslint-disable-next-line no-console
      console.log('🔍 Checking service for preview:', {
        name: service.name,
        tags: service.tags,
        type: selectedResource.type
      });
      
      // Intelligently detect service type
      const {isFrontend, isBackend, confidence} = detectServiceType(service);
      
      // eslint-disable-next-line no-console
      console.log('🏷️ Service type detected:', {isFrontend, isBackend, confidence});
      
      // Check if user has explicitly selected a tab (from preview tabs)
      const isExplicitTabSelection = previewTabs.length > 0 && 
        previewTabs.some(tab => tab.serviceName === service.name);
      
      // If tabs exist, check if this tab already has content ready
      // This prevents unnecessary re-renders when just switching between tabs
      if (previewTabs.length > 0) {
        const tabForThisService = previewTabs.find(tab => tab.serviceName === service.name);
        if (tabForThisService) {
          // If the tab already has a URL, the container/iframe is ready - just show it
          if (tabForThisService.url) {
            console.log('✅ Tab already loaded with URL, skipping re-render:', service.name);
            loadingPreviewRef.current.delete(service.name);
            return;
          }
          // If tab is not currently loading and has no URL, skip for now
          if (!tabForThisService.loading && !tabForThisService.url) {
            return;
          }
        }
      }
      
      // Priority: Always show frontend if available in the project
      // BUT: If user explicitly clicked a tab, respect their choice
      let serviceToPreview = service;
      
      // Allow both frontend and backend services to be previewed directly
      // Backend services (Java, Node, Go, Python, etc.) will use their respective container-live servers
      if (!isFrontend && !isBackend && !isExplicitTabSelection) {
        // Service is neither frontend nor backend, and wasn't explicitly selected - don't show preview
        setPreviewUrl(null);
        return;
      }

      // Check if we have repository URL to fetch source code
      // Try multiple fields: repoUrl, sshUrl, repositoryUrl
      const repoUrl = serviceToPreview.repoUrl || serviceToPreview.sshUrl || serviceToPreview.repositoryUrl;
      
      // eslint-disable-next-line no-console
      console.log('📦 Repository URL:', repoUrl);
      
      if (!repoUrl) {
        // eslint-disable-next-line no-console
        console.warn('⚠️ Service has no repository URL:', serviceToPreview.name);
        
        // Update preview tabs if they exist
        if (previewTabs.length > 0) {
          const tabIndex = previewTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
          if (tabIndex >= 0) {
            const updatedTabs = [...previewTabs];
            updatedTabs[tabIndex] = {
              ...updatedTabs[tabIndex],
              loading: false,
              error: 'No repository URL available for this service'
            };
            setPreviewTabs(updatedTabs);
          }
        } else {
          setPreviewError('No repository URL available for this service');
        }
        return;
      }

      // ── PRE-FLIGHT: check tool enablement & authentication BEFORE any git call ──
      // This must run before the try{} block so no spinner/loading state is set
      // and no branch/project API calls are made against a disabled/unlinked provider.
      {
        const gitStatus = await checkGitToolStatus(repoUrl);
        if (!gitStatus.toolEnabled || !gitStatus.authenticated) {
          const blockedError = !gitStatus.toolEnabled
            ? `__GIT_TOOL_DISABLED__:${gitStatus.providerName}`
            : `__GIT_NOT_AUTHENTICATED__:${gitStatus.providerName}`;
          // eslint-disable-next-line no-console
          console.warn(`🚫 [Preview] Pre-flight blocked (${blockedError}) for`, serviceToPreview.name);
          loadingPreviewRef.current.delete(serviceToPreview.name);
          if (previewTabs.length > 0) {
            setPreviewTabs(prevTabs => prevTabs.map(t =>
              t.serviceName === serviceToPreview.name
                ? { ...t, loading: false, pullingCode: false, error: blockedError }
                : t
            ));
          } else {
            setPreviewLoading(false);
            setPreviewError(blockedError);
          }
          return;
        }
      }
      // ────────────────────────────────────────────────────────────────────────────

      try {
        // Update loading state
        if (previewTabs.length > 0) {
          const tabIndex = previewTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
          if (tabIndex >= 0) {
            const updatedTabs = [...previewTabs];
            updatedTabs[tabIndex] = {
              ...updatedTabs[tabIndex],
              loading: true,
              error: null
            };
            setPreviewTabs(updatedTabs);
          }
        } else {
          setPreviewLoading(true);
          setPreviewError(null);
        }
        
        // Check if effect was cancelled (cleanup ran)
        if (isCancelled) return;

        // eslint-disable-next-line no-console
        console.log('🎨 Loading preview for service:', serviceToPreview.name);
        // eslint-disable-next-line no-console
        console.log('📋 Service details:', {
          type: serviceToPreview.type,
          hasRepo: !!repoUrl
        });

        // Validate we have valid IDs before querying IndexedDB
        if (!selectedProject?.id || !serviceToPreview.name) {
          console.error('❌ Cannot load preview: missing project ID or service name', {
            projectId: selectedProject?.id,
            serviceName: serviceToPreview.name,
            selectedProjectFull: selectedProject
          });
          return;
        }

        // Check if we have IndexedDB files for this service
        const {default: indexedDbFileStorage} = await import('../utils/indexedDbFileStorage');
        const localFiles = await indexedDbFileStorage.getAllFiles(selectedProject.id, serviceToPreview.name);
        const hasLocalFiles = localFiles.length > 0;
        
        // Check if this is a new project that needs force git pull
        const forceGitPull = sessionStorage.getItem('mia_force_git_pull_on_load') === 'true';
        if (forceGitPull) {
          console.log('🆕 [Preview] New project detected - forcing git pull');
          sessionStorage.removeItem('mia_force_git_pull_on_load');
        }
        
        // eslint-disable-next-line no-console
        console.log('📊 IndexedDB check:', {
          hasLocalFiles,
          fileCount: localFiles.length,
          projectId: selectedProject?.id,
          serviceName: serviceToPreview.name,
          forceGitPull
        });
        
        // Fetch from git if:
        // 1. No IndexedDB files exist (first time), OR
        // 2. Force git pull flag is set (new project creation)
        const shouldFetchFromGit = !hasLocalFiles || forceGitPull;
        
        // eslint-disable-next-line no-console
        console.log('🔍 Decision:', shouldFetchFromGit ? 'Fetch from Git (first time)' : 'Use IndexedDB (source of truth)');
        
        let frontendSourceCode = {};
        
        if (shouldFetchFromGit) {

          // Parse project ID from repository URL
          const projectId = extractProjectIdFromUrl(repoUrl);
          if (!projectId) {
            throw new Error('Could not extract project ID from repository URL');
          }

          // eslint-disable-next-line no-console
          console.log('📦 Fetching repository files from git for project:', projectId);
          // eslint-disable-next-line no-console
          console.log('⏳ [Preview] Phase: FETCHING - Loading source code from repository...');

          // Show toast notification that git pulling is starting
          window.dispatchEvent(new CustomEvent('show-toast', {
            detail: { 
              message: `📦 Pulling code from Git repository for ${serviceToPreview.name}...`, 
              type: 'info' 
            }
          }));

          // Update preview tab to show "Pulling code from repository" message
          setPreviewTabs(prevTabs => prevTabs.map(t => 
            t.serviceName === serviceToPreview.name 
              ? {...t, pullingCode: true, renderPhase: 'Pulling code from Git', renderProgress: 0}
              : t
          ));

          // Fetch all files from the repository with progress tracking
          frontendSourceCode = await fetchAllRepositoryFiles(projectId, repoUrl, (progress) => {
            // Update preview tab with progress
            setPreviewTabs(prevTabs => prevTabs.map(t => 
              t.serviceName === serviceToPreview.name 
                ? {
                    ...t, 
                    renderPhase: `Pulling code from Git `,
                    renderProgress: progress.percentage,
                    renderLogs: [
                      ...(t.renderLogs || []).slice(-10), // Keep last 10 logs
                      `📥 ${progress.current}/${progress.total}: ${progress.currentFile}`
                    ]
                  }
                : t
            ));
          });
          
          if (!frontendSourceCode || Object.keys(frontendSourceCode).length === 0) {
            throw new Error('No source files found in repository');
          }

          // eslint-disable-next-line no-console
          console.log(`📦 Collected ${Object.keys(frontendSourceCode).length} files from git`);
          // eslint-disable-next-line no-console
          console.log('✅ [Preview] Phase: ANALYZING - Source code loaded, detecting framework...');
          
          // Update preview tab to show analysis phase
          setPreviewTabs(prevTabs => prevTabs.map(t => 
            t.serviceName === serviceToPreview.name 
              ? {...t, pullingCode: false, renderPhase: 'Analyzing and preparing'}
              : t
          ));
          
          // Store files in IndexedDB for future use (so user can see code in Code tab)
          // eslint-disable-next-line no-console
          console.log('💾 [Preview] Saving files to IndexedDB for Code tab access...');
          
          let savedCount = 0;
          for (const [filePath, content] of Object.entries(frontendSourceCode)) {
            try {
              // Files from Git should always be marked as ORIGINAL, not NEW
              await indexedDbFileStorage.saveFile(
                selectedProject.id,
                serviceToPreview.name,
                filePath,
                content,
                {
                  originalContent: content,
                  branch: DEFAULT_MICROSERVICE_BRANCH,
                  fileName: filePath.split('/').pop(),
                  fromGit: true // These files come from Git, so they're ORIGINAL
                }
              );
              savedCount++;
              // Log progress every 10 files
              if (savedCount % 10 === 0) {
                // eslint-disable-next-line no-console
                console.log(`💾 [Preview] Saved ${savedCount}/${Object.keys(frontendSourceCode).length} files...`);
              }
            } catch (saveErr) {
              // eslint-disable-next-line no-console
              console.warn(`⚠️ [Preview] Failed to save file ${filePath}:`, saveErr);
            }
          }
          // eslint-disable-next-line no-console
          console.log(`✅ [Preview] Successfully saved ${savedCount} files to IndexedDB`);
          
          // Mark that files are completely saved to IndexedDB
          sessionStorage.setItem('mia_files_saved_to_indexeddb', 'true');
          
          // Trigger repository tree refresh to visualize the files in Code tab
          // Dispatch event to update the file tree
          window.dispatchEvent(new CustomEvent('repositoryFilesUpdated', {
            detail: {
              projectId: selectedProject?.id,
              serviceName: serviceToPreview.name,
              fileCount: savedCount
            }
          }));
          // eslint-disable-next-line no-console
          console.log('🌲 [Preview] Dispatched event to refresh repository tree view');
          
          // Check if there's a pending initial prompt from new project creation
          // Dispatch it NOW after files are pulled (before preview loads)
          const pendingPrompt = sessionStorage.getItem('mia_pending_initial_prompt');
          
          if (pendingPrompt) {
            // eslint-disable-next-line no-console
            console.log('🚀 [Preview] Files pulled, triggering initial prompt immediately:', pendingPrompt.substring(0, 100));
            
            // Clear the pending flags
            sessionStorage.removeItem('mia_pending_initial_prompt');
            sessionStorage.removeItem('mia_pending_initial_mode');
            sessionStorage.removeItem('mia_pending_initial_files');
            
            // Wait for context to be ready, then dispatch
            const waitForContextReady = async () => {
              // Check if we have selectedResource and we're not in the middle of receiving from parent
              if (selectedResource && selectedResource.data && selectedResource.data.name && !receivingContextFromParentRef.current) {
                // eslint-disable-next-line no-console
                console.log('✅ [Preview] Service selected and context ready:', selectedResource.data.name);
                
                // FORCE context send - critical for home-created projects
                const wasReceiving = receivingContextFromParentRef.current;
                receivingContextFromParentRef.current = false;
                
                // Directly send context to parent now
                const contextSent = await sendContextToParent();
                
                // Restore flag
                receivingContextFromParentRef.current = wasReceiving;
                
                if (contextSent) {
                  // eslint-disable-next-line no-console
                  console.log('✅ [Preview] Context sent, dispatching auto-send event');
                  
                  // Wait briefly for React state updates
                  setTimeout(() => {
                    // Dispatch custom event to populate chat input and auto-send
                    window.dispatchEvent(new CustomEvent('populate-chat-input', {
                      detail: {
                        message: pendingPrompt,
                        autoSend: true, // Auto-send for home-created projects
                        setAskAboutComponents: true
                      }
                    }));
                    // eslint-disable-next-line no-console
                    console.log('✅ [Preview] Dispatched populate-chat-input event with autoSend after file pull');
                    
                    // Clear the flag
                    sessionStorage.removeItem('mia_files_saved_to_indexeddb');
                  }, 200);
                } else {
                  // eslint-disable-next-line no-console
                  console.warn('⚠️ [Preview] Context send failed, retrying...');
                  setTimeout(waitForContextReady, 150);
                }
              } else {
                // Not ready yet, check again
                setTimeout(waitForContextReady, 150);
              }
            };
            
            // Start checking
            waitForContextReady();
          }
          
          // Reset the force fetch flag
          setForceGitFetch(false);
        } else {
          // eslint-disable-next-line no-console
          console.log('📂 Using files from localStorage (skip git fetch)');
        }

        // Merge with localStorage modifications (or use only localStorage if no git fetch)
        const mergedFrontendFiles = await indexedDbFileStorage.getFilesForAIContext(
          selectedProject?.id,
          serviceToPreview.name,
          frontendSourceCode
        );
        
        if (Object.keys(mergedFrontendFiles).length === 0) {
          throw new Error('No files available for preview. Please refresh to fetch from git.');
        }
        
        const gitFileCount = Object.keys(frontendSourceCode).length;
        const totalFileCount = Object.keys(mergedFrontendFiles).length;
        
        if (gitFileCount > 0) {
          const modifiedCount = totalFileCount - gitFileCount;
          if (modifiedCount > 0) {
            // eslint-disable-next-line no-console
            console.log(`📝 Merged ${modifiedCount} modified frontend files from localStorage`);
          }
        } else {
          // eslint-disable-next-line no-console
          console.log(`📂 Using ${totalFileCount} frontend files from localStorage`);
        }

        // Check if the service being previewed is a frontend or backend service
        // CRITICAL: Pass source code files for accurate detection
        const serviceDetection = detectServiceType(serviceToPreview, mergedFrontendFiles);
        
        // Determine which container to use based on service type
        let isBackendPreview = false;
        let detectedLanguage;
        let frameworkDisplayName;
        
        // Get routing info using centralized utility
        const routingInfo = getContainerRoutingInfo(serviceToPreview, mergedFrontendFiles, {});
        
        if (!routingInfo) {
          // eslint-disable-next-line no-console
          console.log('⚠️ Service is not detected as frontend or backend, skipping preview');
          setPreviewUrl(null);
          return;
        }
        
        const { isBackend, isFrontend, framework, containerPort, containerName, frameworkDisplay } = routingInfo;
        isBackendPreview = isBackend;
        detectedLanguage = framework;
        frameworkDisplayName = frameworkDisplay;
        
        // eslint-disable-next-line no-console
        console.log(`🎯 Detected ${isBackend ? 'backend' : 'frontend'} framework: ${frameworkDisplayName}, using ${containerName} on port ${containerPort}`);
        
        // Update tab with correct framework immediately after detection
        if (previewTabs.length > 0) {
          const tabIndex = previewTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
          if (tabIndex >= 0) {
            setPreviewTabs(currentTabs => {
              const updatedTabs = [...currentTabs];
              updatedTabs[tabIndex] = {
                ...updatedTabs[tabIndex],
                framework: frameworkDisplayName,
                language: detectedLanguage,
                containerPort: containerPort
              };
              return updatedTabs;
            });
          }
        }
        
        if (isFrontend && framework === 'react') {
          // eslint-disable-next-line no-console
          console.log('🔧 [Preview] Phase: PREPARING - Starting container with detected framework...');
        }
        
        // Store container port for auto-sync
        setPreviewContainerPort(containerPort);

        // Get or create userId from localStorage (persists across page reloads)
        // This ensures consistent session IDs and eliminates session mismatch warnings
        const userId = getOrCreateUserId(selectedProject?.id, serviceToPreview.name);
        // eslint-disable-next-line no-console
        console.log(`🔑 Using User ID: ${userId}`);
        
        // Check if we have an existing session in localStorage
        const existingSession = getSessionFromStorage(selectedProject?.id, serviceToPreview.name, userId);
        
        if (existingSession && existingSession.previewUrl) {
          // eslint-disable-next-line no-console
          console.log('✨ Found existing session in localStorage, reusing it!');
          // eslint-disable-next-line no-console
          console.log('📋 Session data:', existingSession);
          
          // Validate session by checking port mappings
          try {
            const containerApiUrl = getContainerApiUrl(existingSession.containerPort || containerPort, '/api/port-mappings');
            // eslint-disable-next-line no-console
            console.log('🔍 Checking active sessions via port-mappings:', containerApiUrl);
            
            const mappingsResponse = await getAuthorizedFetch()(containerApiUrl, {
              method: 'GET',
              headers: getBffHeaders({})
            });
            
            if (mappingsResponse.ok) {
              const mappingsData = await mappingsResponse.json();
              
              // Check if our session exists in the active mappings
              // mappings is an array, not an object
              const sessionExists = mappingsData.mappings && Array.isArray(mappingsData.mappings) &&
                mappingsData.mappings.some(mapping => 
                  mapping.sessionId === existingSession.sessionId
                );
              
              // eslint-disable-next-line no-console
              console.log('🔍 Session check:', {
                lookingFor: existingSession.sessionId,
                activeMappings: mappingsData.mappings?.map(m => m.sessionId),
                found: sessionExists
              });
              
              if (sessionExists) {
                // eslint-disable-next-line no-console
                console.log('✅ Session is still active, reusing it');
                
                // Update preview tabs with existing session data
                if (previewTabs.length > 0) {
                  const tabIndex = previewTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
                  if (tabIndex >= 0) {
                    setPreviewTabs(currentTabs => {
                      const updatedTabs = [...currentTabs];
                      updatedTabs[tabIndex] = {
                        ...updatedTabs[tabIndex],
                        url: existingSession.previewUrl,
                        sessionId: existingSession.sessionId,
                        userId: userId,
                        tenantId: selectedTenant?.id || selectedTenant?.tenantId,
                        projectId: selectedProject?.id,
                        containerPort: existingSession.containerPort || containerPort,
                        language: existingSession.language || detectedLanguage,
                        loading: false,
                        iframeLoaded: true, // Session already exists, no need to show loading
                        error: null,
                        iframeKey: Date.now() // Force iframe refresh when reusing session
                      };
                      return updatedTabs;
                    });
                    // eslint-disable-next-line no-console
                    console.log('✅ Updated preview tab with existing session (iframe will refresh)');
                    
                    // Update mia_selectedSession to track this as the active session
                    setSelectedSession({
                      sessionId: existingSession.sessionId,
                      previewUrl: existingSession.previewUrl,
                      containerPort: existingSession.containerPort || containerPort,
                      language: existingSession.language || detectedLanguage,
                      tenantId: selectedTenant?.id || selectedTenant?.tenantId,
                      projectId: selectedProject?.id,
                      serviceName: serviceToPreview.name,
                      userId: userId,
                      lastAccessed: new Date().toISOString()
                    });
                  }
                } else {
                  // Fallback to old single preview mode
                  setPreviewUrl(existingSession.previewUrl);
                  setPreviewSessionId(existingSession.sessionId);
                  
                  // Update mia_selectedSession
                  setSelectedSession({
                    sessionId: existingSession.sessionId,
                    previewUrl: existingSession.previewUrl,
                    containerPort: existingSession.containerPort || containerPort,
                    language: existingSession.language || detectedLanguage,
                    tenantId: selectedTenant?.id || selectedTenant?.tenantId,
                    projectId: selectedProject?.id,
                    serviceName: serviceToPreview.name,
                    userId: userId,
                    lastAccessed: new Date().toISOString()
                  });
                }
                
                // Don't auto-switch tabs - let user choose when to view preview
                // Only switch if we're loading a new preview (not reusing a session)
                // This allows users to click services and see their code/config first
                // if (activeTab !== 'preview') {
                //   // eslint-disable-next-line no-console
                //   console.log('✅ Switching to preview tab to display reused session');
                //   setActiveTab('preview');
                // }
                
                setPreviewLoading(false);
                
                // Mark as successfully loaded
                const serviceKey = `${selectedProject?.id}|${serviceToPreview.name}|${selectedRevision || 'latest'}|normal`;
                lastLoadedServiceRef.current = serviceKey;
                
                // Clear from loading ref since we're reusing the session
                loadingPreviewRef.current.delete(serviceToPreview.name);
                
                // Exit early - session is valid
                return;
              } else {
                // eslint-disable-next-line no-console
                console.warn('⚠️ Session not found in active port mappings - will re-render');
                
                // Clear invalid session from storage
                const storageKey = `preview-session-${selectedProject?.id}-${serviceToPreview.name}-${userId}`;
                localStorage.removeItem(storageKey);
                
                // Continue to render a new session below
              }
            } else {
              // eslint-disable-next-line no-console
              console.warn('⚠️ Could not fetch port mappings (HTTP', mappingsResponse.status, ') - will re-render');
              
              // Clear session from storage
              const storageKey = `preview-session-${selectedProject?.id}-${serviceToPreview.name}-${userId}`;
              localStorage.removeItem(storageKey);
            }
          } catch (fetchError) {
            // eslint-disable-next-line no-console
            console.warn('⚠️ Session validation failed:', fetchError.message, '- will re-render');
            
            // Clear invalid session from storage
            const storageKey = `preview-session-${selectedProject?.id}-${serviceToPreview.name}-${userId}`;
            localStorage.removeItem(storageKey);
          }
        }
        
        // eslint-disable-next-line no-console
        console.log('🆕 No existing session found, creating new one...');

        // Find backend service in the configuration (only relevant for frontend previews)
        let backendService = null;
        if (!isBackendPreview && configuration && configuration.services) {
          // Convert services object to array of service objects
          const servicesArray = Object.values(configuration.services);
          backendService = servicesArray.find(svc => {
            const detection = detectServiceType(svc);
            return detection.isBackend;
          });
          
          if (backendService) {
            // eslint-disable-next-line no-console
            console.log('🔍 Found backend service:', backendService.name);
            // Store backend service for incremental updates
            setActiveBackendService(backendService);
          } else {
            setActiveBackendService(null);
          }
        } else if (isBackendPreview) {
          // If previewing backend, store it as active
          setActiveBackendService(serviceToPreview);
        }

        // Prepare parallel requests
        const requests = [];
        
        // Check cancellation before expensive operations
        if (isCancelled) {
          // eslint-disable-next-line no-console
          console.log('⏹️ Preview cancelled by user');
          return;
        }
        
        // Send service to appropriate container
        // eslint-disable-next-line no-console
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        // eslint-disable-next-line no-console
        console.log('🚀 STARTING CONTAINER API CALL');
        // eslint-disable-next-line no-console
        console.log(`📦 Sending ${totalFileCount} files to ${containerName}`);
        // eslint-disable-next-line no-console
        console.log(`🔑 Service name (sessionId): ${serviceToPreview.name}`);
        // eslint-disable-next-line no-console
        console.log(`📂 Project: ${selectedProject?.name} (${selectedProject?.id})`);
        // eslint-disable-next-line no-console
        console.log(`🎯 Container port: ${containerPort}`);
        // eslint-disable-next-line no-console
        console.log(`🏷️  Detected language: ${detectedLanguage}`);
        // eslint-disable-next-line no-console
        console.log(`👤 User ID: ${userId}`);
        
        const containerApiUrl = getContainerApiUrl(containerPort, '/api/render');
        // eslint-disable-next-line no-console
        console.log(`🌐 Calling container API:`, containerApiUrl);
        // eslint-disable-next-line no-console
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        
        // Start the async render job
        let renderResponse;
        try {
          // Ensure all file contents are strings (convert objects to JSON strings)
          // This prevents "Received an instance of Object" errors when writing files
          const sanitizedFiles = {};
          for (const [filePath, content] of Object.entries(mergedFrontendFiles)) {
            if (typeof content === 'object' && content !== null) {
              // Convert objects to JSON strings (e.g., parsed package.json)
              sanitizedFiles[filePath] = JSON.stringify(content, null, 2);
            } else {
              sanitizedFiles[filePath] = content;
            }
          }
          
          // eslint-disable-next-line no-console
          console.log('📡 Initiating fetch request to container...');
          renderResponse = await getAuthorizedFetch()(containerApiUrl, {
            method: 'POST',
            headers: getBffHeaders({
              'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
              tenantId: selectedTenant?.tenantId || selectedTenant?.id,
              serviceName: serviceToPreview.name,
              sourceCode: sanitizedFiles,
              projectId: selectedProject?.id,
              revisionId: selectedRevision?.id,
              userId: userId, // Stable userId from localStorage ensures consistent sessions
            }),
          });
          // eslint-disable-next-line no-console
          console.log('✅ Fetch request completed, status:', renderResponse.status);
        } catch (fetchError) {
          // eslint-disable-next-line no-console
          console.error('❌ FETCH REQUEST FAILED:', fetchError);
          // eslint-disable-next-line no-console
          console.error('   Error name:', fetchError.name);
          // eslint-disable-next-line no-console
          console.error('   Error message:', fetchError.message);
          // eslint-disable-next-line no-console
          console.error('   Error stack:', fetchError.stack);
          throw fetchError;
        }
        
        if (!renderResponse.ok) {
          const errorText = await renderResponse.text();
          // eslint-disable-next-line no-console
          console.error('❌ Container API returned error:', renderResponse.status, errorText);
          throw new Error(`Failed to start render: ${renderResponse.statusText}`);
        }
        
        const renderData = await renderResponse.json();
        // eslint-disable-next-line no-console
        console.log('📦 Render data received:', renderData);
        if (!renderData.success || !renderData.sessionId) {
          throw new Error(renderData.message || 'Failed to start render job');
        }
        
        // Store userId for this preview session (will be used in tab object)
        // eslint-disable-next-line no-console
        console.log(`🔑 Session User ID: ${userId} for job ${renderData.sessionId}`);
        
        // eslint-disable-next-line no-console
        console.log(`🎬 Render job started with ID: ${renderData.sessionId}`);
        // eslint-disable-next-line no-console
        console.log('⏳ [Preview] Phase: RENDERING - Container is building your application...');
        
        // Get base URL for polling
        const containerBaseUrl = containerApiUrl.replace('/api/render', '');
        
        // Poll for job completion with progress updates
        const frontendJobPromise = pollRenderStatus(
          containerBaseUrl,
          renderData.sessionId,
          (phase, progress, logs) => {
            // Clear build error modal when a new build starts (after a previous error)
            // This happens when user fixes code and saves - build restarts successfully
            if (buildErrorModal.isOpen && 
                buildErrorModal.serviceName === serviceToPreview.name &&
                (phase === 'initializing' || phase === 'copying' || phase === 'installing' || phase === 'starting')) {
              // eslint-disable-next-line no-console
              console.log(`✅ [Error Recovery] Clearing error modal for ${serviceToPreview.name} - new build phase: ${phase}`);
              setBuildErrorModal({ isOpen: false, error: null, serviceName: null });
            }
            
            // Update tab with progress and logs
            // Use functional setState to get current tabs and avoid stale closures
            setPreviewTabs(currentTabs => {
              if (currentTabs.length === 0) {
                return currentTabs;
              }
              
              const tabIndex = currentTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
              // eslint-disable-next-line no-console
              console.log(`📊 Progress update: ${progress}% - Phase: ${phase} - Tab found at index: ${tabIndex}`);
              // eslint-disable-next-line no-console
              console.log(`📋 Logs received:`, logs?.length || 0, 'entries');
              
              if (tabIndex >= 0) {
                const updatedTabs = [...currentTabs];
                // Replace logs entirely (API returns complete log history, not incremental)
                updatedTabs[tabIndex] = {
                  ...updatedTabs[tabIndex],
                  renderProgress: progress,
                  renderPhase: phase,
                  renderLogs: logs || [],
                };
                return updatedTabs;
              }
              
              return currentTabs;
            });
          }
        );
        
        requests.push(frontendJobPromise);

        // 2. If this is a frontend preview and backend service exists, fetch and send to appropriate backend container
        if (!isBackendPreview && backendService) {
          // eslint-disable-next-line no-console
          console.log('🔧 Found backend service:', backendService.name, '- loading in parallel');
          
          const backendRepoUrl = backendService.repoUrl || backendService.sshUrl || backendService.repositoryUrl;
          
          if (backendRepoUrl && shouldFetchFromGit) {
            const backendProjectId = extractProjectIdFromUrl(backendRepoUrl);
            
            if (backendProjectId) {
              // eslint-disable-next-line no-console
              console.log('📦 Fetching backend repository files from git');
              
              const backendSourceCode = await fetchAllRepositoryFiles(backendProjectId, backendRepoUrl);
              const mergedBackendFiles = await indexedDbFileStorage.getFilesForAIContext(
                selectedProject?.id,
                backendService.name,
                backendSourceCode
              );
              
              // Get routing info for backend service
              const backendRoutingInfo = getContainerRoutingInfo(backendService, mergedBackendFiles, {});
              const backendFrameworkType = detectBackendFramework(mergedBackendFiles);
              const backendContainerPort = backendRoutingInfo?.containerPort || FRAMEWORK_CONFIG[backendFrameworkType].port;
              
              // eslint-disable-next-line no-console
              console.log(`📦 Sending ${Object.keys(mergedBackendFiles).length} backend files to ${backendRoutingInfo?.containerName || 'node-container-live'} (port ${backendContainerPort})`);
              
              const backendContainerApiUrl = getContainerApiUrl(backendContainerPort, '/api/render');
              
              // Use the same userId from this preview session for backend service isolation
              // This ensures frontend and backend services are grouped in the same session
              
              requests.push(
                getAuthorizedFetch()(backendContainerApiUrl, {
                  method: 'POST',
                  headers: getBffHeaders({
                    'Content-Type': 'application/json',
                  }),
                  body: JSON.stringify({
                    tenantId: selectedTenant?.tenantId || selectedTenant?.id,
                    serviceName: backendService.name,
                    sourceCode: mergedBackendFiles,
                    projectId: selectedProject?.id,
                    revisionId: selectedRevision?.id,
                    userId: userId,
                  }),
                })
              );
            }
          } else if (backendRepoUrl) {
            // Use IndexedDB files for backend
            const mergedBackendFiles = await indexedDbFileStorage.getAllFilesForPreview(
              selectedProject?.id,
              backendService.name,
              {}
            );
            
            if (Object.keys(mergedBackendFiles).length > 0) {
              // Get routing info for backend service
              const backendRoutingInfo = getContainerRoutingInfo(backendService, mergedBackendFiles, {});
              const backendFrameworkType = detectBackendFramework(mergedBackendFiles);
              const backendContainerPort = backendRoutingInfo?.containerPort || FRAMEWORK_CONFIG[backendFrameworkType].port;
              
              const backendContainerApiUrl = getContainerApiUrl(backendContainerPort, '/api/render');
              
              // Use the same userId from this preview session for backend service isolation
              // This ensures frontend and backend services are grouped in the same session
              
              requests.push(
                getAuthorizedFetch()(backendContainerApiUrl, {
                  method: 'POST',
                  headers: getBffHeaders({
                    'Content-Type': 'application/json',
                  }),
                  body: JSON.stringify({
                    tenantId: selectedTenant?.tenantId || selectedTenant?.id,
                    serviceName: backendService.name,
                    sourceCode: mergedBackendFiles,
                    projectId: selectedProject?.id,
                    revisionId: selectedRevision?.id,
                    userId: userId,
                  }),
                })
              );
            }
          }
        }

        // Execute all requests in parallel with error handling
        const results = await Promise.all(requests.map(req => 
          req.catch(err => ({
            error: err,
            status: 'failed',
            isNetworkError: err.name === 'TypeError' && err.message.includes('fetch')
          }))
        ));
        
        // Check if cancelled after async operation
        if (isCancelled) return;
        
        // Check frontend result (first request - now a job result)
        const frontendResult = results[0];
        
        if (frontendResult.error || frontendResult.status === 'failed') {
          // Handle network/connection errors gracefully
          if (frontendResult.isNetworkError) {
            const errorMessage = `Cannot connect to ${containerName} on port ${containerPort}. Please start the container:\n\ncd container-live/${containerName}\nnpm run server`;
            
            // eslint-disable-next-line no-console
            console.error('❌ Container not running:', errorMessage);
            
            // Update preview tabs if they exist
            if (previewTabs.length > 0) {
              const tabIndex = previewTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
              if (tabIndex >= 0) {
                setPreviewTabs(currentTabs => {
                  const updatedTabs = [...currentTabs];
                  updatedTabs[tabIndex] = {
                    ...updatedTabs[tabIndex],
                    loading: false,
                    error: `Container not running. Start it with: cd container-live/${containerName} && npm run server`
                  };
                  return updatedTabs;
                });
              }
            } else {
              setPreviewError(`Container not running. Start it with: cd container-live/${containerName} && npm run server`);
            }
            setPreviewLoading(false);
            return; // Exit gracefully without throwing
          }
          
          // Other errors
          const errorMsg = frontendResult.error?.message || frontendResult.error || 'Failed to load preview';
          throw new Error(errorMsg);
        }
        
        // Frontend job completed successfully
        if (frontendResult.previewUrl) {
          const returnedSessionId = frontendResult.sessionId || serviceToPreview.name;
          
          // Build expected sessionId with userId (projectId-serviceName-userId format)
          const expectedSessionId = selectedProject?.id 
            ? `${selectedProject.id}-${serviceToPreview.name}-${userId}` 
            : `${serviceToPreview.name}-${userId}`;
          
          // CRITICAL: Validate that returned sessionId matches expected format
          if (returnedSessionId !== expectedSessionId) {
            // eslint-disable-next-line no-console
            console.warn(`⚠️  SESSION ID FORMAT DIFFERENCE`);
            // eslint-disable-next-line no-console
            console.warn(`   Expected: ${expectedSessionId}`);
            // eslint-disable-next-line no-console
            console.warn(`   Got: ${returnedSessionId}`);
            // eslint-disable-next-line no-console
            console.warn(`   Using returned sessionId (HMR will still work)`);
          }
          
          // Use the sessionId returned by the backend
          const validSessionId = returnedSessionId;
          
          // Use the preview URL returned by the backend (already points to Swagger UI for backend services)
          const finalPreviewUrl = frontendResult.previewUrl;
          
          // eslint-disable-next-line no-console
          console.log('✅ Preview URL received:', finalPreviewUrl);
          // eslint-disable-next-line no-console
          console.log('🎉 [Preview] Phase: READY - Application is now available!');
          // eslint-disable-next-line no-console
          console.log('🔄 Updating tab state to loading=false for service:', serviceToPreview.name);
          if (isBackendPreview) {
            // eslint-disable-next-line no-console
            console.log('🎯 Backend service - Swagger UI at:', finalPreviewUrl);
          }
          
          // Update preview tabs if they exist
          // Use functional update to avoid stale closure issues
          setPreviewTabs(currentTabs => {
            if (currentTabs.length > 0) {
              const tabIndex = currentTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
              // eslint-disable-next-line no-console
              console.log('🔍 Looking for tab with serviceName:', serviceToPreview.name, 'Found at index:', tabIndex);
              // eslint-disable-next-line no-console
              console.log('📋 Current tabs:', currentTabs.map(t => ({name: t.serviceName, loading: t.loading, url: t.url})));
              // eslint-disable-next-line no-console
              console.log('📋 Total tabs:', currentTabs.length, 'activePreviewTab:', activePreviewTab);
              if (tabIndex >= 0) {
                const updatedTabs = [...currentTabs];
                updatedTabs[tabIndex] = {
                  ...updatedTabs[tabIndex],
                  url: finalPreviewUrl,
                  sessionId: validSessionId,
                  userId: userId, // Store userId for this session
                  tenantId: selectedTenant?.id || selectedTenant?.tenantId,
                  projectId: selectedProject?.id,
                  containerPort: containerPort,
                  language: detectedLanguage, // Update language for icon display
                  framework: frameworkDisplayName, // Update framework for status bar display
                  loading: false,
                  error: null,
                  // Clear render progress but keep logs visible until iframe loads successfully
                  renderProgress: undefined,
                  renderPhase: undefined
                  // Note: renderLogs intentionally NOT cleared here - will be cleared by handleIframeLoad on successful render
                };
                // eslint-disable-next-line no-console
                console.log('✅ Updated preview tab:', serviceToPreview.name, 'URL:', finalPreviewUrl, 'loading: false');
                
                return updatedTabs;
              } else {
                // eslint-disable-next-line no-console
                console.error('❌ Could not find tab for service:', serviceToPreview.name);
                return currentTabs;
              }
            } else {
              // eslint-disable-next-line no-console
              console.log('⚠️ No preview tabs exist yet, falling back to single preview mode');
              return currentTabs;
            }
          });
          
          // Save session to localStorage for instant reload on next visit
          saveSessionToStorage(selectedProject?.id, serviceToPreview.name, userId, {
            sessionId: validSessionId,
            previewUrl: finalPreviewUrl,
            containerPort: containerPort,
            language: detectedLanguage,
            framework: frameworkDisplayName,
            tenantId: selectedTenant?.id || selectedTenant?.tenantId,
            projectId: selectedProject?.id
          });
          
          // If there are NO tabs (old single-preview fallback), also set the fallback state
          if (previewTabs.length === 0) {
            // Fallback to old single preview mode
            setPreviewUrl(finalPreviewUrl);
            setPreviewSessionId(validSessionId);
            
            // Save session to localStorage
            saveSessionToStorage(selectedProject?.id, serviceToPreview.name, userId, {
              sessionId: validSessionId,
              previewUrl: finalPreviewUrl,
              containerPort: containerPort,
              language: detectedLanguage,
              framework: frameworkDisplayName,
              tenantId: selectedTenant?.id || selectedTenant?.tenantId,
              projectId: selectedProject?.id
            });
          }
          
          // eslint-disable-next-line no-console
          console.log('✅ Preview loaded:', finalPreviewUrl, 'sessionId:', validSessionId);
          
          // Mark this service as successfully loaded
          const serviceKey = `${selectedProject?.id}|${serviceToPreview.name}|${selectedRevision || 'latest'}|${forceGitFetch ? 'force' : 'normal'}`;
          lastLoadedServiceRef.current = serviceKey;
          
          // Reset error count on success
          errorCountRef.current[serviceToPreview.name] = 0;
          
          // Mark frontend files as sent
          if (shouldFetchFromGit) {
            await indexedDbFileStorage.markAllChangedAsSent(selectedProject?.id, serviceToPreview.name);
          }
          
          // Check backend result if it was sent
          if (results.length > 1 && backendService) {
            const backendResult = results[1];
            if (!backendResult.error && backendResult.status !== 'failed') {
              // eslint-disable-next-line no-console
              console.log('✅ Backend loaded:', backendResult.previewUrl || 'running');
              
              // Update backend tab if it exists
              if (backendResult.previewUrl && previewTabs.length > 0) {
                const backendTabIndex = previewTabs.findIndex(tab => tab.serviceName === backendService.name);
                if (backendTabIndex >= 0) {
                  // eslint-disable-next-line no-console
                  console.log('🔍 Updating backend tab:', backendService.name, 'with URL:', backendResult.previewUrl);
                  setPreviewTabs(currentTabs => {
                    const updatedTabs = [...currentTabs];
                    updatedTabs[backendTabIndex] = {
                      ...updatedTabs[backendTabIndex],
                      url: backendResult.previewUrl,
                      sessionId: backendResult.sessionId || backendService.name,
                      containerPort: 3014,
                      loading: false,
                      error: null
                    };
                    return updatedTabs;
                  });
                  // eslint-disable-next-line no-console
                  console.log('✅ Backend tab updated:', backendService.name, 'URL:', backendResult.previewUrl);
                }
              }
              
              if (shouldFetchFromGit) {
                await indexedDbFileStorage.markAllChangedAsSent(selectedProject?.id, backendService.name);
              }
            } else {
              // eslint-disable-next-line no-console
              console.warn('⚠️ Backend loading failed, but frontend will continue');
            }
          }
          
          // eslint-disable-next-line no-console
          console.log('✅ Preview loaded:', frontendResult.previewUrl, 'sessionId:', frontendResult.sessionId);
          
          // Note: Auto-send for home-created projects now happens after files are pulled (before preview loads)
          // This makes the flow faster by not waiting for preview to build
        } else if (containerPort === 3022) {
          // Helm chart container — no iframe preview URL, HelmPreviewPanel uses WebSocket directly
          const helmSessionId = frontendResult.sessionId;
          // eslint-disable-next-line no-console
          console.log(`⎈ Helm validation complete — sessionId: ${helmSessionId}, status: ${frontendResult.status}`);

          setPreviewTabs(currentTabs => {
            const tabIndex = currentTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
            if (tabIndex >= 0) {
              const updatedTabs = [...currentTabs];
              updatedTabs[tabIndex] = {
                ...updatedTabs[tabIndex],
                // No url needed — HelmPreviewPanel renders directly
                url: `helm://${serviceToPreview.name}`,
                sessionId: helmSessionId,
                userId: userId,
                containerPort: 3022,
                language: detectedLanguage,
                framework: frameworkDisplayName,
                loading: false,
                error: null,
                renderProgress: undefined,
                renderPhase: undefined,
                renderLogs: undefined,
              };
              return updatedTabs;
            }
            return currentTabs;
          });

          saveSessionToStorage(selectedProject?.id, serviceToPreview.name, userId, {
            sessionId: helmSessionId,
            previewUrl: `helm://${serviceToPreview.name}`,
            containerPort: 3022,
            language: detectedLanguage,
            framework: frameworkDisplayName,
            tenantId: selectedTenant?.id || selectedTenant?.tenantId,
            projectId: selectedProject?.id,
          });

          const serviceKey = `${selectedProject?.id}|${serviceToPreview.name}|${selectedRevision || 'latest'}|${forceGitFetch ? 'force' : 'normal'}`;
          lastLoadedServiceRef.current = serviceKey;
          errorCountRef.current[serviceToPreview.name] = 0;
        } else {
          // No preview URL received - clear loading state and show error
          // eslint-disable-next-line no-console
          console.error('❌ No previewUrl in render result:', frontendResult);
          
          // Ensure loading state is cleared
          setPreviewTabs(currentTabs => {
            const tabIndex = currentTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
            if (tabIndex >= 0) {
              const updatedTabs = [...currentTabs];
              updatedTabs[tabIndex] = {
                ...updatedTabs[tabIndex],
                loading: false,
                error: frontendResult.message || 'Failed to generate preview - no URL received'
              };
              return updatedTabs;
            }
            return currentTabs;
          });
          
          throw new Error(frontendResult.message || 'Failed to generate preview');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Error loading preview:', error);
        
        // Increment error count for circuit breaker
        errorCountRef.current[serviceToPreview.name] = (errorCountRef.current[serviceToPreview.name] || 0) + 1;
        
        // Build a human-readable error message with extra context for known error types
        let errorMessage = error.message;
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
          errorMessage = 'Cannot connect to preview services. Please ensure the preview service for the current technology is up and running!';
          // eslint-disable-next-line no-console
          console.warn('⚠️ Network error detected – preview service may not be running.');
        } else if (error.message.includes('BFF server')) {
          errorMessage = error.message + '\n\nStart BFF with: cd mia-flow-bff && npm start';
        }
        
        // Always show the Build Error Modal so the user knows what went wrong
        // instead of being left with a silent white screen.
        setBuildErrorModal({
          isOpen: true,
          error: errorMessage,
          serviceName: serviceToPreview.name
        });

        // Also update the tab so it doesn't remain in a loading / empty state
        if (previewTabs.length > 0) {
          const tabIndex = previewTabs.findIndex(tab => tab.serviceName === serviceToPreview.name);
          if (tabIndex >= 0) {
            const updatedTabs = [...previewTabs];
            updatedTabs[tabIndex] = {
              ...updatedTabs[tabIndex],
              loading: false,
              error: errorMessage
            };
            setPreviewTabs(updatedTabs);
          }
        } else {
          // Fallback to old single preview mode
          setPreviewError(errorMessage);
        }
      } finally {
        setPreviewLoading(false);
        // Always remove from loading set when done (success or error)
        if (serviceToPreview?.name) {
          loadingPreviewRef.current.delete(serviceToPreview.name);
        }
      }
    };

    // Debounce the preview loading to prevent rapid consecutive calls
    if (previewLoadTimeoutRef.current) {
      clearTimeout(previewLoadTimeoutRef.current);
    }
    
    previewLoadTimeoutRef.current = setTimeout(() => {
      loadServicePreview();
    }, 1000); // 1000ms debounce
    
    // Cleanup function to prevent stale requests and clear debounce
    return () => {
      isCancelled = true;
      if (previewLoadTimeoutRef.current) {
        clearTimeout(previewLoadTimeoutRef.current);
      }
    };
    // Only depend on the core identifiers, not the full objects
    // This prevents cascading re-renders when multiple state updates occur
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResource?.data?.name, selectedProject?.id, selectedRevision?.id, forceGitFetch]);

  // REGRESSION FIX: Monitor for stuck tabs (loading but not actually loading)
  // This handles the case when creating a project from home where the tab is created
  // but selectedResource is not set, so the above useEffect doesn't trigger
  useEffect(() => {
    if (activeTab !== 'preview' || previewTabs.length === 0 || !selectedProject) {
      return;
    }

    // Check for tabs that are stuck: loading=true, no URL, no error, and not in loadingPreviewRef
    const stuckTab = previewTabs.find(tab => 
      tab.loading && 
      !tab.url && 
      !tab.error && 
      !loadingPreviewRef.current.has(tab.serviceName) &&
      tab.service // Has service data
    );

    if (stuckTab) {
      // Only set selectedResource if it's not already set to this service
      // This prevents unnecessary re-triggers
      if (selectedResource?.data?.name !== stuckTab.service.name) {
        // eslint-disable-next-line no-console
        console.log('🔧 Detected stuck tab, setting selectedResource to trigger load:', stuckTab.serviceName);
        
        // Set selectedResource to trigger the above useEffect
        setSelectedResource({
          type: 'service',
          data: stuckTab.service
        });
      }
    }
  }, [previewTabs, activeTab, selectedProject, selectedResource]);

  // Check if selected resource is a CRUD collection and load preview
  useEffect(() => {
    const loadCollectionPreview = async () => {
      // Only proceed if we have a selected collection resource and we're on the preview tab
      if (!selectedResource || selectedResource.type !== 'collection' || activeTab !== 'preview') {
        return;
      }

      const collection = selectedResource.data;
      const collectionName = selectedResource.name;
      
      // eslint-disable-next-line no-console
      console.log('📊 Loading CRUD preview for collection:', collectionName);

      try {
        // Import syncCollectionToCrud dynamically
        const {syncCollectionToCrud} = await import('../utils/crudServiceSync');
        
        // Sync collection to CRUD service
        const syncSuccess = await syncCollectionToCrud(
          selectedProject?.id,
          collectionName,
          collection
        );

        if (!syncSuccess) {
          // eslint-disable-next-line no-console
          console.warn('⚠️ Failed to sync collection to CRUD service');
        }

        // CRUD service preview URL - Use Swagger UI to browse all collections
        const crudPreviewUrl = `http://localhost:3030/swagger`;
        
        // Check if we already have a tab for CRUD service (shared for all collections)
        const existingTabIndex = previewTabs.findIndex(tab => tab.type === 'crud');
        
        if (existingTabIndex >= 0) {
          // Tab already exists, just switch to it
          setActivePreviewTab(existingTabIndex);
          // eslint-disable-next-line no-console
          console.log('✅ Switching to existing CRUD preview tab');
        } else {
          // Create new tab for CRUD service (shared for all collections)
          const newTab = {
            serviceName: 'CRUD Collections',
            url: crudPreviewUrl,
            sessionId: 'crud-service',
            containerPort: 3030,
            language: 'crud',
            loading: false,
            error: null,
            iframeKey: Date.now(),
            service: null,
            type: 'crud'
          };
          
          setPreviewTabs(currentTabs => [...currentTabs, newTab]);
          setActivePreviewTab(previewTabs.length); // Set to new tab index
          
          // eslint-disable-next-line no-console
          console.log('✅ CRUD preview tab created with Swagger UI:', crudPreviewUrl);
        }
        
        setPreviewUrl(crudPreviewUrl);
        setPreviewSessionId('crud-service');
        setPreviewContainerPort(3030);
        
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Error loading CRUD preview:', error);
        setPreviewError(error.message);
      }
    };

    loadCollectionPreview();
  }, [selectedResource, selectedProject, previewTabs, activeTab]);

  // Listen for file changes and send incremental updates to preview
  const fileChangeListenerRef = useRef(null);
  const previewUpdateDebounceTimer = useRef(null);
  const pendingPreviewUpdates = useRef(new Set());
  
  useEffect(() => {
    const handleFileChange = async (event) => {
      const {projectId: changedProjectId, serviceName: changedServiceName, filePath} = event.detail || {};
      
      // eslint-disable-next-line no-console
      console.log('📝 File changed in Monaco:', {changedProjectId, changedServiceName, filePath});
      
      // 1. Update tree view - reload all files to reflect changes (debounced)
      if (changedProjectId === selectedProject?.id && changedServiceName === selectedResource?.data?.name) {
        // eslint-disable-next-line no-console
        console.log('🌲 Refreshing tree view with updated files');
        await loadSourceCode();
        
        // Update hasModifications after reloading files
        // Use getChangedFilesForPreview to get files that need to be pushed
        const changedFiles = await indexedDbFileStorage.getChangedFilesForPreview(
          selectedProject.id,
          selectedResource.data.name
        );
        const hasChanges = changedFiles.length > 0;
        // eslint-disable-next-line no-console
        console.log('🔍 Checking modifications:', {
          changedFilesCount: changedFiles.length,
          changedFiles: changedFiles.map(f => ({path: f.filePath, state: f.state})),
          hasChanges
        });
        setHasModifications(hasChanges);
        // eslint-disable-next-line no-console
        console.log('✅ [DEBUG] Set hasModifications to:', hasChanges);
        
        // Note: Do NOT reload the file if it's currently open in editor - 
        // the user is actively editing it and reloading would be disruptive
      }
      
      // 2. Batch preview updates - add to pending queue and debounce
      // This prevents sending duplicate updates when AI applies multiple diffs rapidly
      pendingPreviewUpdates.current.add(JSON.stringify({projectId: changedProjectId, serviceName: changedServiceName, filePath}));
      
      // Clear existing timer and start new one
      if (previewUpdateDebounceTimer.current) {
        clearTimeout(previewUpdateDebounceTimer.current);
      }
      
      previewUpdateDebounceTimer.current = setTimeout(async () => {
        const updates = Array.from(pendingPreviewUpdates.current).map(s => JSON.parse(s));
        pendingPreviewUpdates.current.clear();
        
        if (updates.length === 0) return;
        
        // Process the first update (all updates should be for the same service)
        const firstUpdate = updates[0];
        const {projectId: updateProjectId, serviceName: updateServiceName} = firstUpdate;
        
        // eslint-disable-next-line no-console
        console.log(`📦 Processing batched preview update for ${updates.length} file change(s)`);
        
        // 3. Update preview with incremental changes
        // Check if preview is active (either single preview or tabs)
        const activePreviewTab = previewTabs.find(tab => tab.serviceName === updateServiceName);
        const hasActivePreview = !!previewSessionId || !!activePreviewTab;
      
        // eslint-disable-next-line no-console
        console.log('🔍 Checking preview update conditions:', {
          hasPreviewSessionId: !!previewSessionId,
          hasActivePreviewTab: !!activePreviewTab,
          hasActivePreview,
          hasSelectedResource: !!selectedResource,
          resourceType: selectedResource?.type,
          previewSessionId,
          activePreviewTab: activePreviewTab?.serviceName
        });
        
        if (!hasActivePreview || !selectedResource || selectedResource.type !== 'service') {
          // eslint-disable-next-line no-console
          console.log('⏭️ Skipping preview update - conditions not met');
          return;
        }
        
        // Check if file change is for currently previewed service or its backend
        const isServiceChange = updateProjectId === selectedProject?.id && 
                                (selectedResource.data?.name === updateServiceName || 
                                 activeBackendService?.name === updateServiceName);
        
        if (!isServiceChange) {
          // eslint-disable-next-line no-console
          console.log('⏭️ Skipping preview update - changed file not for active service');
          return;
        }
        
        try {
          const {default: indexedDbFileStorage} = await import('../utils/indexedDbFileStorage');
          const changedFiles = await indexedDbFileStorage.getChangedFilesForPreview(selectedProject?.id, updateServiceName);
        
          if (changedFiles.length === 0) {
            return;
          }
          
          // Get routing info using centralized utility
          const allFiles = await indexedDbFileStorage.getAllFilesForPreview(selectedProject?.id, updateServiceName, {});
          const currentService = updateServiceName === selectedResource.data?.name ? selectedResource.data : activeBackendService;
          
          const routingInfo = getContainerRoutingInfo(currentService, allFiles, { activePreviewTab, previewSessionId });
          
          if (!routingInfo) {
            console.error('❌ Failed to get routing info for service:', updateServiceName);
            return;
          }
          
          const { isBackend, isFrontend, framework, containerPort, containerName, sessionId } = routingInfo;
          
          // Skip update if no session exists yet (preview container not started)
          if (!sessionId) {
            // eslint-disable-next-line no-console
            console.log('⏭️ Skipping incremental update - preview session not yet created (will use full render)');
            return;
          }
          
          // eslint-disable-next-line no-console
          console.log(`🎯 Detected ${isBackend ? 'BACKEND' : 'FRONTEND'} framework for update: ${framework} (${containerName}, port ${containerPort})`);
          
          // eslint-disable-next-line no-console
          console.log('🔍 Service detection for update:', {
            serviceName: updateServiceName,
            isBackendChange: isBackend,
            isFrontendChange: isFrontend,
            serviceType: isBackend ? 'backend' : 'frontend',
            containerPort,
            containerName,
            sessionId
          });
          
          // eslint-disable-next-line no-console
          console.log(`🔄 ${isBackend ? 'Backend' : 'Frontend'} file changed, sending batched incremental update (${updates.length} files)`);
          
          // eslint-disable-next-line no-console
          console.log(`📤 Sending ${changedFiles.length} changed file(s) to ${containerName} (session: ${sessionId})`);
          addActivityLog(`📤 Sending ${changedFiles.length} file(s) to ${containerName}`);
          
          const filesToUpdate = {};
          changedFiles.forEach(file => {
            filesToUpdate[file.filePath] = file.content;
          });
          
          // Send incremental update
          const updateApiUrl = getContainerApiUrl(containerPort, `/api/preview/${sessionId}/update`);
          const updateResponse = await getAuthorizedFetch()(updateApiUrl, {
            method: 'POST',
            headers: getBffHeaders({
              'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
              files: filesToUpdate
            }),
          });

          if (updateResponse.ok) {
            const updateResult = await updateResponse.json();
            if (updateResult.success) {
              // Mark files as sent to preview (separate from GitLab push)
              await indexedDbFileStorage.markAllChangedAsPreviewSent(selectedProject?.id, updateServiceName);
              // eslint-disable-next-line no-console
              console.log(`✅ ${isBackend ? 'Backend' : 'Frontend'} preview updated with ${changedFiles.length} file(s)`);
              addActivityLog(`✅ Preview updated with ${changedFiles.length} file(s)`);
              
              // Refresh iframe for both frontend and backend changes
              // Backend needs longer delay for Maven rebuild + service restart
              const refreshDelay = isBackend ? 3000 : 2000;
              setTimeout(() => {
                refreshPreview();
                // eslint-disable-next-line no-console
                console.log('🔄 Preview iframe refreshed');
                addActivityLog('🔄 Preview refreshed');
              }, refreshDelay);
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('❌ Failed to send incremental update:', error);
        }
      }, 500); // Debounce for 500ms to batch rapid file changes
    };

    // Only register listener once to prevent duplicates
    if (!fileChangeListenerRef.current) {
      fileChangeListenerRef.current = handleFileChange;
      window.addEventListener('monacoFileChanged', handleFileChange);
    }
    
    return () => {
      if (fileChangeListenerRef.current) {
        window.removeEventListener('monacoFileChanged', fileChangeListenerRef.current);
        fileChangeListenerRef.current = null;
      }
      // Clear debounce timer on cleanup
      if (previewUpdateDebounceTimer.current) {
        clearTimeout(previewUpdateDebounceTimer.current);
      }
    };
  }, [previewSessionId, previewTabs, selectedResource, selectedProject, activeBackendService, refreshPreview, loadSourceCode]);

  // Listen for diffApplied events from chat to send incremental updates to preview
  useEffect(() => {
    const handleDiffApplied = async (event) => {
      const {filePath, mergedContent, success, conversationId} = event.detail || {};
      
      if (!success || !filePath || !mergedContent) {
        return;
      }
      
      // Only handle if preview is active
      if (!previewSessionId || !selectedResource || selectedResource.type !== 'service') {
        return;
      }
      
      // eslint-disable-next-line no-console
      console.log('🔄 Diff applied from chat, sending to preview:', filePath);
      
      try {
        const {default: indexedDbFileStorage} = await import('../utils/indexedDbFileStorage');
        const changedFiles = await indexedDbFileStorage.getChangedFilesForPreview(selectedProject?.id, selectedResource.data?.name);
        
        if (changedFiles.length === 0) {
          return;
        }
        
        // Get routing info using centralized utility
        const allFiles = await indexedDbFileStorage.getAllFilesForPreview(selectedProject?.id, selectedResource.data?.name, {});
        const routingInfo = getContainerRoutingInfo(selectedResource.data, allFiles, { previewSessionId });
        
        if (!routingInfo) {
          console.error('❌ Failed to get routing info for diff update');
          return;
        }
        
        const { containerPort, containerName, framework } = routingInfo;
        
        // eslint-disable-next-line no-console
        console.log(`🎯 Detected framework for diff update: ${framework} (${containerName}, port ${containerPort})`);
        
        // eslint-disable-next-line no-console
        console.log(`📤 Sending ${changedFiles.length} changed file(s) to ${containerName} after chat update`);
        
        const filesToUpdate = {};
        changedFiles.forEach(file => {
          filesToUpdate[file.filePath] = file.content;
        });
        
        // Send incremental update
        const updateApiUrl = getContainerApiUrl(containerPort, `/api/preview/${previewSessionId}/update`);
        const updateResponse = await getAuthorizedFetch()(updateApiUrl, {
          method: 'POST',
          headers: getBffHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({
            files: filesToUpdate
          }),
        });

        if (updateResponse.ok) {
          const updateResult = await updateResponse.json();
          if (updateResult.success) {
            // Mark files as sent to preview
            await indexedDbFileStorage.markAllChangedAsPreviewSent(selectedProject?.id, selectedResource.data?.name);
            // eslint-disable-next-line no-console
            console.log(`✅ Preview updated with ${changedFiles.length} file(s) from chat`);
            
            // Refresh iframe after a short delay to allow HMR to process
            setTimeout(() => {
              refreshPreview();
              // eslint-disable-next-line no-console
              console.log('🔄 Preview iframe refreshed after chat update');
            }, 2000);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Failed to send chat updates to preview:', error);
      }
    };

    window.addEventListener('diffApplied', handleDiffApplied);
    
    return () => {
      window.removeEventListener('diffApplied', handleDiffApplied);
    };
  }, [previewSessionId, selectedResource, selectedProject, refreshPreview]);

  // Listen for MCP service creation events and auto-trigger Pull
  useEffect(() => {
    const handleServiceCreated = async (event) => {
      const {projectId, serviceName, toolName} = event.detail || {};
      
      // Only trigger if this is for the currently selected project
      if (selectedProject && projectId === selectedProject.id) {
        // eslint-disable-next-line no-console
        console.log(`🆕 Service created via ${toolName}: ${serviceName}. Auto-triggering Pull and selecting service...`);
        
        // Small delay to allow backend to fully create the service
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Refresh configuration to get the newly created service
        try {
          const updatedConfig = await miaPlatformDataService.getMicroservices(
            selectedProject.id,
            selectedRevision.id
          );
          
          if (updatedConfig?.services?.[serviceName]) {
            setConfiguration(updatedConfig);
            
            // Automatically select the newly created service
            const newService = updatedConfig.services[serviceName];
            const newResource = {
              type: 'service',
              name: serviceName,
              data: newService
            };
            
            setSelectedResource(newResource);
            setSelectedFile(null);
            
            // eslint-disable-next-line no-console
            console.log(`✅ Automatically selected newly created service: ${serviceName}`);
            
            // Notify parent about resource selection
            if (onProjectContextChange) {
              onProjectContextChange({
                tenant: selectedTenant,
                project: selectedProject,
                revision: selectedRevision,
                selectedResource: newResource,
                selectedFile: null
              });
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('❌ Failed to refresh configuration after service creation:', error);
        }
        
        // Dispatch event to trigger Pull operation
        window.dispatchEvent(new CustomEvent('autoTriggerGitPull', {
          detail: {
            projectId,
            serviceName,
            reason: 'Service created via MCP'
          }
        }));
      }
    };
    
    window.addEventListener('mcpServiceCreated', handleServiceCreated);
    
    return () => {
      window.removeEventListener('mcpServiceCreated', handleServiceCreated);
    };
  }, [selectedProject, selectedRevision, selectedTenant, onProjectContextChange]);

  // Listen for service pipeline status updates from GitControls
  useEffect(() => {
    const handleServicePipelineUpdate = (event) => {
      const { status } = event.detail || {};
      setServicePipelineStatus(status);
    };

    window.addEventListener('servicePipelineStatusUpdate', handleServicePipelineUpdate);
    
    return () => {
      window.removeEventListener('servicePipelineStatusUpdate', handleServicePipelineUpdate);
    };
  }, []);

  // Listen for auto-sync updates and refresh preview iframe
  useEffect(() => {
    const handleSyncStarted = (event) => {
      const {projectId, serviceName} = event.detail || {};
      
      if (previewSessionId && selectedResource && 
          projectId === selectedProject?.id && 
          serviceName === selectedResource.name) {
        // eslint-disable-next-line no-console
        console.log('📤 Sync started for preview');
        setCompilationStatus('syncing');
      }
    };

    const handlePreviewUpdated = (event) => {
      const {projectId, serviceName, compilationReady} = event.detail || {};
      
      // Check if the updated service matches ANY preview tab (for multi-tab support)
      const matchingTab = previewTabs.find(tab => 
        tab.serviceName === serviceName && projectId === selectedProject?.id
      );
      
      // Also check if it matches the single preview mode (legacy support)
      const matchesSinglePreview = previewSessionId && selectedResource && 
          projectId === selectedProject?.id && 
          serviceName === selectedResource.name;
      
      // Refresh if it matches any tab or the single preview
      if (matchingTab || matchesSinglePreview) {
        // eslint-disable-next-line no-console
        console.log('🔄 Auto-sync completed for', serviceName, ', compilation ready:', compilationReady);
        
        if (compilationReady) {
          setCompilationStatus('ready');
          // Small delay to ensure visual feedback before refresh
          setTimeout(() => {
            refreshPreview();
            setCompilationStatus(null);
          }, 1000);
        } else {
          setCompilationStatus('compiling');
        }
      }
    };

    window.addEventListener('previewSyncStarted', handleSyncStarted);
    window.addEventListener('previewFilesUpdated', handlePreviewUpdated);
    
    return () => {
      window.removeEventListener('previewSyncStarted', handleSyncStarted);
      window.removeEventListener('previewFilesUpdated', handlePreviewUpdated);
    };
  }, [previewSessionId, selectedResource, selectedProject, previewTabs, refreshPreview]);

  // Listen for git ref (branch/tag) changes and refresh preview
  useEffect(() => {
    const handleGitRefChanged = (event) => {
      const {projectId, serviceName, ref, refType} = event.detail || {};
      
      // Update selectedResourceGitBranch if this change is for the currently selected resource
      if (selectedResource && selectedResource.type === 'service' &&
          projectId === selectedProject?.id &&
          serviceName === (selectedResource.data?.name || selectedResource.name)) {
        setSelectedResourceGitBranch(ref);
        // eslint-disable-next-line no-console
        console.log(`🌿 Updated git branch for selected resource ${serviceName}:`, ref);
      }

      // Check if the change affects the current preview
      const matchingTab = previewTabs.find(tab => 
        tab.serviceName === serviceName && projectId === selectedProject?.id
      );
      
      const matchesSinglePreview = previewSessionId && selectedResource && 
          projectId === selectedProject?.id && 
          serviceName === selectedResource.name;
      
      if (matchingTab || matchesSinglePreview) {
        // eslint-disable-next-line no-console
        console.log(`🔄 Git ${refType} changed to ${ref} for ${serviceName}, refreshing preview...`);
        
        // Wait a bit for files to be pulled and saved to IndexedDB
        setTimeout(() => {
          refreshPreview();
        }, 1000);
      }
    };

    window.addEventListener('gitRefChanged', handleGitRefChanged);
    
    return () => {
      window.removeEventListener('gitRefChanged', handleGitRefChanged);
    };
  }, [previewSessionId, selectedResource, selectedProject, previewTabs, refreshPreview]);

  // Listen for runtime errors posted by the injected iframeErrorCapture script.
  // The script inside the iframe sends errors via:
  //   postMessage({ source: 'mia-flow-preview-client', type, message, stack })
  // Without this listener the only symptom visible to the user is a white screen.
  useEffect(() => {
    const handleIframeMessage = (event) => {
      // Only handle messages from our injected error-capture script
      if (!event.data || event.data.source !== 'mia-flow-preview-client') {
        return;
      }

      const { type, message, stack } = event.data;

      // Show the Build Error Modal only for real errors, not for console.warn / console.error noise
      if (type === 'error' || type === 'unhandledrejection') {
        // Derive service name from the currently active preview tab
        const activeTabData = previewTabs[activePreviewTab];
        const serviceName = activeTabData?.serviceName || 'Preview';

        // Build a readable error string that includes the stack trace when available
        const fullError = stack && stack !== message
          ? `${message}\n\nStack trace:\n${stack}`
          : message;

        // eslint-disable-next-line no-console
        console.warn(`🚨 [iframeErrorCapture] Runtime error in preview (${serviceName}):`, fullError);

        setBuildErrorModal({
          isOpen: true,
          error: fullError,
          serviceName,
        });
      }
    };

    window.addEventListener('message', handleIframeMessage);

    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [previewTabs, activePreviewTab]);

  // Handle resource selection (service, endpoint, collection, etc.)
  const handleResourceClick = (resource) => {
    // Check if we're clicking on the same resource
    const isSameResource = selectedResource?.type === resource?.type && 
      selectedResource?.name === resource?.name;
    
    // If clicking on the same resource, just keep the current view state
    if (isSameResource) {
      return;
    }
    
    // Check if we're switching between sources (services with repository URLs)
    const isCurrentSourceService = selectedResource?.type === 'service' && 
      (selectedResource?.data?.repositoryUrl || selectedResource?.data?.repoUrl || selectedResource?.data?.sshUrl);
    const isNewSourceService = resource?.type === 'service' && 
      (resource?.data?.repositoryUrl || resource?.data?.repoUrl || resource?.data?.sshUrl);
    
    // Check if selecting a collection
    const isCollection = resource?.type === 'collection';
    const isCurrentCollection = selectedResource?.type === 'collection';
    
    // Keep repository view if:
    // 1. Switching between sources and currently in source code view
    // 2. Switching between collections and currently in source code view
    const shouldKeepRepositoryView = showRepositoryView && (
      (isCurrentSourceService && isNewSourceService) ||
      (isCurrentCollection && isCollection)
    );
    
    setSelectedResource(resource);
    setSelectedFile(null); // Clear file selection when resource is selected
    
    // Set selected collection if it's a collection resource
    if (isCollection) {
      setSelectedCollection({
        name: resource.name,
        data: resource.data
      });
      // Keep current view when switching between collections, otherwise default to details
      if (!isCurrentCollection) {
        setShowRepositoryView(false); // Show details view by default for new collection selection
      }
    } else {
      setSelectedCollection(null);
      if (!shouldKeepRepositoryView) {
        setShowRepositoryView(false); // Reset to details view when selecting a new resource (unless switching between sources)
      }
    }
    
    // eslint-disable-next-line no-console
    console.log('🔧 Selected resource:', resource);
    
    // Notify parent about resource selection
    if (onProjectContextChange) {
      onProjectContextChange({
        tenant: selectedTenant,
        project: selectedProject,
        revision: selectedRevision,
        selectedResource: resource,
        selectedFile: null // Clear file when resource changes
      });
    }
  };

  // Handle service creation (reload configuration after creating a service)
  const handleCreateService = async () => {
    if (!selectedProject || !selectedRevision) return;
    
    try {
      // eslint-disable-next-line no-console
      console.log('🔄 Reloading configuration after service creation...');
      await loadConfiguration(selectedProject.id, selectedRevision.id);
      // eslint-disable-next-line no-console
      console.log('✅ Configuration reloaded successfully');
      // Close the modal after successful creation
      setIsCreateServiceModalOpen(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to reload configuration:', error);
    }
  };

  // Handle collection save from editor
  const handleCollectionSave = (updatedCollectionData) => {
    if (!selectedCollection || !selectedProject) return;
    
    const updatedCollections = {
      ...collections,
      [selectedCollection.name]: updatedCollectionData
    };
    
    setCollections(updatedCollections);
    setSelectedCollection({
      ...selectedCollection,
      data: updatedCollectionData
    });
    
    // Update configuration
    if (configuration) {
      setConfiguration({
        ...configuration,
        collections: updatedCollections
      });
    }
    
    // Track collection save
    if (window.analytics?.trackAction) {
      window.analytics.trackAction('collection', 'save', {
        collectionName: selectedCollection.name,
        projectId: selectedProject._id,
        fieldsCount: updatedCollectionData.fields?.length || 0
      });
    }
    
    // eslint-disable-next-line no-console
    console.log('💾 Collection updated in Canvas:', selectedCollection.name);
  };

  // Handle refresh - reload configuration while keeping filters
  const handleRefresh = async () => {
    // Store current selections to restore after refresh
    const currentResourceName = selectedResource?.name;
    const currentResourceType = selectedResource?.type;
    
    // Refresh tenants list (keeping selection)
    if (tenants.length > 0) {
      await loadTenants();
    }
    
    // Refresh projects list if tenant is selected (keeping selection)
    if (selectedTenant?.id) {
      await loadProjects(selectedTenant.id, projectsSearchQuery, 1, false);
    }
    
    // Refresh revisions list if project is selected (keeping selection)
    if (selectedProject?.id) {
      await loadRevisions(selectedProject.id);
    }
    
    // Refresh configuration if both project and revision are selected
    if (selectedProject && selectedRevision) {
      // eslint-disable-next-line no-console
      console.log('🔄 Refreshing configuration for project:', selectedProject.id, 'revision:', selectedRevision.id);
      
      // Store the resource to re-select after configuration loads
      if (currentResourceName && currentResourceType) {
        setPendingResourceReselect({
          name: currentResourceName,
          type: currentResourceType
        });
      }
      
      await loadConfiguration(selectedProject.id, selectedRevision.id);
    }
  };
  
  // Re-select resource after configuration refresh
  useEffect(() => {
    if (pendingResourceReselect && configuration && collections) {
      const {name, type} = pendingResourceReselect;
      
      // eslint-disable-next-line no-console
      console.log('🔄 Re-selecting resource after refresh:', name, type);
      
      // Build refreshed resource with updated data from new configuration
      const refreshedResource = {
        name,
        type
      };
      
      // Find the updated resource data from the new configuration
      if (type === 'service' && configuration.services?.[name]) {
        refreshedResource.data = configuration.services[name];
      } else if (type === 'endpoint' && configuration.endpoints?.[name]) {
        refreshedResource.data = configuration.endpoints[name];
      } else if (type === 'collection') {
        const refreshedCollection = collections[name] || configuration.collections?.[name];
        if (refreshedCollection) {
          refreshedResource.data = refreshedCollection;
        }
      }
      
      // Re-select the resource to refresh the displayed content
      if (refreshedResource.data) {
        setSelectedResource(refreshedResource);
        
        // Also update collection if it was selected
        if (type === 'collection') {
          setSelectedCollection({
            name,
            data: refreshedResource.data
          });
        }
        
        // eslint-disable-next-line no-console
        console.log('✅ Resource re-selected with fresh data:', name);
      }
      
      // Clear the pending re-select
      setPendingResourceReselect(null);
    }
  }, [configuration, collections, pendingResourceReselect]);
  
  // Expose handleRefresh to parent via ref
  useImperativeHandle(ref, () => ({
    handleRefresh
  }));

  // Detect content type based on content structure
  const contentType = useMemo(() => {
    if (!content?.content) return 'document';
    
    // Check if content type is explicitly specified
    if (content.type === 'image') return 'image';
    if (content.type === 'video') return 'video';
    
    const text = content.content;
    
    // Check if content is a data URL (base64 image or video)
    if (typeof text === 'string' && text.startsWith('data:image/')) {
      return 'image';
    }
    if (typeof text === 'string' && text.startsWith('data:video/')) {
      return 'video';
    }
    
    // Check for code patterns
    const codePatterns = [
      /```[\s\S]+```/g, // Code blocks
      /function\s+\w+\s*\(/g, // Function declarations
      /import\s+.*from/g, // ES6 imports
      /export\s+(default\s+)?/g, // ES6 exports
      /<\/?\w+[^>]*>/g, // HTML tags
      /\{\s*[\w\s:,]+\}/g, // JSON-like objects
    ];
    
    const hasCode = codePatterns.some(pattern => pattern.test(text));
    
    // Check for document patterns
    const docPatterns = [
      /^#\s+/m, // Markdown headers
      /^\*\s+/m, // Bullet points
      /^\d+\.\s+/m, // Numbered lists
    ];
    
    const hasDocStructure = docPatterns.some(pattern => pattern.test(text));
    
    if (hasCode) return 'code';
    if (hasDocStructure) return 'document';
    
    // Default to document for general content
    return 'document';
  }, [content?.type, content?.content]);

  // Extract code from markdown if present
  const extractedCode = useMemo(() => {
    if (!content?.content || contentType !== 'code') return '';
    
    const text = content.content;
    const codeBlockRegex = /```(?:\w+)?\n?([\s\S]*?)```/g;
    const matches = [...text.matchAll(codeBlockRegex)];
    
    if (matches.length > 0) {
      return matches.map(match => match[1]).join('\n\n');
    }
    
    return text;
  }, [content?.content, contentType]);

  // Detect programming language for syntax highlighting
  const detectedLanguage = useMemo(() => {
    if (!content?.content) return 'text';
    
    const text = content.content.toLowerCase();
    
    // Check for HTML first (most specific)
    if (text.includes('<!doctype html>') || 
        text.includes('<html') || 
        (text.includes('<head>') && text.includes('<body>'))) {
      return 'html';
    }
    
    if (text.includes('function') && text.includes('const')) return 'javascript';
    if (text.includes('def ') && text.includes('import')) return 'python';
    if (text.includes('{') && text.includes('"')) return 'json';
    if (text.includes('select') || text.includes('from')) return 'sql';
    if (text.includes('body {') || text.includes('.class')) return 'css';
    
    return 'text';
  }, [content?.content]);

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleTabChange = (tab) => {
    // Track tab change
    if (window.analytics?.trackAction) {
      window.analytics.trackAction('canvas', 'tab_changed', {
        from: activeTab,
        to: tab
      });
    }
    
    // Track feature usage
    if (window.analytics?.trackFeature) {
      window.analytics.trackFeature(`canvas_${tab}_view`, {
        from: activeTab
      });
    }
    
    setActiveTab(tab);
    
    // Update URL with tab change
    if (onSelectionChange) {
      const lineNumber = location.hash ? parseInt(location.hash.replace('#L', '')) : null;
      onSelectionChange(
        selectedResource?.data?.name || params.microservice,
        selectedRevision?.name || params.branch || 'main',
        tab,
        tab === 'code' ? selectedFile?.path : null,
        tab === 'code' && lineNumber ? lineNumber : null
      );
    }
  };

  // Deployment functions
  const handleDeployClick = (environment) => {
    setSelectedEnvironment(environment);
    setShowDeploymentModal(true);
    setDeploymentError(null);
    setPipelineStatus(null);
    setDeploymentPipelineId(null);
  };

  const handleDeploymentConfirm = async () => {
    // Detailed validation with specific error messages
    if (!selectedProject) {
      setDeploymentError('No project selected. Please select a project first.');
      return;
    }
    
    if (!selectedProject.id) {
      setDeploymentError('Invalid project data. Missing project ID.');
      return;
    }

    if (!selectedRevision) {
      setDeploymentError('No revision selected. Please select a revision first.');
      return;
    }

    if (!selectedEnvironment) {
      setDeploymentError('No environment selected. Please select an environment first.');
      return;
    }

    try {
      setDeploymentLoading(true);
      setDeploymentError(null);

      // eslint-disable-next-line no-console
      console.log('🚀 Starting deployment:', {
        projectId: selectedProject.id,
        projectName: selectedProject.name,
        environment: selectedEnvironment.envId,
        revision: selectedRevision.id,
      });

      const result = await miaPlatformDataService.triggerDeployment(selectedProject.id, {
        deployType: 'deploy_all',
        environment: selectedEnvironment.envId,
        refType: 'revision',
        revision: selectedRevision.id,
      });

      // eslint-disable-next-line no-console
      console.log('✅ Deployment triggered:', result);
      
      // Track deployment
      if (window.analytics?.trackDeployment) {
        window.analytics.trackDeployment('deploy_all', {
          projectId: selectedProject.id,
          projectName: selectedProject.name,
          environment: selectedEnvironment.envId,
          revision: selectedRevision.id,
          pipelineId: result.pipelineId || result.id
        });
      }

      // Handle different field names for pipeline ID
      const pipelineId = result.pipelineId || result.id;
      
      if (!pipelineId) {
        throw new Error('Invalid deployment response: missing pipeline ID');
      }

      setDeploymentPipelineId(pipelineId);
      
      // Start polling for pipeline status
      startPipelinePolling(pipelineId);

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Deployment failed:', error);
      setDeploymentError(error.message || 'Failed to trigger deployment');
    } finally {
      setDeploymentLoading(false);
    }
  };

  const startPipelinePolling = (pipelineId) => {
    if (!selectedProject || !selectedEnvironment) return;
    
    if (!pipelineId) {
      // eslint-disable-next-line no-console
      console.error('❌ Cannot start polling: pipeline ID is missing');
      return;
    }

    setPipelinePolling(true);

    // Clear any existing interval
    if (pipelinePollingInterval.current) {
      clearInterval(pipelinePollingInterval.current);
    }

    // Poll every 5 seconds
    const pollInterval = setInterval(async () => {
      try {
        const status = await miaPlatformDataService.getPipelineStatus(
          selectedProject.id,
          pipelineId,
          selectedEnvironment.envId
        );

        setPipelineStatus(status);

        // Stop polling if pipeline is finished (success, failed, or canceled)
        if (status.status && ['success', 'failed', 'canceled', 'skipped'].includes(status.status.toLowerCase())) {
          clearInterval(pipelinePollingInterval.current);
          setPipelinePolling(false);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Failed to fetch pipeline status:', error);
        // Continue polling even on error
      }
    }, 5000);

    pipelinePollingInterval.current = pollInterval;
  };

  const handleDeploymentClose = () => {
    setShowDeploymentModal(false);
    setSelectedEnvironment(null);
    setDeploymentError(null);
    setPipelineStatus(null);
    setDeploymentPipelineId(null);

    // Stop polling
    if (pipelinePollingInterval.current) {
      clearInterval(pipelinePollingInterval.current);
      setPipelinePolling(false);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pipelinePollingInterval.current) {
        clearInterval(pipelinePollingInterval.current);
      }
    };
  }, []);

  const getPipelineStatusIcon = (status) => {
    if (!status) return '⏳';
    const statusLower = status.toLowerCase();
    const icons = {
      success: '✅',
      failed: '❌',
      running: '⏳',
      pending: '⏸️',
      canceled: '🚫',
      skipped: '⏭️'
    };
    return icons[statusLower] || '❓';
  };

  const getPipelineStatusClass = (status) => {
    if (!status) return 'status-unknown';
    return `status-${status.toLowerCase()}`;
  };

  // Pods status functions
  const handleViewPodsClick = async (environment) => {
    if (!selectedProject) return;

    setPodsEnvironment(environment);
    setShowPodsModal(true);
    setPodsLoading(true);
    setPodsError(null);
    setPodsData([]);

    try {
      // eslint-disable-next-line no-console
      console.log('📦 Fetching pods for environment:', environment.envId);

      const pods = await miaPlatformDataService.getPodsStatus(
        selectedProject._id,
        environment.envId
      );

      setPodsData(pods || []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to fetch pods:', error);
      setPodsError(error.message || 'Failed to fetch pods status');
    } finally {
      setPodsLoading(false);
    }
  };

  const handlePodsModalClose = () => {
    setShowPodsModal(false);
    setPodsEnvironment(null);
    setPodsData([]);
    setPodsError(null);
  };

  const getPodStatusIcon = (status) => {
    if (!status) return '❓';
    const statusLower = status.toLowerCase();
    if (statusLower === 'running') return '✅';
    if (statusLower === 'pending') return '⏳';
    if (statusLower === 'succeeded') return '✔️';
    if (statusLower === 'failed') return '❌';
    if (statusLower === 'unknown') return '❓';
    if (statusLower === 'crashloopbackoff') return '🔄';
    if (statusLower === 'error') return '⚠️';
    return '❓';
  };

  const getPodStatusClass = (status) => {
    if (!status) return 'pod-status-unknown';
    const statusLower = status.toLowerCase();
    if (statusLower === 'running') return 'pod-status-running';
    if (statusLower === 'pending') return 'pod-status-pending';
    if (statusLower === 'succeeded') return 'pod-status-succeeded';
    if (statusLower === 'failed') return 'pod-status-failed';
    if (statusLower === 'crashloopbackoff') return 'pod-status-error';
    if (statusLower === 'error') return 'pod-status-error';
    return 'pod-status-unknown';
  };

  // Get file icon based on file extension
  const getFileIcon = (filePath) => {
    const iconName = getFileIconName(filePath);
    return <Icon name={iconName} size={16} />;
  };

  // Render resource details based on type
  const renderResourceDetails = () => {
    if (!selectedResource) return null;

    const {type, name, data} = selectedResource;

    // Common fields to display for all resources
    const renderDetailItem = (label, value, isCode = false) => {
      if (value === undefined || value === null) return null;
      return (
        <div className="detail-item">
          <span className="detail-label">{label}</span>
          <span className={`detail-value ${isCode ? 'detail-value-code' : ''}`}>
            {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
          </span>
        </div>
      );
    };

    // Render array/object as JSON
    const renderComplexValue = (value) => {
      if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
        return <pre className="detail-json">{JSON.stringify(value, null, 2)}</pre>;
      }
      return value;
    };

    switch (type) {
      case 'service':
        if (!data) return null;
        
        return (
          <div className="microservice-detail-content">
            <div className="detail-section">
              <h3>{'Overview'}</h3>
              <div className="detail-grid">
                {renderDetailItem('Name', data.name || name)}
                {renderDetailItem('Type', data.type || 'custom')}
                {renderDetailItem('Docker Image', data.dockerImage, true)}
                {repositoryUrl && (
                  <div className="detail-item detail-item-full-width">
                    <span className="detail-label">{'Repository'}</span>
                    <div className="repo-actions">
                      <ExternalLinkButton
                        className="repo-link-primary"
                        href={repositoryUrl}
                        variant="secondary"
                      >
                        <span className="repo-icon"><Icon name="link" size={16} /></span>
                        <span className="repo-link-text">{'View Repository'}</span>
                      </ExternalLinkButton>
                      <div className="repo-dropdown-wrapper" ref={repoDropdownRef}>
                        <button
                          className="repo-action-button"
                          onClick={() => setShowRepoDropdown(!showRepoDropdown)}
                          type="button"
                        >
                          {'Code ▾'}
                        </button>
                        {showRepoDropdown && (
                          <div className="repo-dropdown-menu">
                            <div className="repo-dropdown-section">
                              <div className="repo-dropdown-section-title">{'Clone'}</div>
                              <div className="repo-dropdown-clone-item">
                                <input
                                  className="repo-clone-input"
                                  onClick={(e) => e.target.select()}
                                  readOnly
                                  title="SSH"
                                  type="text"
                                  value={repositoryUrl.replace('https://', 'git@').replace('.git', '.git').replace(/\//, ':')}
                                />
                                <button
                                  className="repo-copy-button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      repositoryUrl.replace('https://', 'git@').replace('.git', '.git').replace(/\//, ':')
                                    );
                                  }}
                                  title="Copy SSH URL"
                                  type="button"
                                >
                                  <Icon name="document" size={14} />
                                </button>
                              </div>
                              <div className="repo-dropdown-clone-item">
                                <input
                                  className="repo-clone-input"
                                  onClick={(e) => e.target.select()}
                                  readOnly
                                  title="HTTPS"
                                  type="text"
                                  value={repositoryUrl}
                                />
                                <button
                                  className="repo-copy-button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(repositoryUrl);
                                  }}
                                  title="Copy HTTPS URL"
                                  type="button"
                                >
                                  <Icon name="document" size={14} />
                                </button>
                              </div>
                            </div>
                            
                            {(() => {
                              const sshRepoUrl = repositoryUrl.replace('https://', 'git@').replace(/\.git$/, '.git').replace(/\//, ':');
                              const repoFolder = repositoryUrl.split('/').pop()?.replace(/\.git$/, '') || 'repo';
                              return (
                                <div className="repo-dropdown-section">
                                  <div className="repo-dropdown-section-title">{'Open in Claude Code'}</div>
                                  <div className="repo-dropdown-clone-item">
                                    <input
                                      className="repo-clone-input"
                                      onClick={(e) => e.target.select()}
                                      readOnly
                                      title="SSH: Run this in your terminal to clone via SSH and open in Claude Code"
                                      type="text"
                                      value={`git clone ${sshRepoUrl} && cd ${repoFolder} && claude`}
                                    />
                                    <button
                                      className="repo-copy-button"
                                      onClick={() => navigator.clipboard.writeText(`git clone ${sshRepoUrl} && cd ${repoFolder} && claude`)}
                                      title="Copy SSH command to clipboard"
                                      type="button"
                                    >
                                      <Icon name="document" size={14} />
                                    </button>
                                  </div>
                                  <div className="repo-dropdown-clone-item">
                                    <input
                                      className="repo-clone-input"
                                      onClick={(e) => e.target.select()}
                                      readOnly
                                      title="HTTPS: Run this in your terminal to clone via HTTPS and open in Claude Code"
                                      type="text"
                                      value={`git clone ${repositoryUrl} && cd ${repoFolder} && claude`}
                                    />
                                    <button
                                      className="repo-copy-button"
                                      onClick={() => navigator.clipboard.writeText(`git clone ${repositoryUrl} && cd ${repoFolder} && claude`)}
                                      title="Copy HTTPS command to clipboard"
                                      type="button"
                                    >
                                      <Icon name="document" size={14} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })()}

                            {(() => {
                              const sshRepoUrl = repositoryUrl.replace('https://', 'git@').replace(/\.git$/, '.git').replace(/\//, ':');
                              const repoFolder = repositoryUrl.split('/').pop()?.replace(/\.git$/, '') || 'repo';
                              return (
                                <div className="repo-dropdown-section">
                                  <div className="repo-dropdown-section-title">{'Open in GitHub Copilot'}</div>
                                  <div className="repo-dropdown-clone-item">
                                    <input
                                      className="repo-clone-input"
                                      onClick={(e) => e.target.select()}
                                      readOnly
                                      title="SSH: Run this in your terminal to clone via SSH and open in GitHub Copilot"
                                      type="text"
                                      value={`git clone ${sshRepoUrl} && cd ${repoFolder} && code .`}
                                    />
                                    <button
                                      className="repo-copy-button"
                                      onClick={() => navigator.clipboard.writeText(`git clone ${sshRepoUrl} && cd ${repoFolder} && code .`)}
                                      title="Copy SSH command to clipboard"
                                      type="button"
                                    >
                                      <Icon name="document" size={14} />
                                    </button>
                                  </div>
                                  <div className="repo-dropdown-clone-item">
                                    <input
                                      className="repo-clone-input"
                                      onClick={(e) => e.target.select()}
                                      readOnly
                                      title="HTTPS: Run this in your terminal to clone via HTTPS and open in GitHub Copilot"
                                      type="text"
                                      value={`git clone ${repositoryUrl} && cd ${repoFolder} && code .`}
                                    />
                                    <button
                                      className="repo-copy-button"
                                      onClick={() => navigator.clipboard.writeText(`git clone ${repositoryUrl} && cd ${repoFolder} && code .`)}
                                      title="Copy HTTPS command to clipboard"
                                      type="button"
                                    >
                                      <Icon name="document" size={14} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })()}

                            <div className="repo-dropdown-section">
                              <div className="repo-dropdown-section-title">{'Open in your IDE'}</div>
                              <div className="repo-dropdown-links">
                                <a
                                  className="repo-dropdown-link"
                                  href={`vscode://vscode.git/clone?url=${encodeURIComponent(repositoryUrl.replace('https://', 'git@').replace(/\.git$/, '.git').replace(/\//, ':'))}`}
                                  rel="noopener noreferrer"
                                >
                                  {'Visual Studio Code (SSH)'}
                                </a>
                                <a
                                  className="repo-dropdown-link"
                                  href={`vscode://vscode.git/clone?url=${encodeURIComponent(repositoryUrl)}`}
                                  rel="noopener noreferrer"
                                >
                                  {'Visual Studio Code (HTTPS)'}
                                </a>
                                <a
                                  className="repo-dropdown-link"
                                  href={`jetbrains://idea/checkout/git?idea.required.plugins.id=Git4Idea&checkout.repo=${encodeURIComponent(repositoryUrl.replace('https://', 'git@').replace(/\.git$/, '.git').replace(/\//, ':'))}`}
                                  rel="noopener noreferrer"
                                >
                                  {'IntelliJ IDEA (SSH)'}
                                </a>
                                <a
                                  className="repo-dropdown-link"
                                  href={`jetbrains://idea/checkout/git?idea.required.plugins.id=Git4Idea&checkout.repo=${encodeURIComponent(repositoryUrl)}`}
                                  rel="noopener noreferrer"
                                >
                                  {'IntelliJ IDEA (HTTPS)'}
                                </a>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {renderDetailItem('Replicas', data.replicas || 1)}
                {data.serviceAccountName && renderDetailItem('Service Account', data.serviceAccountName)}
                {data.logParser && renderDetailItem('Log Parser', data.logParser)}
                {data.terminationGracePeriodSeconds !== undefined && renderDetailItem('Termination Grace Period', `${data.terminationGracePeriodSeconds}s`)}
                {data.advanced !== undefined && renderDetailItem('Advanced Mode', data.advanced)}
                {data.createdAt && renderDetailItem('Created At', new Date(data.createdAt).toLocaleString())}
              </div>
            </div>

            {data.resources && (
              <div className="detail-section">
                <h3>{'Resources'}</h3>
                <div className="detail-grid">
                  {data.resources.cpuLimits && (
                    <>
                      {renderDetailItem('CPU Min', data.resources.cpuLimits.min)}
                      {renderDetailItem('CPU Max', data.resources.cpuLimits.max)}
                    </>
                  )}
                  {data.resources.memoryLimits && (
                    <>
                      {renderDetailItem('Memory Min', data.resources.memoryLimits.min)}
                      {renderDetailItem('Memory Max', data.resources.memoryLimits.max)}
                    </>
                  )}
                </div>
              </div>
            )}

            {data.containerPorts && data.containerPorts.length > 0 && (
              <div className="detail-section">
                <h3>{'Container Ports'}</h3>
                <div className="detail-list">
                  {data.containerPorts.map((port, index) => (
                    <div className="detail-list-item" key={index}>
                      <span className="detail-list-label">{port.name || `Port ${index + 1}`}</span>
                      <span className="detail-list-value">{`${port.from || 'N/A'} → ${port.to || 'N/A'}`}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.environment && data.environment.length > 0 && (
              <div className="detail-section">
                <h3>{'Environment Variables'}</h3>
                <div className="detail-list">
                  {data.environment.slice(0, 10).map((env, index) => (
                    <div className="detail-list-item" key={index}>
                      <span className="detail-list-label">{env.name}</span>
                      <span className="detail-list-value">
                        {env.valueType === 'secret' ? '🔒 Secret' : env.value || 'N/A'}
                      </span>
                    </div>
                  ))}
                  {data.environment.length > 10 && (
                    <div className="detail-list-item">
                      <span className="detail-list-value">{`... and ${data.environment.length - 10} more`}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {data.tags && data.tags.length > 0 && (
              <div className="detail-section">
                <h3>{'Tags'}</h3>
                <div className="tags-list">
                  {data.tags.map((tag, index) => (
                    <span className="tag-badge" key={index}>{tag}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
        
      case 'endpoint':
        return (
          <div className="microservice-detail-content">
            <div className="detail-section">
              <h3>{'Overview'}</h3>
              <div className="detail-grid">
                {renderDetailItem('Path', name)}
                {renderDetailItem('Type', data.type)}
                {renderDetailItem('Base Path', data.basePath || data.pathName)}
                {renderDetailItem('Public', typeof data.public === 'boolean' ? data.public : 'inherited')}
                {renderDetailItem('Show In Documentation', typeof data.showInDocumentation === 'boolean' ? data.showInDocumentation : 'inherited')}
                {data.service && renderDetailItem('Service', data.service)}
                {data.collectionId && renderDetailItem('Collection', data.collectionId)}
              </div>
            </div>
            
            {data.routes && typeof data.routes === 'object' && Object.keys(data.routes).length > 0 && (
              <div className="detail-section">
                <h3>{`Routes (${Object.keys(data.routes).length})`}</h3>
                <div className="detail-list">
                  {Object.entries(data.routes).slice(0, 10).map(([routeId, route]) => (
                    <div className="detail-list-item" key={routeId}>
                      <span className="detail-list-label">{`${route.verb || 'GET'} ${route.path || '/'}`}</span>
                      <span className="detail-list-value">
                        {route.public?.inherited !== undefined ? '(inherited)' : 
                         route.public?.value !== undefined ? (route.public.value ? 'Public' : 'Private') :
                         route.public === true ? 'Public' : 'Private'}
                      </span>
                    </div>
                  ))}
                  {Object.keys(data.routes).length > 10 && (
                    <div className="detail-list-item">
                      <span className="detail-list-value">{`... and ${Object.keys(data.routes).length - 10} more routes`}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {data.pathRewrite && (
              <div className="detail-section">
                <h3>{'Rewrite Configuration'}</h3>
                <div className="detail-grid">
                  {renderDetailItem('Path Rewrite', data.pathRewrite, true)}
                </div>
              </div>
            )}
          </div>
        );

      case 'collection':
        return (
          <div className="microservice-detail-content">
            <div className="detail-section">
              <h3>{'Overview'}</h3>
              <div className="detail-grid">
                {renderDetailItem('Name', name)}
                {renderDetailItem('Type', data.type || 'CRUD')}
                {renderDetailItem('Default State', data.defaultState)}
                {renderDetailItem('Enable Lookup', data.enableLookup)}
              </div>
            </div>
            
            {data.fields && data.fields.length > 0 && (
              <div className="detail-section">
                <h3>{'Fields'}</h3>
                <div className="detail-list">
                  {data.fields.map((field, idx) => (
                    <div className="detail-list-item" key={idx}>
                      <span className="detail-list-label">{field.name}</span>
                      <span className="detail-list-value">
                        {`${field.type}${field.required ? ' (required)' : ''}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.indexes && data.indexes.length > 0 && (
              <div className="detail-section">
                <h3>{'Indexes'}</h3>
                <div className="detail-list">
                  {data.indexes.map((index, idx) => (
                    <div className="detail-list-item" key={idx}>
                      <span className="detail-list-label">{index.name || `Index ${idx + 1}`}</span>
                      <span className="detail-list-value detail-value-code">
                        {renderComplexValue(index.fields || index)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'fastData':
        return (
          <div className="microservice-detail-content">
            <div className="detail-section">
              <h3>{'Configuration'}</h3>
              <div className="detail-grid">
                {renderDetailItem('Version', data.version)}
                {renderDetailItem('Last Commit ID', data.lastCommitId, true)}
                {data.updatedAt && renderDetailItem('Updated At', new Date(data.updatedAt).toLocaleString())}
              </div>
            </div>

            {data.systems && Object.keys(data.systems).length > 0 && (
              <div className="detail-section">
                <h3>{'Systems'}</h3>
                <div className="detail-list">
                  {Object.entries(data.systems).map(([systemName, system]) => (
                    <div className="detail-list-item" key={systemName}>
                      <span className="detail-list-label">{systemName}</span>
                      <span className="detail-list-value">{system.type || 'N/A'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.projections && Object.keys(data.projections).length > 0 && (
              <div className="detail-section">
                <h3>{`Projections (${Object.keys(data.projections).length})`}</h3>
                <div className="detail-list">
                  {Object.entries(data.projections).slice(0, 10).map(([projName]) => (
                    <div className="detail-list-item" key={projName}>
                      <span className="detail-list-label">{projName}</span>
                    </div>
                  ))}
                  {Object.keys(data.projections).length > 10 && (
                    <div className="detail-list-item">
                      <span className="detail-list-value">{`... and ${Object.keys(data.projections).length - 10} more`}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {data.singleViews && Object.keys(data.singleViews).length > 0 && (
              <div className="detail-section">
                <h3>{`Single Views (${Object.keys(data.singleViews).length})`}</h3>
                <div className="detail-list">
                  {Object.entries(data.singleViews).slice(0, 10).map(([svName]) => (
                    <div className="detail-list-item" key={svName}>
                      <span className="detail-list-label">{svName}</span>
                    </div>
                  ))}
                  {Object.keys(data.singleViews).length > 10 && (
                    <div className="detail-list-item">
                      <span className="detail-list-value">{`... and ${Object.keys(data.singleViews).length - 10} more`}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case 'fastDataProjection':
        return (
          <div className="microservice-detail-content">
            <div className="detail-section">
              <h3>{'Overview'}</h3>
              <div className="detail-grid">
                {renderDetailItem('Projection ID', data.projectionId || name)}
                {selectedResource.system && renderDetailItem('System', selectedResource.system)}
                {data.topics?.ingestionTopicName && renderDetailItem('Ingestion Topic', data.topics.ingestionTopicName, true)}
                {data.topics?.prUpdateTopicName && renderDetailItem('Update Topic', data.topics.prUpdateTopicName, true)}
              </div>
            </div>

            {data.fields && data.fields.length > 0 && (
              <div className="detail-section">
                <h3>{`Fields (${data.fields.length})`}</h3>
                <div className="detail-list">
                  {data.fields.map((field, idx) => (
                    <div className="detail-list-item" key={idx}>
                      <span className="detail-list-label">
                        {field.name}
                        {field.primaryKey && ' 🔑'}
                      </span>
                      <span className="detail-list-value">
                        {`${field.type}${field.required ? ' (required)' : ''}${field.nullable ? ' (nullable)' : ''}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.indexes && data.indexes.length > 0 && (
              <div className="detail-section">
                <h3>{`Indexes (${data.indexes.length})`}</h3>
                <div className="detail-list">
                  {data.indexes.map((index, idx) => (
                    <div className="detail-list-item" key={idx}>
                      <span className="detail-list-label">
                        {index.name || `Index ${idx + 1}`}
                        {index.unique && ' 🔒'}
                      </span>
                      <span className="detail-list-value">
                        {index.type || 'normal'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.strategies && Object.keys(data.strategies).length > 0 && (
              <div className="detail-section">
                <h3>{'Strategies'}</h3>
                <div className="detail-list">
                  {Object.entries(data.strategies).map(([strategyName, strategy]) => (
                    <div className="detail-list-item" key={strategyName}>
                      <span className="detail-list-label">{strategyName}</span>
                      <span className="detail-list-value">{strategy.type || 'N/A'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="detail-section">
              <h3>{'Raw Configuration'}</h3>
              {renderComplexValue(data)}
            </div>
          </div>
        );

      case 'fastDataSingleView':
        return (
          <div className="microservice-detail-content">
            <div className="detail-section">
              <h3>{'Overview'}</h3>
              <div className="detail-grid">
                {renderDetailItem('Single View ID', data.singleViewId || name)}
                {selectedResource.system && renderDetailItem('System', selectedResource.system)}
                {data.topic && renderDetailItem('Topic', data.topic, true)}
              </div>
            </div>

            {data.fields && data.fields.length > 0 && (
              <div className="detail-section">
                <h3>{`Fields (${data.fields.length})`}</h3>
                <div className="detail-list">
                  {data.fields.map((field, idx) => (
                    <div className="detail-list-item" key={idx}>
                      <span className="detail-list-label">
                        {field.name}
                        {field.primaryKey && ' 🔑'}
                      </span>
                      <span className="detail-list-value">
                        {`${field.type}${field.required ? ' (required)' : ''}${field.nullable ? ' (nullable)' : ''}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.aggregation && (
              <div className="detail-section">
                <h3>{'Aggregation'}</h3>
                {renderComplexValue(data.aggregation)}
              </div>
            )}

            <div className="detail-section">
              <h3>{'Raw Configuration'}</h3>
              {renderComplexValue(data)}
            </div>
          </div>
        );

      case 'flowManager':
        return (
          <div className="microservice-detail-content">
            {/* Overview Section */}
            <div className="detail-section">
              <h3>{'Overview'}</h3>
              <div className="detail-grid">
                {renderDetailItem('Flow Name', name)}
                {data.persistencyManagement && renderDetailItem('Persistence Type', data.persistencyManagement.type)}
                {data.persistencyManagement?.host && renderDetailItem('Persistence Host', data.persistencyManagement.host)}
                {data.persistencyManagement?.collectionName && renderDetailItem('Collection Name', data.persistencyManagement.collectionName)}
                {data.states && renderDetailItem('Total States', Object.keys(data.states).length)}
                {data.events && renderDetailItem('Total Events', Object.keys(data.events).length)}
                {data.stateGroups && renderDetailItem('State Groups', Object.keys(data.stateGroups).length)}
                {data.eventGroups && renderDetailItem('Event Groups', Object.keys(data.eventGroups).length)}
                {data.commands && renderDetailItem('Commands', Object.keys(data.commands).length)}
                {data.protocols && renderDetailItem('Protocols', Object.keys(data.protocols).length)}
                {data.hooks && renderDetailItem('Hooks', Object.keys(data.hooks).length)}
                {data.sideEffects && renderDetailItem('Side Effects', Object.keys(data.sideEffects).length)}
              </div>

              {/* Initial Event */}
              {data.creationNode?.initialEvent && (
                <div className="detail-item" style={{marginTop: '16px'}}>
                  <span className="detail-label">{'Initial Event'}</span>
                  <span className="detail-value detail-value-code">
                    {`${data.creationNode.initialEvent.name} → ${data.states?.[data.creationNode.initialEvent.targetState]?.name || data.creationNode.initialEvent.targetState}`}
                  </span>
                </div>
              )}

              {/* State Groups */}
              {data.stateGroups && Object.keys(data.stateGroups).length > 0 && (
                <div style={{marginTop: '16px'}}>
                  <span className="detail-label">{'State Groups'}</span>
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px', marginTop: '8px'}}>
                    {Object.entries(data.stateGroups).map(([groupId, group]) => (
                      <span className="resource-badge" key={groupId} style={{backgroundColor: group.color || '#888'}}>
                        {group.name || `Group ${groupId}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Show all states */}
              {data.states && Object.keys(data.states).length > 0 && (
                <div style={{marginTop: '16px'}}>
                  <span className="detail-label">{'States'}</span>
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px', marginTop: '8px'}}>
                    {Object.entries(data.states).map(([stateId, state]) => (
                      <span className="resource-badge badge-flow" key={stateId}>
                        {state.name || stateId}
                        {state.isFinal && ' (final)'}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Show all events */}
              {data.events && Object.keys(data.events).length > 0 && (
                <div style={{marginTop: '16px'}}>
                  <span className="detail-label">{'Events'}</span>
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px', marginTop: '8px'}}>
                    {Object.entries(data.events).map(([eventId, event]) => (
                      <span className="resource-badge badge-endpoint" key={eventId}>
                        {event.name || eventId}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Flow Visualization */}
            <div className="detail-section">
              <FlowVisualization flowData={data} flowName={name} />
            </div>

            {/* Raw Configuration */}
            <div className="detail-section">
              <h3>{'Raw Configuration'}</h3>
              {renderComplexValue(data)}
            </div>
          </div>
        );

      case 'infrastructure':
        return (
          <div className="microservice-detail-content">
            <div className="detail-section">
              <h3>{'Overview'}</h3>
              <div className="detail-grid">
                {renderDetailItem('Name', data.name || name)}
                {renderDetailItem('Type', data.type)}
                {data.meta?.kind && renderDetailItem('Kind', data.meta.kind)}
                {data.meta?.apiVersion && renderDetailItem('API Version', data.meta.apiVersion)}
              </div>
            </div>

            {/* Metadata Section */}
            {data.meta && (
              <div className="detail-section">
                <h3>{'Metadata'}</h3>
                <div className="detail-grid">
                  {Object.entries(data.meta).map(([key, value]) => (
                    renderDetailItem(key.charAt(0).toUpperCase() + key.slice(1), value, typeof value === 'string')
                  ))}
                </div>
              </div>
            )}

            {/* Spec Section */}
            {data.spec && (
              <div className="detail-section">
                <h3>{'Specification'}</h3>
                <div className="detail-list">
                  {Object.entries(data.spec).map(([key, value]) => (
                    <div className="detail-list-item" key={key}>
                      <span className="detail-list-label">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                      <span className="detail-list-value">
                        {typeof value === 'object' ? renderComplexValue(value) : String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Source Marketplace Item */}
            {data.sourceMarketplaceItem && (
              <div className="detail-section">
                <h3>{'Source Marketplace Item'}</h3>
                <div className="detail-grid">
                  {renderDetailItem('Item ID', data.sourceMarketplaceItem.itemId)}
                  {renderDetailItem('Version', data.sourceMarketplaceItem.version)}
                  {renderDetailItem('Tenant ID', data.sourceMarketplaceItem.tenantId, true)}
                </div>
              </div>
            )}

            {/* Generated From */}
            {data.generatedFrom && (
              <div className="detail-section">
                <h3>{'Generated From'}</h3>
                <div className="detail-grid">
                  {renderDetailItem('ID', data.generatedFrom._id, true)}
                </div>
              </div>
            )}

            <div className="detail-section">
              <h3>{'Raw Configuration'}</h3>
              {renderComplexValue(data)}
            </div>
          </div>
        );

      case 'customInfrastructure':
        return (
          <div className="microservice-detail-content">
            <div className="detail-section">
              <h3>{'Overview'}</h3>
              <div className="detail-grid">
                {renderDetailItem('Name', name)}
                {renderDetailItem('Type', data.type)}
                {renderDetailItem('Kind', data.kind)}
                {renderDetailItem('API Version', data.apiVersion)}
              </div>
            </div>

            <div className="detail-section">
              <h3>{'Raw Configuration'}</h3>
              {renderComplexValue(data)}
            </div>
          </div>
        );

      default:
        return (
          <div className="microservice-detail-content">
            <div className="detail-section">
              <h3>{'Details'}</h3>
              {renderComplexValue(data)}
            </div>
          </div>
        );
    }
  };

  if (!content) {
    return null;
  }

  return (
    <div 
      className={`canvas-container ${isVisible ? 'visible' : ''} ${isFullscreen ? 'fullscreen' : ''}`}
      style={{
        left: isVisible && !isFullscreen ? `${chatSidebarWidth}px` : 'auto',
        width: isVisible && !isFullscreen ? `calc(100% - ${chatSidebarWidth}px)` : '100%'
      }}
    >
        {/* Unified Top Bar */}
        <UnifiedTopBar
          activeTab={activeTab}
          configuration={{...configuration, environments: fullProjectConfig?.environments}}
          selectedProject={selectedProject}
          hasModifications={hasModifications}
          isFullscreen={isFullscreen}
          isPushing={false}
          pipelineStatus={servicePipelineStatus}
          splitViewMode={splitViewMode}
          onSplitViewModeChange={(mode) => {
            setSplitViewMode(mode);
            if (mode === 'preview') {
              setActiveTab('preview');
            }
          }}
          previewTabs={previewTabs}
          onDetails={() => {
            // Navigate to service tab to show service details
            handleTabChange('service');
          }}
          onPipeline={async () => {
            // Open pipeline list modal in Canvas
            setShowServicePipelineList(true);
            setLoadingServicePipelines(true);
            
            // Try to dispatch event for GitControls too (if it's rendered)
            window.dispatchEvent(new CustomEvent('viewPipelineRequested', {
              detail: {
                projectId: selectedProject?.id,
                serviceName: selectedResource?.data?.name
              }
            }));
            
            // Also load pipelines directly in Canvas
            try {
              const repositoryUrl = selectedResource?.data?.repositoryUrl ||
                                   selectedResource?.data?.repoUrl || 
                                   selectedResource?.data?.sshUrl;
              
              if (repositoryUrl) {
                const unifiedGitDataService = (await import('../services/UnifiedGitDataService')).default;
                const url = new URL(repositoryUrl.replace('.git', ''));
                const pathParts = url.pathname.split('/').filter(Boolean);
                const gitlabProjectId = pathParts.join('/');
                
                // Get current branch using utility
                const currentBranch = getResourceGitBranch(selectedResource, selectedProject?.id, 'main');

                const pipelineList = await unifiedGitDataService.getPipelines(gitlabProjectId, repositoryUrl, currentBranch);
                setServicePipelines(pipelineList || []);
              }
            } catch (error) {
              console.error('Failed to load pipelines:', error);
              setServicePipelines([]);
            } finally {
              setLoadingServicePipelines(false);
            }
          }}
          onPreview={() => handleTabChange('preview')}
          onPull={() => {
            // Delegate to CodeViewWithLogger
            if (codeViewRef.current) {
              addActivityLog('🔄 Pull requested...');
              codeViewRef.current.pullFromGit();
            } else {
              addActivityLog('⚠️ No active code view');
            }
          }}
          onPush={async () => {
            // Open PushModal instead of direct push
            const serviceName = selectedResource?.data?.name || selectedResource?.name;
            const gitBranch = getResourceGitBranch(selectedResource, selectedProject?.id);

            // eslint-disable-next-line no-console
            console.log('🔘 Push button clicked:', {
              selectedResourceName: serviceName,
              gitBranch,
              selectedResourceGitBranch
            });
            
            if (selectedProject && (selectedResource?.type === 'service' || selectedResource?.type === 'configuration')) {
              // eslint-disable-next-line no-console
              console.log('✅ Opening push modal with branch:', gitBranch);
              setPushModalOpen(true);
            } else {
              // eslint-disable-next-line no-console
              console.warn('⚠️ Cannot push - requirements not met');
              addActivityLog('⚠️ No service or configuration selected for push');
            }
          }}
          onRefresh={async (forceCleanup = false) => {
            if (selectedResource?.type === 'service') {
              try {
                // eslint-disable-next-line no-console
                console.log(`🔄 Refresh clicked ${forceCleanup ? 'with force cleanup' : ''} - forcing full re-render via /api/render`);
                
                // Get all files from IndexedDB for the current service
                const allFiles = await indexedDbFileStorage.getAllFilesForPreview(
                  selectedProject?.id,
                  selectedResource.data?.name,
                  {}
                );
                
                if (Object.keys(allFiles).length === 0) {
                  // eslint-disable-next-line no-console
                  console.warn('⚠️ No files found in IndexedDB to refresh – triggering full auto-render from git');
                  const activeTab = previewTabs.find(tab => tab.serviceName === selectedResource.data?.name);
                  if (activeTab) {
                    await autoRenderOnPreviewNotFound(activeTab);
                  }
                  return;
                }
                
                // eslint-disable-next-line no-console
                console.log(`📤 Forcing full re-render with ${Object.keys(allFiles).length} files`);
                
                // Get routing info using centralized utility
                const activeTab = previewTabs.find(tab => tab.serviceName === selectedResource.data?.name);
                const routingInfo = getContainerRoutingInfo(
                  selectedResource.data,
                  allFiles,
                  { activePreviewTab: activeTab, previewSessionId: activeTab?.sessionId }
                );
                
                if (!routingInfo) {
                  console.error('❌ Failed to get routing info for force refresh');
                  return;
                }
                
                const { isBackend: isBackendService, containerPort, containerName, framework, sessionId } = routingInfo;
                
                // eslint-disable-next-line no-console
                console.log(`🎯 Force re-render detected ${isBackendService ? 'backend' : 'frontend'}: ${framework} (${containerName})`);
                
                if (!sessionId) {
                  // eslint-disable-next-line no-console
                  console.warn('⚠️ No active preview session found for this service, proceeding with a fresh render anyway');
                }
                
                // Extract service name and find tab index (needed for both cleanup and render)
                const serviceName = selectedResource.data?.name;
                const tabIndex = previewTabs.findIndex(tab => tab.serviceName === serviceName);
                
                // If force cleanup is requested, call the force-cleanup endpoint first
                // Only if we have a valid session — skip cleanup when starting fresh (no sessionId)
                if (forceCleanup && sessionId) {
                  try {
                    // eslint-disable-next-line no-console
                    console.log(`🧹 Calling force-cleanup for session ${sessionId}...`);
                    
                    // Show cleanup in progress in preview tab
                    if (tabIndex >= 0) {
                      setPreviewTabs(currentTabs => {
                        const updatedTabs = [...currentTabs];
                        updatedTabs[tabIndex] = {
                          ...updatedTabs[tabIndex],
                          loading: true,
                          error: null,
                          renderPhase: 'cleanup',
                          renderProgress: 0,
                          renderLogs: ['🧹 Starting force cleanup...']
                        };
                        return updatedTabs;
                      });
                    }
                    
                    const cleanupApiUrl = getContainerApiUrl(containerPort, `/api/preview/${sessionId}/force-cleanup`);
                    const cleanupResponse = await getAuthorizedFetch()(cleanupApiUrl, {
                      method: 'POST',
                      headers: getBffHeaders(),
                    });
                    
                    if (cleanupResponse.ok) {
                      const cleanupResult = await cleanupResponse.json();
                      // eslint-disable-next-line no-console
                      console.log('✅ Force cleanup completed:', cleanupResult.message);
                      
                      // Update tab with cleanup completion
                      if (tabIndex >= 0) {
                        setPreviewTabs(currentTabs => {
                          const updatedTabs = [...currentTabs];
                          updatedTabs[tabIndex] = {
                            ...updatedTabs[tabIndex],
                            renderPhase: 'cleanup',
                            renderProgress: 50,
                            renderLogs: [
                              '🧹 Starting force cleanup...',
                              `✅ Cleanup completed: ${cleanupResult.message || 'Environment reset to first-time state'}`,
                              `🧹 Cleaned: ${Object.keys(cleanupResult.cleaned || {}).filter(k => cleanupResult.cleaned[k]).join(', ')}`,
                              '🔄 Starting fresh render...'
                            ]
                          };
                          return updatedTabs;
                        });
                      }
                    } else {
                      // eslint-disable-next-line no-console
                      console.warn('⚠️ Force cleanup failed, continuing with render...');
                      
                      // Show warning but continue
                      if (tabIndex >= 0) {
                        setPreviewTabs(currentTabs => {
                          const updatedTabs = [...currentTabs];
                          updatedTabs[tabIndex] = {
                            ...updatedTabs[tabIndex],
                            renderLogs: [
                              '🧹 Starting force cleanup...',
                              '⚠️ Cleanup failed, attempting render anyway...'
                            ]
                          };
                          return updatedTabs;
                        });
                      }
                    }
                  } catch (cleanupError) {
                    // eslint-disable-next-line no-console
                    console.error('❌ Error during force cleanup:', cleanupError);
                    
                    // Show error but continue with render
                    if (tabIndex >= 0) {
                      setPreviewTabs(currentTabs => {
                        const updatedTabs = [...currentTabs];
                        updatedTabs[tabIndex] = {
                          ...updatedTabs[tabIndex],
                          renderLogs: [
                            '🧹 Starting force cleanup...',
                            `❌ Cleanup error: ${cleanupError.message}`,
                            '🔄 Attempting render anyway...'
                          ]
                        };
                        return updatedTabs;
                      });
                    }
                  }
                }
                
                // eslint-disable-next-line no-console
                console.log(`📡 Calling /api/render for ${containerName} (port ${containerPort}) with session: ${sessionId}`);
                
                // Extract userId for render request
                const userId = getOrCreateUserId(selectedProject?.id, serviceName);
                
                // Show loading state in preview tab
                if (tabIndex >= 0) {
                  setPreviewTabs(currentTabs => {
                    const updatedTabs = [...currentTabs];
                    const existingLogs = updatedTabs[tabIndex].renderLogs || [];
                    updatedTabs[tabIndex] = {
                      ...updatedTabs[tabIndex],
                      loading: true,
                      error: null,
                      renderPhase: 'starting',
                      renderProgress: 0,
                      // Preserve cleanup logs if they exist, otherwise start fresh
                      renderLogs: forceCleanup && existingLogs.length > 0 
                        ? existingLogs // Keep cleanup logs
                        : ['🔄 Starting full re-render...']
                    };
                    return updatedTabs;
                  });
                }
                
                // Force full re-render via /api/render
                const renderApiUrl = getContainerApiUrl(containerPort, '/api/render');
                const renderResponse = await getAuthorizedFetch()(renderApiUrl, {
                  method: 'POST',
                  headers: getBffHeaders({
                    'Content-Type': 'application/json',
                  }),
                  body: JSON.stringify({
                    tenantId: selectedTenant?.tenantId || selectedTenant?.id,
                    serviceName: serviceName,
                    sourceCode: allFiles,
                    projectId: selectedProject?.id,
                    revisionId: selectedRevision?.id,
                    userId: userId,
                  }),
                });

                if (renderResponse.ok) {
                  const renderResult = await renderResponse.json();
                  if (renderResult.success && renderResult.sessionId) {
                    // eslint-disable-next-line no-console
                    console.log(`🎬 Re-render job started with ID: ${renderResult.sessionId}`);
                    
                    // Poll for completion with progress updates
                    const containerBaseUrl = renderApiUrl.replace('/api/render', '');
                    await pollRenderStatus(
                      containerBaseUrl,
                      renderResult.sessionId,
                      (phase, progress, logs) => {
                        console.log(`📊 Re-render progress: ${progress}% - Phase: ${phase}`);
                        
                        // Update tab with progress - use functional setState to avoid stale closure
                        setPreviewTabs(currentTabs => {
                          const currentTabIndex = currentTabs.findIndex(t => t.serviceName === serviceName);
                          if (currentTabIndex >= 0) {
                            const updatedTabs = [...currentTabs];
                            const existingLogs = updatedTabs[currentTabIndex].renderLogs || [];
                            
                            // Preserve cleanup logs (first 4 logs if they start with cleanup emoji)
                            const cleanupLogs = existingLogs.slice(0, 4).filter(log => 
                              typeof log === 'string' && (log.startsWith('🧹') || log.startsWith('✅') || log.startsWith('⚠️') || log.startsWith('❌'))
                            );
                            const hasCleanupLogs = cleanupLogs.length > 0 && cleanupLogs[0].includes('cleanup');
                            
                            // Combine cleanup logs with render logs
                            const combinedLogs = hasCleanupLogs 
                              ? [...cleanupLogs, ...(logs || [])]
                              : (logs || []);
                            
                            updatedTabs[currentTabIndex] = {
                              ...updatedTabs[currentTabIndex],
                              loading: true,
                              renderPhase: phase,
                              renderProgress: progress,
                              renderLogs: combinedLogs
                            };
                            return updatedTabs;
                          }
                          return currentTabs;
                        });
                      }
                    );
                    
                    // eslint-disable-next-line no-console
                    console.log(`✅ Successfully re-rendered ${Object.keys(allFiles).length} files for ${containerName}`);
                    
                    // Clear loading state and render progress
                    if (tabIndex >= 0) {
                      setPreviewTabs(currentTabs => {
                        const updatedTabs = [...currentTabs];
                        updatedTabs[tabIndex] = {
                          ...updatedTabs[tabIndex],
                          loading: false,
                          // Clear render progress state to hide the loading bar
                          renderPhase: undefined,
                          renderProgress: undefined,
                          renderLogs: undefined,
                          iframeKey: Date.now()
                        };
                        return updatedTabs;
                      });
                    }
                    
                    // Force refresh the iframe after re-render completes
                    const updatedResource = {...selectedResource};
                    setSelectedResource(null);
                    setTimeout(() => setSelectedResource(updatedResource), 1000);
                  } else {
                    // eslint-disable-next-line no-console
                    console.error('❌ Failed to start re-render:', renderResult.message);
                    
                    // Show error in tab
                    if (tabIndex >= 0) {
                      setPreviewTabs(currentTabs => {
                        const updatedTabs = [...currentTabs];
                        updatedTabs[tabIndex] = {
                          ...updatedTabs[tabIndex],
                          loading: false,
                          error: renderResult.message || 'Failed to start re-render'
                        };
                        return updatedTabs;
                      });
                    }
                  }
                } else {
                  // eslint-disable-next-line no-console
                  console.error('❌ Failed to call /api/render:', renderResponse.status);
                  
                  // Show error in tab
                  if (tabIndex >= 0) {
                    setPreviewTabs(currentTabs => {
                      const updatedTabs = [...currentTabs];
                      updatedTabs[tabIndex] = {
                        ...updatedTabs[tabIndex],
                        loading: false,
                        error: `Failed to call /api/render: ${renderResponse.status}`
                      };
                      return updatedTabs;
                    });
                  }
                }
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error('❌ Error refreshing preview:', error);
                // Still try to refresh the iframe
                const updatedResource = {...selectedResource};
                setSelectedResource(null);
                setTimeout(() => setSelectedResource(updatedResource), 1000);
              }
            }
          }}
          onServiceSelect={(serviceName) => {
            if (serviceName === 'canvas') {
              // Clear selection and reset to canvas view
              setSelectedResource(null);
              setPreviewUrl(null);
              setPreviewTabs([]);
              setActivePreviewTab(-1);
              
              // Notify parent to update URL
              if (onProjectContextChange) {
                onProjectContextChange({
                  tenant: selectedTenant,
                  project: selectedProject,
                  revision: selectedRevision,
                  selectedResource: null,
                  selectedMicroservice: null,
                  activeTab: 'preview'
                });
              }
            } else if (serviceName === '+create-service') {
              // Open create service modal
              setIsCreateServiceModalOpen(true);
            } else if (serviceName === 'project-configuration') {
              // Select the project configuration repository
              const configurationRepo = {
                name: 'Project Configuration',
                repositoryUrl: fullProjectConfig?.repositoryUrl || fullProjectConfig?.configurationGitPath || selectedProject?.repositoryUrl || selectedProject?.configurationGitPath,
                repoUrl: fullProjectConfig?.repositoryUrl || fullProjectConfig?.configurationGitPath || selectedProject?.repositoryUrl || selectedProject?.configurationGitPath,
                type: 'configuration'
              };
              
              // Log for debugging
              console.log('🗂️ [Canvas] Selected Project Configuration:', {
                repositoryUrl: configurationRepo.repositoryUrl,
                fullProjectConfig: fullProjectConfig,
                selectedProject: selectedProject
              });
              
              setSelectedResource({type: 'configuration', data: configurationRepo});
              
              // Show repository view in code tab
              if (activeTab === 'code') {
                setShowRepositoryView(true);
                setShowTreePanel(false);
              }
            } else if (serviceName && configuration.services[serviceName]) {
              const service = configuration.services[serviceName];
              setSelectedResource({type: 'service', data: service});
              
              // Different behavior based on active tab
              if (activeTab === 'code') {
                setShowRepositoryView(true);
                setShowTreePanel(false);
              } else if (activeTab === 'preview') {
                // Notify parent to update URL for the selected service
                if (onProjectContextChange) {
                  onProjectContextChange({
                    tenant: selectedTenant,
                    project: selectedProject,
                    revision: selectedRevision,
                    selectedResource: {type: 'service', data: service},
                    selectedMicroservice: service,
                    activeTab: 'preview'
                  });
                }
              }
            }
          }}
          onTabChange={handleTabChange}
          showDashboardTab={showDashboardTab}
          showRequirementsTab={showRequirementsTab}
          onToggleFullscreen={handleToggleFullscreen}
          onNewConversation={onNewConversation}
          onShowHistory={onShowHistory}
          selectedResource={selectedResource}
          markdownFiles={markdownFiles}
          selectedMarkdownFile={selectedMarkdownFile}
          onMarkdownFileSelect={(filePath) => {
            setSelectedMarkdownFile(filePath);
          }}
        />

        {/* Canvas Content */}
        <div className="canvas-content">
          {/* DASHBOARD TAB */}
          {activeTab === 'dashboard' && (
            <div className="dashboard-layout">
              <DynamicTaskPanel
                selectedProject={selectedProject}
                previewTabs={previewTabs}
                configuration={configuration}
                selectedEnvironment={selectedEnvironment}
                onAction={(action, ...args) => {
                  switch (action) {
                    case 'create-project':
                      setIsCreateProjectModalOpen(true);
                      break;
                    case 'view-logs':
                      handleTabChange('cloud');
                      setCloudTab('containers');
                      break;
                    case 'start-service':
                      handleTabChange('preview');
                      break;
                    case 'deploy':
                      setShowDeploymentModal(true);
                      break;
                    case 'add-service':
                      handleTabChange('code');
                      break;
                    case 'add-endpoint':
                      handleTabChange('code');
                      break;
                    default:
                      break;
                  }
                }}
                isVisible={true}
              />
              <UnifiedDashboard
                selectedProject={selectedProject}
                selectedEnvironment={selectedEnvironment}
                previewTabs={previewTabs}
                activePreviewTab={activePreviewTab}
                configuration={configuration}
                onAction={(action, ...args) => {
                  switch (action) {
                    case 'open-console':
                      if (selectedProject) {
                        const consoleUrl = `${config.consoleBaseUrl}/projects/${selectedProject.id}`;
                        window.open(consoleUrl, '_blank');
                      }
                      break;
                    case 'settings':
                      handleTabChange('summary');
                      break;
                    case 'view-preview':
                      setActivePreviewTab(args[0]);
                      handleTabChange('preview');
                      break;
                    case 'open-tab':
                      window.open(args[0], '_blank', 'noopener,noreferrer');
                      break;
                    case 'restart':
                      controlContainer(args[0], args[1], 'restart');
                      break;
                    case 'start-service':
                      handleTabChange('preview');
                      break;
                    case 'deploy':
                      setShowDeploymentModal(true);
                      break;
                    case 'add-service':
                      handleTabChange('code');
                      break;
                    case 'view-logs':
                      handleTabChange('cloud');
                      setCloudTab('containers');
                      break;
                    default:
                      break;
                  }
                }}
              />
            </div>
          )}

          {/* PREVIEW TAB */}
          {activeTab === 'preview' && (
            <>
              {/* Configuration Markdown Preview - Show first if configuration is selected */}
              {selectedResource?.type === 'configuration' ? (
                <div className="canvas-markdown-preview-container">
                  {/* Left Sidebar - Markdown File List */}
                  <div className="markdown-files-sidebar">
                    {/* Dashboard Section - Always visible at the top */}
                    <button
                      className={`markdown-sidebar-header ${selectedSidebarView === 'dashboard' ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedSidebarView('dashboard');
                        setSelectedMarkdownFile(null);
                      }}
                      type="button"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/>
                        <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/>
                        <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/>
                        <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/>
                      </svg>
                      <span>Dashboard</span>
                    </button>

                    {/* Documentation Section - Always Expanded */}
                    <div className="markdown-sidebar-section expanded">
                      <button
                        className={`markdown-sidebar-header ${selectedSidebarView === 'documentation' ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedSidebarView('documentation');
                          setSelectedMarkdownFile(null);
                        }}
                        type="button"
                      >
                        <Icon name="file" size={16} />
                        <span>Documentation</span>
                      </button>
                      <div className="markdown-files-list">
                        {markdownFiles.length > 0 ? (
                          markdownFiles.map((file) => (
                            <button
                              key={file}
                              className={`markdown-file-item ${selectedMarkdownFile === file ? 'selected' : ''}`}
                              onClick={() => {
                                setSelectedSidebarView('documentation');
                                setSelectedMarkdownFile(file);
                              }}
                              type="button"
                            >
                              <Icon name="file" size={14} />
                              <span className="file-name">{file}</span>
                            </button>
                          ))
                        ) : (
                          <div className="no-files-message">
                            <Icon name="info" size={20} />
                            <p>No markdown files found</p>
                            <p className="help-text">Visit the <strong>Code</strong> tab to load repository files</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Content Area - Dashboard or Markdown Preview */}
                  <div className="markdown-preview-content">
                    {selectedSidebarView === 'dashboard' ? (
                      <DashboardView
                        selectedProject={memoizedSelectedProject}
                        selectedResource={selectedResource}
                        configuration={configuration}
                        onResourceSelect={handleResourceClick}
                        onOpenConsole={() => {
                          if (selectedProject && selectedProject.id) {
                            const consoleUrl = `${config.consoleBaseUrl}/projects/${selectedProject.id}/overview`;
                            window.open(consoleUrl, '_blank');
                          }
                        }}
                        onProjectDeleted={(deletedProjectId) => {
                          setProjects(prev => prev.filter(p => p.id !== deletedProjectId));
                          setSelectedProject(null);
                          if (onNavigateHome) onNavigateHome();
                        }}
                      />
                    ) : selectedMarkdownFile ? (
                      <>
                        <div className="markdown-preview-header">
                          <Icon name="file" size={16} />
                          <span>{selectedMarkdownFile}</span>
                        </div>
                        <div className="canvas-document markdown-content">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              code({node, inline, className, children, ...props}) {
                                const match = (className || '').match(/language-mermaid/);
                                const chartCode = String(children).replace(/\n$/, '');
                                return !inline && match ? (
                                  <Mermaid chart={chartCode} />
                                ) : (
                                  <code className={className} {...props}>{children}</code>
                                );
                              }
                            }}
                          >
                            {markdownContent}
                          </ReactMarkdown>
                        </div>
                      </>
                    ) : (
                      <div className="preview-placeholder">
                        <span className="placeholder-icon"><Icon name="file" size={48} /></span>
                        <p>Select a markdown file from the sidebar to preview</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : ((selectedResource?.type === 'service' || previewUrl) && (() => {
                if (selectedResource?.type === 'service') {
                  const detection = detectServiceType(selectedResource.data);
                  return detection.isFrontend || detection.isBackend;
                }
                return previewUrl; // Show preview if we have a URL even without selectedResource
              })() || previewTabs.length > 0) ? (
                <div
                  className={`canvas-frontend-preview ${previewFullscreen ? 'preview-fullscreen' : ''}`}
                  ref={previewContainerRef}
                >
                  {/* Multi-tab preview rendering */}
                  {previewTabs.length > 0 ? (
                    <>
                      {previewTabs.map((tab, index) => {
                        const isActive = activePreviewTab === index;
                        return (
                          <div 
                            key={tab.serviceName}
                            style={{ display: isActive ? 'flex' : 'none', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}
                          >
                            {tab.error ? (
                              (() => {
                                if (tab.error.startsWith('__GIT_TOOL_DISABLED__:') || tab.error.startsWith('__GIT_NOT_AUTHENTICATED__:')) {
                                  const isDisabled = tab.error.startsWith('__GIT_TOOL_DISABLED__:');
                                  const pName = tab.error.split(':')[1];
                                  const pIconName = pName === 'GitHub' ? 'github' : pName === 'Azure DevOps' ? 'azure-devops' : 'gitlab';
                                  return (
                                    <div className={`preview-git-blocked ${isDisabled ? 'tool-disabled' : 'not-connected'}`}>
                                      <div className="preview-git-blocked-icon">
                                        <Icon name={pIconName} size={48} glow={true} />
                                      </div>
                                      <h3>{isDisabled ? `${pName} Integration Disabled` : `${pName} Account Not Connected`}</h3>
                                      <p>{isDisabled
                                        ? `The ${pName} integration is disabled. Enable it in Tools to load this preview.`
                                        : `Your ${pName} account is not connected. Connect it to load this preview.`}
                                      </p>
                                      <div className="preview-git-blocked-steps">
                                        {isDisabled ? (<>
                                          <div className="preview-blocked-step"><span>1</span><span>Go to <strong>Tools</strong></span></div>
                                          <div className="preview-blocked-step"><span>2</span><span>Enable <strong>{pName}</strong></span></div>
                                          <div className="preview-blocked-step"><span>3</span><span>Reload this page</span></div>
                                        </>) : (<>
                                          <div className="preview-blocked-step"><span>1</span><span>Go to <strong>Tools</strong></span></div>
                                          <div className="preview-blocked-step"><span>2</span><span>Connect your <strong>{pName}</strong> account</span></div>
                                          <div className="preview-blocked-step"><span>3</span><span>Come back — preview will load automatically</span></div>
                                        </>)}
                                      </div>
                                      <button
                                        className="preview-git-blocked-btn"
                                        onClick={() => { window.location.href = '/tools'; }}
                                        type="button"
                                      >
                                        {isDisabled ? '🔧 Go to Tools' : '🔑 Connect Account'}
                                      </button>
                                    </div>
                                  );
                                }
                                return (
                                  <div className="preview-error">
                                    <span className="error-icon"><Icon className="warning" name="warning" size={24} /></span>
                                    <p>{tab.error}</p>
                                  </div>
                                );
                              })()
                            ) : tab.url ? (
                              <>
                                {/* Loading overlay - shows on top of iframe while loading */}
                                {tab.loading && !tab.iframeLoaded && (
                                  <div className="preview-loading-overlay" style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'rgba(0, 0, 0, 0.95)',
                                    zIndex: 1000,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <div className="loading-spinner" />
                                    <p style={{ fontSize: '16px', fontWeight: '500', marginTop: '16px' }}>
                                      {tab.pullingCode 
                                        ? `📦 Pulling code from Git for ${tab.serviceName}...` 
                                        : tab.renderPhase === 'installing'
                                          ? `Installing dependencies for ${tab.serviceName} (this may take 5-7 minutes for large projects with 600+ files)...`
                                          : `Rendering ${tab.serviceName} preview...`
                                      }
                                    </p>
                                    {(tab.renderPhase || tab.renderLogs) && (
                                      <div className="render-progress" style={{ marginTop: '20px', textAlign: 'left', maxWidth: '600px', width: '100%' }}>
                                        {tab.renderPhase && (
                                          <>
                                            <div style={{ marginBottom: '10px' }}>
                                              <strong>Phase:</strong> {tab.renderPhase} ({tab.renderProgress || 0}%)
                                            </div>
                                            <div style={{ 
                                              width: '100%', 
                                              height: '8px', 
                                              background: '#333', 
                                              borderRadius: '4px',
                                              overflow: 'hidden'
                                            }}>
                                              <div style={{
                                                width: `${tab.renderProgress || 0}%`,
                                                height: '100%',
                                                background: 'linear-gradient(90deg, #4CAF50, #8BC34A)',
                                                transition: 'width 0.3s ease'
                                              }} />
                                            </div>
                                          </>
                                        )}
                                        {tab.renderLogs && tab.renderLogs.length > 0 && (
                                          <LogViewer logs={tab.renderLogs} />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Navbar and iframe - always rendered when url exists */}
                                <div className="preview-navbar">
                                  <button
                                    className="nav-btn"
                                    onClick={() => {
                                      const updatedTabs = [...previewTabs];
                                      updatedTabs[index] = {...tab, iframeKey: Date.now()};
                                      setPreviewTabs(updatedTabs);
                                    }}
                                    title="Force Refresh"
                                    type="button"
                                  >
                                    <Icon name="refresh" size={12} glow={false} />
                                  </button>
                                  {editingTabUrl === tab.serviceName ? (
                                    <form
                                      className="url-form"
                                      onSubmit={(e) => {
                                        e.preventDefault();
                                        const baseUrl = getBaseUrl(tab.url);
                                        const pathInput = tabUrlInputs[tab.serviceName] || '/';
                                        const newUrl = constructFullUrl(baseUrl, pathInput);
                                        const updatedTabs = [...previewTabs];
                                        updatedTabs[index] = {...tab, url: newUrl, iframeKey: Date.now()};
                                        setPreviewTabs(updatedTabs);
                                        setEditingTabUrl(null);
                                      }}
                                    >
                                      <input
                                        className="url-input"
                                        type="text"
                                        value={tabUrlInputs[tab.serviceName] !== undefined ? tabUrlInputs[tab.serviceName] : getUrlPath(tab.url)}
                                        onChange={(e) => setTabUrlInputs({...tabUrlInputs, [tab.serviceName]: e.target.value})}
                                        onBlur={() => setEditingTabUrl(null)}
                                        placeholder="/path"
                                        autoFocus
                                      />
                                      <button type="submit" className="nav-btn" title="Go">
                                        <Icon name="arrow-right" size={12} glow={false} />
                                      </button>
                                    </form>
                                  ) : (
                                    <div
                                      className="url-display"
                                      onClick={() => {
                                        setTabUrlInputs({...tabUrlInputs, [tab.serviceName]: getUrlPath(tab.url)});
                                        setEditingTabUrl(tab.serviceName);
                                      }}
                                      title="Click to edit URL path"
                                    >
                                      <Icon name="compass" size={11} glow={false} />
                                      <span className="url-text">{getUrlPath(tab.url)}</span>
                                    </div>
                                  )}
                                  <button
                                    className="nav-btn"
                                    onClick={() => window.open(tab.url, '_blank')}
                                    title="Open in new tab"
                                    type="button"
                                  >
                                    <Icon name="link" size={12} glow={false} />
                                  </button>
                                </div>
                                {/* Hide iframe when loading/re-rendering to avoid showing error messages */}
                                {tab.containerPort === 3022 ? (
                                  /* Helm Chart Container — show inline validation panel instead of iframe */
                                  <HelmPreviewPanel
                                    enabled={isActive}
                                    sessionId={tab.sessionId}
                                    projectId={tab.projectId || selectedProject?.id}
                                    serviceName={tab.serviceName}
                                    containerPort={3022}
                                    onRunTest={async () => {
                                      const renderApiUrl = getContainerApiUrl(3022, '/api/render');
                                      const projectId = tab.projectId || selectedProject?.id;
                                      const userId = getOrCreateUserId(projectId, tab.serviceName);
                                      // getAllFiles returns an array of {filePath, content, ...} records
                                      const rawRecords = await indexedDbFileStorage.getAllFiles(projectId, tab.serviceName);
                                      if (!rawRecords || rawRecords.length === 0) return;
                                      // Convert array → {filePath: content} map, sanitize objects to strings
                                      const files = {};
                                      for (const record of rawRecords) {
                                        const p = record.filePath || record.path;
                                        const c = record.content;
                                        if (p) files[p] = typeof c === 'string' ? c : JSON.stringify(c, null, 2);
                                      }
                                      if (Object.keys(files).length === 0) return;
                                      await getAuthorizedFetch()(renderApiUrl, {
                                        method: 'POST',
                                        headers: getBffHeaders({'Content-Type': 'application/json'}),
                                        body: JSON.stringify({
                                          serviceName: tab.serviceName,
                                          sourceCode: files,
                                          projectId,
                                          userId,
                                        }),
                                      });
                                    }}
                                  />
                                ) : (
                                  <>
                                    {!tab.loading && (
                                      <iframe
                                        className="frontend-preview-iframe"
                                        key={tab.iframeKey}
                                        ref={isActive ? iframeRef : null}
                                        sandbox="allow-scripts allow-same-origin allow-forms"
                                        src={tab.url}
                                        title={`${tab.serviceName} Preview`}
                                        onLoad={(e) => handleIframeLoad(e, tab)}
                                      />
                                    )}
                                    {/* Show loading overlay when re-rendering */}
                                    {tab.loading && (
                                      <div className="preview-loading">
                                        <div className="loading-spinner" />
                                        <p>{tab.pullingCode 
                                          ? `Pulling code from repository for ${tab.serviceName}...` 
                                          : tab.renderPhase === 'installing'
                                            ? `Installing dependencies for ${tab.serviceName} (this may take 5-7 minutes for large projects with 600+ files)...`
                                            : `Rendering ${tab.serviceName} preview...`
                                        }</p>
                                        {(tab.renderPhase || tab.renderLogs) && (
                                          <div className="render-progress" style={{ marginTop: '20px', textAlign: 'left', maxWidth: '600px', width: '100%' }}>
                                            {tab.renderPhase && (
                                              <>
                                                <div style={{ marginBottom: '10px' }}>
                                                  <strong>Phase:</strong> {tab.renderPhase} ({tab.renderProgress || 0}%)
                                                </div>
                                                <div style={{ 
                                                  width: '100%', 
                                                  height: '8px', 
                                                  background: '#333', 
                                                  borderRadius: '4px',
                                                  overflow: 'hidden'
                                                }}>
                                                  <div style={{
                                                    width: `${tab.renderProgress || 0}%`,
                                                    height: '100%',
                                                    background: 'linear-gradient(90deg, #4CAF50, #8BC34A)',
                                                    transition: 'width 0.3s ease'
                                                  }} />
                                                </div>
                                              </>
                                            )}
                                            {tab.renderLogs && tab.renderLogs.length > 0 && (
                                              <LogViewer logs={tab.renderLogs} />
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    {/* Preview Logs Component */}
                                    <PreviewLogs
                                      enabled={isActive}
                                      position="bottom"
                                      sessionId={tab.sessionId}
                                      containerPort={tab.containerPort}
                                      isExpanded={logsExpanded}
                                      onToggleExpanded={() => setLogsExpanded(prev => !prev)}
                                      onErrorCountChange={setLogErrorCount}
                                      onBuildError={(errorText) => {
                                        // Use a Canvas-level key so the guard survives PreviewLogs
                                        // unmount/remount (e.g. when the user switches tabs).
                                        const shownKey = `${tab.serviceName}:${tab.sessionId || 'no-session'}`;
                                        if (buildErrorShownRef.current.has(shownKey)) return;
                                        buildErrorShownRef.current.add(shownKey);
                                        setBuildErrorModal({
                                          isOpen: true,
                                          error: errorText,
                                          serviceName: tab.serviceName,
                                        });
                                      }}
                                      hideUI={!logsExpanded}
                                      onAskAI={(prompt) => {
                                        window.dispatchEvent(new CustomEvent('askAIFromLogs', {
                                          detail: {prompt}
                                        }));
                                      }}
                                    />
                                  </>
                                )}
                              </>
                            ) : tab.loading ? (
                              <div className="preview-loading">
                                <div className="loading-spinner" />
                                <p>{tab.pullingCode 
                                  ? `Pulling code from repository for ${tab.serviceName}...` 
                                  : tab.renderPhase === 'installing'
                                    ? `Installing dependencies for ${tab.serviceName} (this may take 5-7 minutes for large projects with 600+ files)...`
                                    : `Rendering ${tab.serviceName} preview...`
                                }</p>
                                {(tab.renderPhase || tab.renderLogs) && (
                                  <div className="render-progress" style={{ marginTop: '20px', textAlign: 'left', maxWidth: '600px', width: '100%' }}>
                                    {tab.renderPhase && (
                                      <>
                                        <div style={{ marginBottom: '10px' }}>
                                          <strong>Phase:</strong> {tab.renderPhase} ({tab.renderProgress || 0}%)
                                        </div>
                                        <div style={{ 
                                          width: '100%', 
                                          height: '8px', 
                                          background: '#333', 
                                          borderRadius: '4px',
                                          overflow: 'hidden'
                                        }}>
                                          <div style={{
                                            width: `${tab.renderProgress || 0}%`,
                                            height: '100%',
                                            background: 'linear-gradient(90deg, #4CAF50, #8BC34A)',
                                            transition: 'width 0.3s ease'
                                          }} />
                                        </div>
                                      </>
                                    )}
                                    {tab.renderLogs && tab.renderLogs.length > 0 && (
                                      <LogViewer logs={tab.renderLogs} />
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : (
                              /* Fallback: tab has no url, no error and is not loading.
                                 Open the modal so the user gets feedback instead of a white screen. */
                              <div className="preview-error" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '16px' }}>
                                <span className="error-icon"><Icon className="warning" name="warning" size={32} /></span>
                                <p style={{ margin: 0 }}>{'Preview could not be loaded.'}</p>
                                <button
                                  style={{ padding: '8px 20px', background: '#007acc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                                  onClick={() => setBuildErrorModal({ isOpen: true, error: 'The preview could not be started. This may be caused by a build error, a missing service, or a configuration issue. Please check your code and try again.', serviceName: tab.serviceName })}
                                  type="button"
                                >
                                  {'View Details'}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  ) : previewLoading ? (
                    <div className="preview-loading">
                      <div className="loading-spinner" />
                      <p>{'Loading frontend preview...'}</p>
                    </div>
                  ) : compilationStatus ? (
                    <div className="preview-loading" style={{position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1000, background: 'rgba(0,0,0,0.8)', padding: '20px'}}>
                      <div className="loading-spinner" />
                      <p>
                        {compilationStatus === 'syncing' && 'Syncing files to preview...'}
                        {compilationStatus === 'compiling' && 'Compiling your changes...'}
                        {compilationStatus === 'ready' && '✓ Compilation complete!'}
                      </p>
                    </div>
                  ) : previewError ? (
                    (() => {
                      if (previewError.startsWith('__GIT_TOOL_DISABLED__:') || previewError.startsWith('__GIT_NOT_AUTHENTICATED__:')) {
                        const isDisabled = previewError.startsWith('__GIT_TOOL_DISABLED__:');
                        const pName = previewError.split(':')[1];
                        const pIconName = pName === 'GitHub' ? 'github' : pName === 'Azure DevOps' ? 'azure-devops' : 'gitlab';
                        return (
                          <div className={`preview-git-blocked ${isDisabled ? 'tool-disabled' : 'not-connected'}`}>
                            <div className="preview-git-blocked-icon">
                              <Icon name={pIconName} size={48} glow={true} />
                            </div>
                            <h3>{isDisabled ? `${pName} Integration Disabled` : `${pName} Account Not Connected`}</h3>
                            <p>{isDisabled
                              ? `The ${pName} integration is disabled. Enable it in Tools to load this preview.`
                              : `Your ${pName} account is not connected. Connect it to load this preview.`}
                            </p>
                            <div className="preview-git-blocked-steps">
                              {isDisabled ? (<>
                                <div className="preview-blocked-step"><span>1</span><span>Go to <strong>Tools</strong></span></div>
                                <div className="preview-blocked-step"><span>2</span><span>Enable <strong>{pName}</strong></span></div>
                                <div className="preview-blocked-step"><span>3</span><span>Reload this page</span></div>
                              </>) : (<>
                                <div className="preview-blocked-step"><span>1</span><span>Go to <strong>Tools</strong></span></div>
                                <div className="preview-blocked-step"><span>2</span><span>Connect your <strong>{pName}</strong> account</span></div>
                                <div className="preview-blocked-step"><span>3</span><span>Come back — preview will load automatically</span></div>
                              </>)}
                            </div>
                            <button
                              className="preview-git-blocked-btn"
                              onClick={() => { window.location.href = '/tools'; }}
                              type="button"
                            >
                              {isDisabled ? '🔧 Go to Tools' : '🔑 Connect Account'}
                            </button>
                          </div>
                        );
                      }
                      return (
                        <div className="preview-error">
                          <span className="error-icon"><Icon className="warning" name="warning" size={24} /></span>
                          <p>{previewError}</p>
                        </div>
                      );
                    })()
                  ) : previewUrl ? (
                    <>
                      <div className="preview-navbar">
                        <button
                          className="nav-btn"
                          onClick={() => setIframeKey(Date.now())}
                          title="Force Refresh"
                          type="button"
                        >
                          <Icon name="refresh" size={12} glow={false} />
                        </button>
                        {isEditingPreviewUrl ? (
                          <form
                            className="url-form"
                            onSubmit={(e) => {
                              e.preventDefault();
                              const baseUrl = getBaseUrl(previewUrl);
                              const newFullUrl = constructFullUrl(baseUrl, previewUrlInput);
                              setPreviewUrlInput(newFullUrl);
                              setIsEditingPreviewUrl(false);
                              setIframeKey(Date.now());
                            }}
                          >
                            <input
                              className="url-input"
                              type="text"
                              value={previewUrlInput !== '' ? previewUrlInput : getUrlPath(previewUrl)}
                              onChange={(e) => setPreviewUrlInput(e.target.value)}
                              onBlur={() => setIsEditingPreviewUrl(false)}
                              placeholder="/path"
                              autoFocus
                            />
                            <button type="submit" className="nav-btn" title="Go">
                              <Icon name="arrow-right" size={12} glow={false} />
                            </button>
                          </form>
                        ) : (
                          <div
                            className="url-display"
                            onClick={() => {
                              setPreviewUrlInput(getUrlPath(previewUrlInput || previewUrl));
                              setIsEditingPreviewUrl(true);
                            }}
                            title="Click to edit URL path"
                          >
                            <Icon name="compass" size={11} glow={false} />
                            <span className="url-text">{getUrlPath(previewUrlInput || previewUrl)}</span>
                          </div>
                        )}
                        <button
                          className="nav-btn"
                          onClick={() => window.open(previewUrlInput || previewUrl, '_blank')}
                          title="Open in new tab"
                          type="button"
                        >
                          <Icon name="link" size={12} glow={false} />
                        </button>
                      </div>
                      <iframe
                        className="frontend-preview-iframe"
                        key={iframeKey}
                        ref={iframeRef}
                        sandbox="allow-scripts allow-same-origin allow-forms"
                        src={previewUrlInput || previewUrl}
                        title="Frontend Preview"
                        onLoad={(e) => handleIframeLoad(e, {serviceName: selectedResource?.data?.name, url: previewUrl, service: selectedResource?.data})}
                      />                     
                      {/* Preview Logs Component */}
                      <PreviewLogs
                        enabled={true}
                        position="bottom"
                        sessionId={previewSessionId}
                        containerPort={previewContainerPort}
                        isExpanded={logsExpanded}
                        onToggleExpanded={() => setLogsExpanded(prev => !prev)}
                        onErrorCountChange={setLogErrorCount}
                        onBuildError={(errorText) => {
                          const shownKey = `${selectedResource?.data?.name || 'preview'}:${previewSessionId || 'no-session'}`;
                          if (buildErrorShownRef.current.has(shownKey)) return;
                          buildErrorShownRef.current.add(shownKey);
                          setBuildErrorModal({
                            isOpen: true,
                            error: errorText,
                            serviceName: selectedResource?.data?.name || selectedResource?.name || 'Preview',
                          });
                        }}
                        hideUI={!logsExpanded}
                        onAskAI={(prompt) => {
                          window.dispatchEvent(new CustomEvent('askAIFromLogs', {
                            detail: {prompt}
                          }));
                        }}
                      />
                    </>
                  ) : (
                    <div className="preview-placeholder">
                      <span className="placeholder-icon"><Icon name="design" size={48} /></span>
                      <p>{'Select a frontend or backend service to preview'}</p>
                    </div>
                  )}
                </div>
              ) : contentType === 'image' ? (
                <div className="canvas-image-container">
                  <div className="canvas-image-wrapper">
                    <img 
                      alt={content.title || 'Generated image'}
                      className="canvas-image"
                      src={content.content}
                    />
                  </div>
                  {content.prompt && (
                    <div className="canvas-image-prompt">
                      <strong>{'Prompt: '}</strong>
                      {content.prompt}
                    </div>
                  )}
                </div>
              ) : contentType === 'video' ? (
                <div className="canvas-video-container">
                  <div className="canvas-video-wrapper">
                    <video 
                      className="canvas-video"
                      controls
                      src={content.content}
                    >
                      {'Your browser does not support the video tag.'}
                    </video>
                  </div>
                  {content.prompt && (
                    <div className="canvas-video-prompt">
                      <strong>{'Prompt: '}</strong>
                      {content.prompt}
                    </div>
                  )}
                </div>
              ) : detectedLanguage === 'html' ? (
                <iframe
                  className="html-preview-iframe"
                  sandbox="allow-scripts allow-same-origin"
                  srcDoc={extractedCode || content.content}
                  style={{width: '100%', height: '100%', border: 'none'}}
                  title="HTML Preview"
                />
              ) : (
                <div className="canvas-document">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({node, inline, className, children, ...props}) {
                        const match = (className || '').match(/language-mermaid/);
                        const chartCode = String(children).replace(/\n$/, '');
                        return !inline && match ? (
                          <Mermaid chart={chartCode} />
                        ) : (
                          <code className={className} {...props}>{children}</code>
                        );
                      }
                    }}
                  >
                    {content.content}
                  </ReactMarkdown>
                </div>
              )}
            </>
          )}

          {/* CODE TAB - Source Code with IndexedDB and Git Integration */}
          {activeTab === 'code' && (
            <div className={`canvas-code-view ${splitViewMode === 'split' ? 'split-view-mode' : ''}`}>
              {/* Code Layout with Split View Support */}
              <div className={`code-layout ${splitViewMode === 'split' ? 'split-layout' : ''}`}>
                {/* Source Code View with Logger */}
                <div 
                  className="code-content-area" 
                  style={splitViewMode === 'split' ? {width: `${splitRatio}%`} : {}}
                >
                  {(selectedResource?.type === 'service' || selectedResource?.type === 'configuration') && repositoryUrl ? (
                    <CodeViewWithLogger
                      ref={codeViewRef}
                      projectId={selectedProject?.id || selectedProject?._id}
                      serviceName={selectedResource.data?.name}
                      repositoryUrl={repositoryUrl}
                      branch={selectedRevision?.name || 'main'}
                      onFileSelect={handleFileSelect}
                      onFileChange={(filePath, content) => {
                        console.log('File changed:', filePath);
                      }}
                      // Project context for git operations
                      selectedProject={selectedProject}
                      selectedRevision={selectedRevision}
                      selectedTenant={selectedTenant}
                      selectedResource={selectedResource}
                      configuration={configuration}
                      collections={collections}
                      onProjectContextChange={onProjectContextChange}
                      // Preview control
                      previewUrl={previewUrl}
                      setPreviewUrl={setPreviewUrl}
                      setPreviewError={setPreviewError}
                      setForceGitFetch={setForceGitFetch}
                      autoTriggeredPullRef={autoTriggeredPullRef}
                    />
                  ) : selectedResource?.type === 'service' ? (
                    <div className="code-service-details">
                      <div className="code-editor-header">
                        <span className="file-path">{selectedResource.data?.name || 'Service'}</span>
                        <div className="header-actions">
                          <button
                            className="view-mode-btn"
                            onClick={() => setActiveTab('service')}
                            title="View full service details"
                            type="button"
                          >
                            <Icon name="package" size={16} />
                            {' Full Details'}
                          </button>
                        </div>
                      </div>
                      <div className="code-service-info">
                        <p>No source code repository configured for this service.</p>
                        <button
                          className="view-mode-btn"
                          onClick={() => setActiveTab('service')}
                          type="button"
                        >
                          View Service Details
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="code-editor-empty">
                      <Icon name="code" size={48} />
                      <h3>Source Code</h3>
                      <p>Select a source code microservice from the dropdown above to browse its repository</p>
                    </div>
                  )}
                </div>

                {/* Split View Preview Panel - Only shown in split mode */}
                {splitViewMode === 'split' && previewTabs.length > 0 && (
                  <>
                    {/* Split Resize Handle */}
                    <div 
                      className={`resize-handle ${isResizingSplit ? 'active' : ''}`}
                      onMouseDown={() => setIsResizingSplit(true)}
                    />
                    <div className="split-preview-panel" style={{width: `${100 - splitRatio}%`}}>
                      <div className="split-preview-header">
                        <span className="preview-title">
                          <Icon name="eye" size={16} />
                          Live Preview
                        </span>
                        <div className="preview-actions">
                          <button
                            className="preview-action-btn"
                            onClick={handleCaptureThumbnail}
                            disabled={isCapturingThumbnail || !selectedProject}
                            title={isCapturingThumbnail ? "Capturing thumbnail..." : "Capture thumbnail"}
                            type="button"
                          >
                            {isCapturingThumbnail ? (
                              <span className="capture-spinner">⏳</span>
                            ) : (
                              <Icon name="camera" size={14} />
                            )}
                          </button>
                          <button
                            className="preview-action-btn"
                            onClick={() => {
                              const activeTab = previewTabs[activePreviewTab];
                              if (activeTab?.url) {
                                window.open(activeTab.url, '_blank', 'noopener,noreferrer');
                              }
                            }}
                            disabled={!previewTabs[activePreviewTab]?.url}
                            title="Open in new tab"
                            type="button"
                          >
                            <Icon name="link" size={14} />
                          </button>
                          <button
                            className="preview-action-btn"
                            onClick={() => setSplitViewMode('editor')}
                            title="Close preview panel"
                            type="button"
                          >
                            <Icon name="close" size={14} />
                          </button>
                        </div>
                      </div>
                    <div className="split-preview-content">
                      {previewTabs[activePreviewTab]?.loading ? (
                        <div className="preview-loading">
                          <div className="loading-spinner" />
                          <p>Loading preview...</p>
                        </div>
                      ) : previewTabs[activePreviewTab]?.error ? (
                        (() => {
                          const errMsg = previewTabs[activePreviewTab].error;
                          if (errMsg.startsWith('__GIT_TOOL_DISABLED__:') || errMsg.startsWith('__GIT_NOT_AUTHENTICATED__:')) {
                            const isDisabled = errMsg.startsWith('__GIT_TOOL_DISABLED__:');
                            const pName = errMsg.split(':')[1];
                            const pIconName = pName === 'GitHub' ? 'github' : pName === 'Azure DevOps' ? 'azure-devops' : 'gitlab';
                            return (
                              <div className={`preview-git-blocked ${isDisabled ? 'tool-disabled' : 'not-connected'}`}>
                                <div className="preview-git-blocked-icon">
                                  <Icon name={pIconName} size={48} glow={true} />
                                </div>
                                <h3>{isDisabled ? `${pName} Integration Disabled` : `${pName} Not Connected`}</h3>
                                <p>{isDisabled ? `Enable the ${pName} integration in Tools.` : `Connect your ${pName} account in Connectors.`}</p>
                                <button className="preview-git-blocked-btn" onClick={() => { window.location.href = '/tools'; }} type="button">
                                  {isDisabled ? '🔧 Go to Tools' : '🔑 Connect Account'}
                                </button>
                              </div>
                            );
                          }
                          return (
                            <div className="preview-error">
                              <Icon name="warning" size={24} />
                              <p>{errMsg}</p>
                            </div>
                          );
                        })()
                      ) : previewTabs[activePreviewTab]?.url ? (
                        <iframe
                          className="split-preview-iframe"
                          key={previewTabs[activePreviewTab].iframeKey}
                          sandbox="allow-scripts allow-same-origin allow-forms"
                          src={previewTabs[activePreviewTab].url}
                          title="Split View Preview"
                          onLoad={(e) => handleIframeLoad(e, previewTabs[activePreviewTab])}
                        />
                      ) : (
                        <div className="preview-empty">
                          <Icon name="eye" size={32} />
                          <p>No preview available</p>
                          <p className="hint">Start a service to see live preview</p>
                        </div>
                      )}
                    </div>
                  </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* CLOUD TAB - All Resources with Sidebar */}
          {activeTab === 'cloud' && (
            <div className={`canvas-code-view ${splitViewMode === 'split' ? 'split-view-mode' : ''}`}>
              {generatedFiles && generatedFiles.length > 0 ? (
                <div className={`code-layout ${splitViewMode === 'split' ? 'split-layout' : ''}`}>
                  {/* File Explorer Sidebar */}
                  {showTreePanel && (
                    <>
                      <div className="file-explorer" style={{width: `${treeWidth}px`}}>
                        <div className="file-explorer-header">
                          <div className="explorer-header-left">
                            <span className="explorer-icon"><Icon name="folder" size={16} /></span>
                            <span className="explorer-title">{'Files'}</span>
                          </div>
                          <button
                            className="toggle-tree-btn"
                            onClick={() => setShowTreePanel(false)}
                            title="Hide sidebar"
                            type="button"
                          >
                            {'◀'}
                          </button>
                        </div>
                        <div className="file-list">
                          {generatedFiles.map((file, index) => (
                            <button
                              className={`file-item ${selectedFile === file ? 'selected' : ''}`}
                              key={index}
                              onClick={() => {
                                setSelectedFile(file);
                                setSelectedResource(null); // Clear resource selection
                              }}
                              type="button"
                            >
                              <span className="file-icon">{getFileIcon(file.path)}</span>
                              <span className="file-name">{file.path}</span>
                            </button>
                          ))}
                        </div>
                        
                        {/* Project Resources Tree */}
                        {selectedProject && selectedRevision && (
                          <div className="resources-section">
                            <ProjectResourcesTree
                              key={treeRefreshKey}
                              collectionsOverride={collections}
                              configuration={configuration}
                              error={configError}
                              loading={configLoading}
                              onResourceClick={handleResourceClick}
                              onCreateService={handleCreateService}
                              projectId={selectedProject.id}
                              revisionId={selectedRevision.name}
                              tenantId={selectedTenant?.id}
                              selectedResource={selectedResource}
                              showCreateServiceModal={isCreateServiceModalOpen}
                              setShowCreateServiceModal={setIsCreateServiceModalOpen}
                            />
                          </div>
                        )}
                        {/* Tree Resize Handle */}
                        <div 
                          className={`tree-resize-handle ${isResizingTree ? 'active' : ''}`}
                          onMouseDown={() => setIsResizingTree(true)}
                        />
                      </div>
                    </>
                  )}

                  {/* Code Editor */}
                  <div className="code-editor-panel">
                    {selectedFile ? (
                      <>
                        <div className="code-editor-header">
                          {!showTreePanel && (
                            <button
                              className="show-tree-btn-inline"
                              onClick={() => setShowTreePanel(true)}
                              title="Show sidebar"
                              type="button"
                            >
                              {'▶'}
                            </button>
                          )}
                          <span className="file-path">{selectedFile.path}</span>
                          <button
                            className="code-copy-btn"
                            onClick={() => navigator.clipboard.writeText(selectedFile.content)}
                            title="Copy code"
                            type="button"
                          >
                            <Icon name="document" size={16} />
                            {' Copy'}
                          </button>
                        </div>
                        <pre className="code-editor-content">
                          <code>{selectedFile.content}</code>
                        </pre>
                      </>
                    ) : selectedResource ? (
                      <div className="microservice-detail-view">
                        <div className="code-editor-header">
                          <div className="header-left">
                            {!showTreePanel && (
                              <button
                                className="show-tree-btn-inline"
                                onClick={() => setShowTreePanel(true)}
                                title="Show sidebar"
                                type="button"
                              >
                                {'▶'}
                              </button>
                            )}
                            <span className="file-path">{selectedResource.name}</span>
                          </div>
                          <div className="header-actions">
                            <span className={`service-type-badge badge-${selectedResource.type}`}>
                              {selectedResource.type}
                            </span>
                            {selectedResource.type === 'service' && (() => {
                              const detection = detectServiceType(selectedResource.data);
                              const isPreviewable = detection.isFrontend || detection.isBackend;
                              return (
                                <>
                                  {repositoryUrl && (
                                    <button
                                      className="view-mode-btn"
                                      onClick={() => setActiveTab('code')}
                                      title="Edit source code in Code tab"
                                      type="button"
                                    >
                                      <Icon name="code" size={16} />
                                      {' Edit Source'}
                                    </button>
                                  )}
                                  {isPreviewable && (
                                    <button
                                      className="view-mode-btn"
                                      onClick={() => setActiveTab('preview')}
                                      title="View preview"
                                      type="button"
                                    >
                                      <Icon name="eye" size={16} />
                                      {' Preview'}
                                    </button>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                        {renderResourceDetails()}
                      </div>
                    ) : (
                      <div className="code-editor-empty">
                        <p>{'Select a file or microservice to view details'}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="code-layout">
                  {/* Sidebar with Project Resources Tree */}
                  {showTreePanel && (
                    <>
                      <div className="file-explorer" style={{width: `${treeWidth}px`}}>
                        {/* Dashboard Section - Always visible at the top */}
                        <button
                          className={`markdown-sidebar-header ${selectedSidebarView === 'dashboard' ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedSidebarView('dashboard');
                            setSelectedResource(null);
                            setSelectedFile(null);
                          }}
                          type="button"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/>
                            <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/>
                            <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/>
                            <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/>
                          </svg>
                          <span>Dashboard</span>
                        </button>

                        {/* Architecture Section - After Dashboard */}
                        <button
                          className={`markdown-sidebar-header ${selectedSidebarView === 'architecture' ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedSidebarView('architecture');
                            setSelectedResource(null);
                            setSelectedFile(null);
                          }}
                          type="button"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 3h7v7H3V3z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinejoin="round"/>
                            <path d="M14 3h7v7h-7V3z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinejoin="round"/>
                            <path d="M3 14h7v7H3v-7z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinejoin="round"/>
                            <path d="M14 14h7v7h-7v-7z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinejoin="round"/>
                            <line x1="12" y1="6.5" x2="14" y2="6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            <line x1="10" y1="6.5" x2="6.5" y2="6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            <line x1="6.5" y1="10" x2="6.5" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            <line x1="17.5" y1="10" x2="17.5" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            <line x1="6.5" y1="17.5" x2="14" y2="17.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          <span>Architecture</span>
                        </button>

                        <div className="file-explorer-header">
                          <div className="explorer-header-left">
                            <span className="explorer-icon"><Icon name="package" size={16} /></span>
                            <span className="explorer-title">{'Resources'}</span>
                            {selectedProject && selectedRevision && (
                              <button
                                className="refresh-config-btn-inline"
                                onClick={async () => {
                                  try {
                                    addActivityLog('Refreshing configuration from BFF...');
                                    await loadConfiguration(selectedProject.id, selectedRevision.id);
                                    addActivityLog('Configuration refreshed successfully');
                                  } catch (error) {
                                    console.error('Failed to refresh configuration:', error);
                                    addActivityLog(`Failed to refresh: ${error.message}`);
                                  }
                                }}
                                disabled={configLoading}
                                title="Refresh configuration from Console"
                                type="button"
                              >
                                <Icon name="refresh" size={14} />
                              </button>
                            )}
                          </div>
                      
                      </div>
                      
                      {/* Project Resources Tree */}
                      {selectedProject && selectedRevision && (
                        <div className="resources-section-full">
                          <ProjectResourcesTree
                            key={treeRefreshKey}
                            collectionsOverride={collections}
                            configuration={configuration}
                            error={configError}
                            loading={configLoading}
                            onResourceClick={handleResourceClick}
                            onCreateService={handleCreateService}
                            projectId={selectedProject.id}
                            revisionId={selectedRevision.name}
                            tenantId={selectedTenant?.id}
                            selectedResource={selectedResource}
                            showCreateServiceModal={isCreateServiceModalOpen}
                            setShowCreateServiceModal={setIsCreateServiceModalOpen}
                          />
                        </div>
                      )}
                      
                      {!selectedProject && (
                        <div className="sidebar-placeholder">
                          <span className="placeholder-icon"><Icon name="organization" size={32} /></span>
                          <p>{'Select an organization and project to view microservices'}</p>
                        </div>
                      )}
                      {/* Tree Resize Handle */}
                      <div 
                        className={`tree-resize-handle ${isResizingTree ? 'active' : ''}`}
                        onMouseDown={() => setIsResizingTree(true)}
                      />
                    </div>
                    </>
                  )}

                  {/* Code/Detail Panel */}
                  <div className="code-editor-panel">
                    {selectedResource ? (
                      <div className="microservice-detail-view">
                        {selectedResource.type === 'collection' && showRepositoryView ? (
                          <>
                            {/* Collection Source Code View - With Tree Panel */}
                            <div className="collection-editor-header">
                              <div className="header-left">
                                {!showTreePanel && (
                                  <button
                                    className="show-tree-btn-inline"
                                    onClick={() => setShowTreePanel(true)}
                                    title="Show sidebar"
                                    type="button"
                                  >
                                    {'▶'}
                                  </button>
                                )}
                                <span className="file-path">{`${selectedCollection?.name || 'Collection'}.json`}</span>
                              </div>
                              <div className="header-actions">
                                <button
                                  className="view-mode-btn"
                                  onClick={() => setShowRepositoryView(false)}
                                  title="Show collection overview"
                                  type="button"
                                >
                                  <Icon name="document" size={16} />{' Overview'}
                                </button>
                              </div>
                            </div>
                            <div className="microservice-repository-layout">
                              {/* Collections tree sidebar */}
                              {showTreePanel && (
                                <div className="repository-sidebar">
                                  <div className="sidebar-header">
                                    <span className="sidebar-title">{'Collections'}</span>
                                  </div>
                                  {selectedProject && selectedRevision && (
                                    <ProjectResourcesTree
                                      key={treeRefreshKey}
                                      collectionsOverride={collections}
                                      configuration={configuration}
                                      error={configError}
                                      loading={configLoading}
                                      onResourceClick={handleResourceClick}
                                      selectedResource={selectedResource}
                                      showOnlyCollections={true}
                                    />
                                  )}
                                </div>
                              )}
                              {/* Monaco editor */}
                              <div className="repository-content">
                                {selectedCollection && (
                                  <CollectionEditor
                                    collection={selectedCollection}
                                    key={`collection-editor-2-${selectedCollection.name}`}
                                    onSave={handleCollectionSave}
                                    projectId={selectedProject?.id}
                                    readOnly={false}
                                  />
                                )}
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="code-editor-header">
                              <div className="header-left">
                                {!showTreePanel && (
                                  <button
                                    className="show-tree-btn-inline"
                                    onClick={() => setShowTreePanel(true)}
                                    title="Show sidebar"
                                    type="button"
                                  >
                                    {'▶'}
                                  </button>
                                )}
                                <span className="file-path">{selectedResource.name}</span>
                              </div>
                              <div className="header-actions">
                                <span className={`service-type-badge badge-${selectedResource.type}`}>
                                  {selectedResource.type}
                                </span>
                                {selectedResource.type === 'service' && (() => {
                                  const detection = detectServiceType(selectedResource.data);
                                  const isPreviewable = detection.isFrontend || detection.isBackend;
                                  return (
                                    <>
                                      {isPreviewable && (
                                        <button
                                          className="view-mode-btn"
                                          onClick={() => setActiveTab('preview')}
                                          title="View preview"
                                          type="button"
                                        >
                                          <Icon name="eye" size={16} />
                                          {' Preview'}
                                        </button>
                                      )}
                                    </>
                                  );
                                })()}
                                {selectedProject && selectedProject.id && selectedRevision && (
                                  <button
                                    className="view-mode-btn"
                                    onClick={() => {
                                      const revisionName = selectedRevision.name || selectedRevision.id;
                                      let path = '';
                                      
                                      if (selectedResource.type === 'service') {
                                        const serviceName = selectedResource.data?.name || selectedResource.name;
                                        const serviceType = selectedResource.data?.type || 'custom';
                                        path = `/design/revisions/${revisionName}/config/services/${serviceType}/${serviceName}/#general`;
                                      } else if (selectedResource.type === 'endpoint') {
                                        const endpointId = selectedResource.data?._id || selectedResource.name;
                                        path = `/design/revisions/${revisionName}/config/endpoints/${endpointId}/#general`;
                                      } else if (selectedResource.type === 'collection') {
                                        const collectionName = selectedResource.data?.name || selectedResource.name;
                                        path = `/design/revisions/${revisionName}/config/collections/${collectionName}`;
                                      } else if (selectedResource.type === 'fastData') {
                                        path = `/design/revisions/${revisionName}/config/fast-data/#general`;
                                      } else if (selectedResource.type === 'flowManager') {
                                        path = `/design/revisions/${revisionName}/config/flow-manager/flow-manager-service`;
                                      } else if (selectedResource.type === 'infrastructure') {
                                        path = `/design/revisions/${revisionName}/config/advanced/#general`;
                                      } else {
                                        path = `/design/revisions/${revisionName}/config/#general`;
                                      }
                                      
                                      const consoleUrl = `${config.consoleBaseUrl}/projects/${selectedProject.id}${path}`;
                                      window.open(consoleUrl, '_blank');
                                    }}
                                    title="Open in Console"
                                    type="button"
                                  >
                                    <Icon name="link" size={16} />
                                    {' Open in Console'}
                                  </button>
                                )}
                              </div>
                            </div>
                            {renderResourceDetails()}
                          </>
                        )}
                      </div>
                    ) : selectedSidebarView === 'dashboard' ? (
                      <DashboardView
                        selectedProject={memoizedSelectedProject}
                        selectedResource={selectedResource}
                        configuration={configuration}
                        onResourceSelect={handleResourceClick}
                        onOpenConsole={() => {
                          if (selectedProject && selectedProject.id) {
                            const consoleUrl = `${config.consoleBaseUrl}/projects/${selectedProject.id}/overview`;
                            window.open(consoleUrl, '_blank');
                          }
                        }}
                        onProjectDeleted={(deletedProjectId) => {
                          setProjects(prev => prev.filter(p => p.id !== deletedProjectId));
                          setSelectedProject(null);
                          if (onNavigateHome) onNavigateHome();
                        }}
                      />
                    ) : selectedSidebarView === 'architecture' ? (
                      <div className="architecture-view-container">
                        <div className="cloud-section">
                          <h2 className="cloud-section-title">
                            <Icon name="flow" size={20} />
                            {' Architecture Diagram'}
                          </h2>
                          {configuration && configuration.services && Object.keys(configuration.services).length > 0 ? (
                            <div className="architecture-container">
                              {(() => {
                                // Categorize services
                                const frontends = [];
                                const backends = [];
                                const apiGateways = [];
                                const otherServices = [];
                                
                                Object.entries(configuration.services).forEach(([serviceKey, service]) => {
                                  const detection = detectServiceType(service);
                                  const serviceName = service.name.toLowerCase();
                                  
                                  if (serviceName.includes('api-gateway') || serviceName.includes('gateway')) {
                                    apiGateways.push({key: serviceKey, service, detection});
                                  } else if (detection.isFrontend) {
                                    frontends.push({key: serviceKey, service, detection});
                                  } else if (detection.isBackend) {
                                    backends.push({key: serviceKey, service, detection});
                                  } else {
                                    otherServices.push({key: serviceKey, service, detection});
                                  }
                                });
                                
                                return (
                                  <div className="architecture-layers">
                                    {/* Frontend Layer */}
                                    {frontends.length > 0 && (
                                      <div className="arch-layer">
                                        <div className="arch-layer-label">{'Frontend Layer'}</div>
                                        <div className="arch-layer-content">
                                          {frontends.map(({key, service, detection}) => {
                                            const serviceLanguage = 'react';
                                            const languageIcon = getLanguageIcon(serviceLanguage, 3001);
                                            
                                            return (
                                              <div className="arch-service-card" key={key}>
                                                <div className="arch-service-icon">{languageIcon}</div>
                                                <div className="arch-service-name">{service.name}</div>
                                                <div className="arch-service-type">{'FRONTEND'}</div>
                                                {service.containerPorts && service.containerPorts.length > 0 && (
                                                  <div className="arch-service-ports">
                                                    {service.containerPorts.map((port, idx) => (
                                                      <span key={idx} className="arch-port">{':'}{port.to || port.from}</span>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                        {(apiGateways.length > 0 || backends.length > 0) && (
                                          <div className="arch-connector-down">
                                            <div className="connector-line"></div>
                                            <div className="connector-arrow">{'\u25bc'}</div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* API Gateway Layer */}
                                    {apiGateways.length > 0 && (
                                      <div className="arch-layer">
                                        <div className="arch-layer-label">{'API Gateway'}</div>
                                        <div className="arch-layer-content">
                                          {apiGateways.map(({key, service, detection}) => {
                                            const serviceLanguage = detection.isBackend ? 'node' : 'react';
                                            const languageIcon = getLanguageIcon(serviceLanguage, 8080);
                                            
                                            return (
                                              <div className="arch-service-card" key={key}>
                                                <div className="arch-service-icon">{languageIcon}</div>
                                                <div className="arch-service-name">{service.name}</div>
                                                <div className="arch-service-type">{'GATEWAY'}</div>
                                                {service.containerPorts && service.containerPorts.length > 0 && (
                                                  <div className="arch-service-ports">
                                                    {service.containerPorts.map((port, idx) => (
                                                      <span key={idx} className="arch-port">{':'}{port.to || port.from}</span>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                        {backends.length > 0 && (
                                          <div className="arch-connector-down">
                                            <div className="connector-line"></div>
                                            <div className="connector-arrow">{'\u25bc'}</div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Backend Layer */}
                                    {backends.length > 0 && (
                                      <div className="arch-layer">
                                        <div className="arch-layer-label">{'Backend Layer'}</div>
                                        <div className="arch-layer-content">
                                          {backends.map(({key, service, detection}) => {
                                            const serviceLanguage = 'node';
                                            const languageIcon = getLanguageIcon(serviceLanguage, 3014);
                                            
                                            return (
                                              <div className="arch-service-card" key={key}>
                                                <div className="arch-service-icon">{languageIcon}</div>
                                                <div className="arch-service-name">{service.name}</div>
                                                <div className="arch-service-type">{'BACKEND'}</div>
                                                {service.containerPorts && service.containerPorts.length > 0 && (
                                                  <div className="arch-service-ports">
                                                    {service.containerPorts.map((port, idx) => (
                                                      <span key={idx} className="arch-port">{':'}{port.to || port.from}</span>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Other Services Layer */}
                                    {otherServices.length > 0 && (
                                      <div className="arch-layer">
                                        <div className="arch-layer-label">{'Other Services'}</div>
                                        <div className="arch-layer-content">
                                          {otherServices.map(({key, service, detection}) => {
                                            const serviceLanguage = 'docker';
                                            const languageIcon = getLanguageIcon(serviceLanguage, 8080);
                                            
                                            return (
                                              <div className="arch-service-card" key={key}>
                                                <div className="arch-service-icon">{languageIcon}</div>
                                                <div className="arch-service-name">{service.name}</div>
                                                <div className="arch-service-type">{'SERVICE'}</div>
                                                {service.containerPorts && service.containerPorts.length > 0 && (
                                                  <div className="arch-service-ports">
                                                    {service.containerPorts.map((port, idx) => (
                                                      <span key={idx} className="arch-port">{':'}{port.to || port.from}</span>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          ) : (
                            <div className="empty-state">
                              <Icon name="flow" size={48} />
                              <p>{'No services configured'}</p>
                              <p className="hint">{'Add services to see the architecture diagram'}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="code-editor-empty">
                        <p>{'Select a file or microservice to view details'}</p>
                      </div>
                    )}
                  </div>

                  {/* Split View Preview Panel - Only shown in split mode */}
                  {splitViewMode === 'split' && previewTabs.length > 0 && (
                    <>
                      {/* Split Resize Handle */}
                      <div 
                        className={`resize-handle ${isResizingSplit ? 'active' : ''}`}
                        onMouseDown={() => setIsResizingSplit(true)}
                      />
                      <div className="split-preview-panel" style={{width: `${100 - splitRatio}%`}}>
                        <div className="split-preview-header">
                          <span className="preview-title">
                            <Icon name="eye" size={16} />
                            Live Preview
                          </span>
                          <div className="preview-actions">
                            <button
                              className="preview-action-btn"
                              onClick={handleCaptureThumbnail}
                              disabled={isCapturingThumbnail || !selectedProject}
                              title={isCapturingThumbnail ? "Capturing thumbnail..." : "Capture thumbnail"}
                              type="button"
                            >
                              {isCapturingThumbnail ? (
                                <span className="capture-spinner">⏳</span>
                              ) : (
                                <Icon name="camera" size={14} />
                              )}
                            </button>
                            <button
                              className="preview-action-btn"
                              onClick={() => {
                                const activeTab = previewTabs[activePreviewTab];
                                if (activeTab?.url) {
                                  window.open(activeTab.url, '_blank', 'noopener,noreferrer');
                                }
                              }}
                              disabled={!previewTabs[activePreviewTab]?.url}
                              title="Open in new tab"
                              type="button"
                            >
                              <Icon name="link" size={14} />
                            </button>
                            <button
                              className="preview-action-btn"
                              onClick={() => setSplitViewMode('editor')}
                              title="Close preview panel"
                              type="button"
                            >
                              <Icon name="close" size={14} />
                            </button>
                          </div>
                      </div>
                      <div className="split-preview-content">
                        {previewTabs[activePreviewTab]?.loading ? (
                          <div className="preview-loading">
                            <div className="loading-spinner" />
                            <p>{previewTabs[activePreviewTab]?.pullingCode 
                              ? `Pulling code from repository...` 
                              : `Rendering preview...`
                            }</p>
                          </div>
                        ) : previewTabs[activePreviewTab]?.error ? (
                          (() => {
                            const errMsg = previewTabs[activePreviewTab].error;
                            if (errMsg.startsWith('__GIT_TOOL_DISABLED__:') || errMsg.startsWith('__GIT_NOT_AUTHENTICATED__:')) {
                              const isDisabled = errMsg.startsWith('__GIT_TOOL_DISABLED__:');
                              const pName = errMsg.split(':')[1];
                              const pIconName = pName === 'GitHub' ? 'github' : pName === 'Azure DevOps' ? 'azure-devops' : 'gitlab';
                              return (
                                <div className={`preview-git-blocked ${isDisabled ? 'tool-disabled' : 'not-connected'}`}>
                                  <div className="preview-git-blocked-icon">
                                    <Icon name={pIconName} size={48} glow={true} />
                                  </div>
                                  <h3>{isDisabled ? `${pName} Integration Disabled` : `${pName} Not Connected`}</h3>
                                  <p>{isDisabled ? `Enable the ${pName} integration in Tools.` : `Connect your ${pName} account in Connectors.`}</p>
                                  <button className="preview-git-blocked-btn" onClick={() => { window.location.href = '/tools'; }} type="button">
                                    {isDisabled ? '🔧 Go to Tools' : '🔑 Connect Account'}
                                  </button>
                                </div>
                              );
                            }
                            return (
                              <div className="preview-error">
                                <Icon name="warning" size={24} />
                                <p>{errMsg}</p>
                              </div>
                            );
                          })()
                        ) : previewTabs[activePreviewTab]?.url ? (
                          <iframe
                            className="split-preview-iframe"
                            key={previewTabs[activePreviewTab].iframeKey}
                            sandbox="allow-scripts allow-same-origin allow-forms"
                            src={previewTabs[activePreviewTab].url}
                            title="Split View Preview"
                            onLoad={(e) => handleIframeLoad(e, previewTabs[activePreviewTab])}
                          />
                        ) : (
                          <div className="preview-empty">
                            <Icon name="eye" size={32} />
                            <p>No preview available</p>
                            <p className="hint">Start a service to see live preview</p>
                          </div>
                        )}
                      </div>
                    </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* AI TAB */}
          {activeTab === 'ai' && (
            <div className="canvas-cloud-view">
              {selectedProject && selectedRevision && configuration ? (
                <div className="cloud-observability-container">
                  {/* Cloud Observability Tabs */}
                  <div className="cloud-tabs-bar">
                 
                  
                    <button
                      className={`cloud-tab-btn ${cloudTab === 'agents' ? 'active' : ''}`}
                      onClick={() => setCloudTab('agents')}
                      type="button"
                    >
                      <Icon name="robot" size={16} />
                      {' Background Agents'}
                    </button>
                  </div>


                  {/* Background Agents Tab. FIXME: is the only tab, added || true for select it */}
                 
                  {cloudTab === 'agents' || true && (
                    <div className="cloud-section">
                      <h2 className="cloud-section-title">
                        <Icon name="robot" size={20} />
                        {' Background Agents'}
                      </h2>
                      <BackgroundAgentsManager />
                    </div>
                  )}


                </div>
              ) : (
                <div className="cloud-empty-state">
                  <Icon name="computer" size={64} />
                  <h2>{'Select a project to view cloud observability'}</h2>
                  <p>{'Choose an organization, project, and revision from the sidebar to see services, containers, and configurations'}</p>
                </div>
              )}
            </div>
          )}

          {/* REQUIREMENTS DLC TAB */}
          {activeTab === 'requirements' && (
            <RequirementsDLCPanel
              projectContext={{
                projectId: selectedProject?.id,
                revision: selectedRevision,
                configuration,
              }}
            />
          )}
        </div>

        {/* Enhanced Status Bar - Visible only in preview tab when there are active services and not viewing configuration */}
        {activeTab === 'preview' && previewTabs && previewTabs.length > 0 && selectedResource?.type !== 'configuration' && (
          <EnhancedStatusBar
            previewTabs={previewTabs}
            activePreviewTab={activePreviewTab}
            activityLogs={activityLogs}
            logErrorCount={logErrorCount}
            onCaptureThumbnail={handleCaptureThumbnail}
            isCapturingThumbnail={isCapturingThumbnail}
            onOpenApp={() => {
              const activeTab = previewTabs[activePreviewTab];
              if (activeTab?.url) {
                window.open(activeTab.url, '_blank', 'noopener,noreferrer');
              }
            }}
            onViewLogs={() => {
              // Toggle logs expansion
              setLogsExpanded(prev => !prev);
            }}
            onRestart={async () => {
              const activeTab = previewTabs[activePreviewTab];
              if (activeTab?.sessionId && activeTab?.containerPort) {
                await controlContainer(activeTab.sessionId, activeTab.containerPort, 'restart');
              }
            }}
            onViewSettings={() => {
              // Switch to code tab to view service configuration
              setActiveTab('code');
            }}
            syncStatus={compilationStatus === 'syncing' ? 'syncing' : compilationStatus === 'ready' ? 'synced' : null}
            lastSyncTime={Date.now()} // You could track this more accurately with a state variable
          />
        )}

        {/* Canvas Footer - Hidden for service preview (with tabs or URL), code mode, summary tab, and configuration */}
        {!(activeTab === 'preview' && 
           selectedResource?.type === 'service' && 
           (() => {
             const detection = detectServiceType(selectedResource.data);
             return detection.isFrontend || detection.isBackend;
           })() &&
           (previewUrl || previewTabs.length > 0)) && 
         activeTab !== 'code' && 
         activeTab !== 'summary' && 
         selectedResource?.type !== 'configuration' &&
         activeTab === 'preview' && (
          <div className="canvas-footer">
            <div className="canvas-footer-left">
              <span className="canvas-word-count">
                {contentType === 'image' ? 
                  `${content.aspect_ratio || 'Custom'} aspect ratio` : 
                  contentType === 'video' ?
                  `${content.duration_seconds || '?'}s · ${content.aspect_ratio || 'Custom'}` :
                  `${content.content?.split(/\s+/).length || 0} words`
                }
              </span>
            </div>
          
          <div className="canvas-footer-right">
            {contentType === 'image' ? (
              <>
                <button
                  className="canvas-action-btn secondary"
                  onClick={() => {
                    // For images, open in new tab
                    const win = window.open();
                    win.document.write(`<img src="${content.content}" alt="Generated image" style="max-width: 100%; height: auto;" />`);
                  }}
                  type="button"
                >
                  {'Open'}
                </button>
                <button
                  className="canvas-action-btn primary"
                  onClick={() => {
                    // Download image
                    const link = document.createElement('a');
                    link.href = content.content;
                    link.download = `${content.title || 'generated-image'}.png`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  type="button"
                >
                  {'Download'}
                </button>
              </>
            ) : contentType === 'video' ? (
              <>
                <button
                  className="canvas-action-btn secondary"
                  onClick={() => {
                    // For videos, open in new tab
                    const win = window.open();
                    win.document.write(`<video src="${content.content}" controls style="max-width: 100%; height: auto;"></video>`);
                  }}
                  type="button"
                >
                  {'Open'}
                </button>
                <button
                  className="canvas-action-btn primary"
                  onClick={() => {
                    // Download video
                    const link = document.createElement('a');
                    link.href = content.content;
                    link.download = `${content.title || 'generated-video'}.mp4`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  type="button"
                >
                  {'Download'}
                </button>
              </>
            ) : (
              <>
                <button
                  className="canvas-action-btn secondary"
                  onClick={() => navigator.clipboard.writeText(content.content)}
                  type="button"
                >
                  {'Copy'}
                </button>
                <button
                  className="canvas-action-btn primary"
                  onClick={() => {
                    const blob = new Blob([content.content], {type: 'text/plain'});
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${content.title || 'document'}.${contentType === 'code' ? detectedLanguage : 'md'}`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  }}
                  type="button"
                >
                  {'Download'}
                </button>
              </>
            )}
          </div>
        </div>
        )}



      {/* Deployment Modal */}
      <Modal
        confirmText={deploymentPipelineId ? 'Close' : 'Deploy'}
        icon="🚀"
        isDangerous={false}
        isLoading={deploymentLoading}
        isOpen={showDeploymentModal}
        onCancel={handleDeploymentClose}
        onClose={handleDeploymentClose}
        onConfirm={deploymentPipelineId ? handleDeploymentClose : handleDeploymentConfirm}
        showCancel={!deploymentPipelineId}
        title={`Deploy to ${selectedEnvironment?.label || selectedEnvironment?.envId || 'Environment'}`}
        type="custom"
      >
        <div className="deployment-modal-content">
          {deploymentError && (
            <div className="deployment-error">
              <span className="error-icon">{'❌'}</span>
              <span className="error-message">{deploymentError}</span>
            </div>
          )}

          {!deploymentPipelineId && !deploymentError && (
            <div className="deployment-info">
              <p className="deployment-message">
                {'This will deploy all services using the '}
                <strong>{selectedRevision?.name || selectedRevision?.id || 'current'}</strong>
                {' revision to the '}
                <strong>{selectedEnvironment?.label || selectedEnvironment?.envId}</strong>
                {' environment.'}
              </p>
              <div className="deployment-details">
                <div className="deployment-detail-item">
                  <span className="detail-label">{'Project:'}</span>
                  <span className="detail-value">{selectedProject?.name}</span>
                </div>
                <div className="deployment-detail-item">
                  <span className="detail-label">{'Environment:'}</span>
                  <span className="detail-value">{selectedEnvironment?.label || selectedEnvironment?.envId}</span>
                </div>
                <div className="deployment-detail-item">
                  <span className="detail-label">{'Namespace:'}</span>
                  <span className="detail-value">{selectedEnvironment?.cluster?.namespace || 'N/A'}</span>
                </div>
                <div className="deployment-detail-item">
                  <span className="detail-label">{'Revision:'}</span>
                  <span className="detail-value">{selectedRevision?.name || selectedRevision?.id}</span>
                </div>
              </div>
            </div>
          )}

          {deploymentPipelineId && (
            <div className="deployment-status">
              <div className="pipeline-info">
                <div className="pipeline-detail-item">
                  <span className="detail-label">{'Pipeline ID:'}</span>
                  <span className="detail-value">{'#'}{deploymentPipelineId}</span>
                </div>
                {pipelineStatus?.status && (
                  <div className="pipeline-status-display">
                    <span className={`pipeline-status-badge ${getPipelineStatusClass(pipelineStatus.status)}`}>
                      {getPipelineStatusIcon(pipelineStatus.status)} {pipelineStatus.status}
                    </span>
                    {pipelinePolling && (
                      <span className="pipeline-polling-indicator">{'⏳ Polling...'}</span>
                    )}
                  </div>
                )}
              </div>

              {pipelineStatus?.webUrl && (
                <div className="pipeline-actions">
                  <ExternalLinkButton
                    href={pipelineStatus.webUrl}
                    variant="primary"
                  >
                    {'View Pipeline in GitLab'}
                  </ExternalLinkButton>
                </div>
              )}

              {pipelineStatus?.stages && pipelineStatus.stages.length > 0 && (
                <div className="pipeline-stages">
                  <h4>{'Pipeline Stages'}</h4>
                  <div className="stages-list">
                    {pipelineStatus.stages.map((stage, idx) => (
                      <div className="stage-item" key={idx}>
                        <span className="stage-icon">{getPipelineStatusIcon(stage.status)}</span>
                        <span className="stage-name">{stage.name}</span>
                        <span className={`stage-status ${getPipelineStatusClass(stage.status)}`}>
                          {stage.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!pipelineStatus && !deploymentError && (
                <p className="loading-message">{'⏳ Loading pipeline status...'}</p>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Pods Status Modal */}
      <Modal
        confirmText="Close"
        icon="📦"
        isOpen={showPodsModal}
        onClose={handlePodsModalClose}
        showCancel={false}
        title={`Services in ${podsEnvironment?.label || podsEnvironment?.envId || 'Environment'}`}
        type="custom"
      >
        <div className="pods-modal-content">
          {podsError && (
            <div className="deployment-error">
              <span className="error-icon">{'❌'}</span>
              <span className="error-message">{podsError}</span>
            </div>
          )}

          {podsLoading && (
            <p className="loading-message">{'⏳ Loading services status...'}</p>
          )}

          {!podsLoading && !podsError && podsData.length === 0 && (
            <p className="loading-message">{'No services found in this environment'}</p>
          )}

          {!podsLoading && !podsError && podsData.length > 0 && (
            <div className="pods-list">
              {podsData.map((pod, idx) => (
                <div className="pod-item" key={idx}>
                  <div className="pod-header">
                    <div className="pod-name-status">
                      <span className="pod-icon">{getPodStatusIcon(pod.status?.phase)}</span>
                      <span className="pod-name">{pod.metadata?.name || 'Unknown'}</span>
                    </div>
                    <span className={`pod-status-badge ${getPodStatusClass(pod.status?.phase)}`}>
                      {pod.status?.phase || 'Unknown'}
                    </span>
                  </div>
                  
                  <div className="pod-details">
                    {pod.metadata?.labels?.['mia-platform.eu/name'] && (
                      <div className="pod-detail-row">
                        <span className="pod-detail-label">{'Service:'}</span>
                        <span className="pod-detail-value">{pod.metadata.labels['mia-platform.eu/name']}</span>
                      </div>
                    )}
                    {pod.spec?.nodeName && (
                      <div className="pod-detail-row">
                        <span className="pod-detail-label">{'Node:'}</span>
                        <span className="pod-detail-value">{pod.spec.nodeName}</span>
                      </div>
                    )}
                    {pod.status?.podIP && (
                      <div className="pod-detail-row">
                        <span className="pod-detail-label">{'IP:'}</span>
                        <span className="pod-detail-value">{pod.status.podIP}</span>
                      </div>
                    )}
                    {pod.status?.startTime && (
                      <div className="pod-detail-row">
                        <span className="pod-detail-label">{'Started:'}</span>
                        <span className="pod-detail-value">
                          {new Date(pod.status.startTime).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {pod.status?.containerStatuses && pod.status.containerStatuses.length > 0 && (
                      <div className="pod-containers">
                        <span className="pod-detail-label">{'Containers:'}</span>
                        <div className="container-list">
                          {pod.status.containerStatuses.map((container, cIdx) => (
                            <div className="container-item" key={cIdx}>
                              <span className="container-icon">
                                {container.ready ? '✅' : '⏳'}
                              </span>
                              <span className="container-name">{container.name}</span>
                              <span className="container-restarts">
                                {container.restartCount > 0 && `🔄 ${container.restartCount}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Pipeline Status Modal */}
      <Modal
        confirmText="Close"
        icon="🚀"
        isOpen={showPipelineModal}
        onClose={() => setShowPipelineModal(false)}
        showCancel={false}
        title="Pipeline Status"
        type="custom"
      >
        <div className="pipeline-status-modal">
          {currentPipelineStatus ? (
            <>
              <div className="pipeline-info">
                <div className="pipeline-detail">
                  <strong>Status:</strong>
                  <span className={`pipeline-status-badge status-${currentPipelineStatus.status}`}>
                    {currentPipelineStatus.status}
                  </span>
                </div>
                {currentPipelineStatus.id && (
                  <div className="pipeline-detail">
                    <strong>Pipeline ID:</strong>
                    <span>#{currentPipelineStatus.id}</span>
                  </div>
                )}
                {currentPipelineStatus.ref && (
                  <div className="pipeline-detail">
                    <strong>Branch:</strong>
                    <span>{currentPipelineStatus.ref}</span>
                  </div>
                )}
                {currentPipelineStatus.sha && (
                  <div className="pipeline-detail">
                    <strong>Commit:</strong>
                    <span>{currentPipelineStatus.sha.substring(0, 8)}</span>
                  </div>
                )}
              </div>
              
              {currentPipelineStatus.webUrl && (
                <div className="pipeline-actions">
                  <ExternalLinkButton
                    href={currentPipelineStatus.webUrl}
                    variant="primary"
                  >
                    View in Tool
                  </ExternalLinkButton>
                </div>
              )}

              {currentPipelineStatus.stages && currentPipelineStatus.stages.length > 0 && (
                <div className="pipeline-stages">
                  <h4>Pipeline Stages</h4>
                  <div className="stages-list">
                    {currentPipelineStatus.stages.map((stage, idx) => (
                      <div className="stage-item" key={idx}>
                        <span className="stage-name">{stage.name}</span>
                        <span className={`stage-status status-${stage.status}`}>
                          {stage.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="modal-message">No pipeline information available</p>
          )}
        </div>
      </Modal>

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={isCreateProjectModalOpen}
        onClose={() => setIsCreateProjectModalOpen(false)}
        onCreateProject={handleCreateProject}
        selectedTenant={selectedTenant}
      />

      <CreateServiceModal
        isOpen={isCreateServiceModalOpen}
        onClose={() => setIsCreateServiceModalOpen(false)}
        onCreate={handleCreateService}
        projectId={selectedProject?.id || selectedProject?._id}
        revisionId={selectedRevision?.id || selectedRevision?.name}
        tenantId={selectedTenant?.tenantId}
      />

      {/* Push Modal */}
      <PushModal
        isOpen={pushModalOpen}
        onClose={() => setPushModalOpen(false)}
        projectId={selectedProject?.id || selectedProject?._id}
        serviceName={selectedResource?.data?.name || selectedResource?.name}
        repositoryUrl={selectedResource?.data?.repositoryUrl || 
                       selectedResource?.data?.repoUrl || 
                       selectedResource?.data?.sshUrl ||
                       selectedResource?.repositoryUrl ||
                       selectedResource?.repoUrl ||
                       selectedResource?.sshUrl}
        gitProjectId={(() => {
          const repoUrl = selectedResource?.data?.repositoryUrl || 
                         selectedResource?.data?.repoUrl || 
                         selectedResource?.data?.sshUrl ||
                         selectedResource?.repositoryUrl ||
                         selectedResource?.repoUrl ||
                         selectedResource?.sshUrl;
          if (!repoUrl) return null;
          try {
            const url = new URL(repoUrl.replace('.git', ''));
            return url.pathname.substring(1); // Remove leading /
          } catch {
            // Fallback: extract project path from URL string
            return repoUrl.split('/').slice(-2).join('/').replace('.git', '');
          }
        })()}
        branch={getResourceGitBranch(selectedResource, selectedProject?.id, 'main')}
      />

      {/* Build Error Modal */}
      <Modal
        isOpen={buildErrorModal.isOpen}
        onClose={() => setBuildErrorModal({ isOpen: false, error: null, serviceName: null })}
        title={`❌ Build Failed: ${buildErrorModal.serviceName || 'Preview'}`}
        type="custom"
        showCancel={false}
        confirmText="Close"
        className="build-error-modal"
        icon="⚠️"
      >
        <div
          className="build-error-output"
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '16px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '13px',
            lineHeight: '1.6',
            whiteSpace: 'pre-wrap',
          }}
        >
          {buildErrorModal.error}
        </div>
        <div
          className="build-error-next-steps"
          style={{
            marginTop: '16px',
            padding: '12px',
            border: '1px solid #ffc107',
            borderRadius: '4px',
          }}
        >
          <strong>💡 Next Steps:</strong>
          <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
            <li>Review the error message above for specific file and line numbers</li>
            <li>Fix the issues in your code (check exports, syntax, imports)</li>
            <li>The preview will automatically rebuild after you save your changes</li>
          </ul>
        </div>
        <div style={{
          marginTop: '16px',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          {(() => {
            // Extract file paths from error message
            // Look for patterns like "src/App.tsx:3:7" or "at src/main.tsx:3:7"
            const fileMatches = buildErrorModal.error?.match(/(?:at\s+)?(?:src\/|\.\/)?([A-Za-z0-9_\-./]+\.(tsx?|jsx?|css|json|html))(?::(\d+))?/g) || [];
            const files = [...new Set(fileMatches.map(match => {
              const cleaned = match.replace(/^at\s+/, '').replace(/^\.\//, '');
              return cleaned.includes('src/') ? cleaned.split(':')[0] : `src/${cleaned.split(':')[0]}`;
            }))];
            
            return files.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  // Close the modal first so the user sees immediate feedback
                  setBuildErrorModal({ isOpen: false, error: null, serviceName: null });

                  // Then navigate to the file in the code editor
                  const targetFile = generatedFiles?.find(f =>
                    files.some(errorFile => f.path.endsWith(errorFile) || errorFile.endsWith(f.path))
                  );
                  if (targetFile) {
                    setSelectedFile(targetFile);
                    setSelectedResource(null);
                  }
                  setActiveTab('code');
                }}
                style={{
                  padding: '10px 20px',
                  background: '#16825d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => e.target.style.background = '#0e6b4d'}
                onMouseOut={(e) => e.target.style.background = '#16825d'}
              >
                📄 Open File in Code
              </button>
            );
          })()}
          {onAskAI && (
            <button
              type="button"
              onClick={() => {
                const errorContext = `When building ${buildErrorModal.serviceName || 'React'} I'm getting the following errors:\n\n${buildErrorModal.error}\n\nFix them in the canvas.`;

                // Close the modal first so the user sees immediate feedback
                setBuildErrorModal({ isOpen: false, error: null, serviceName: null });

                // Then perform the side-effects
                setPreviewTabs([]);
                setActiveTab('code');
                onAskAI(errorContext);
              }}
              style={{
                padding: '10px 20px',
                background: '#007acc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseOver={(e) => e.target.style.background = '#005a9e'}
              onMouseOut={(e) => e.target.style.background = '#007acc'}
            >
              🤖 Ask AI for Help
            </button>
          )}
          <button
            className="build-error-close-btn"
            onClick={() => setBuildErrorModal({ isOpen: false, error: null, serviceName: null })}
            style={{
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
})

Canvas.displayName = 'Canvas';

Canvas.propTypes = {
  chatSidebarWidth: PropTypes.number,
  content: PropTypes.shape({
    aspect_ratio: PropTypes.string,
    content: PropTypes.string.isRequired,
    duration_seconds: PropTypes.number,
    prompt: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.oneOf(['document', 'code', 'image', 'video']),
  }),
  generatedFiles: PropTypes.arrayOf(PropTypes.shape({
    content: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
  })),
  initialBranch: PropTypes.string,
  initialMicroservice: PropTypes.string,
  initialTab: PropTypes.string,
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onNavigateHome: PropTypes.func,
  onNewConversation: PropTypes.func,
  onProjectContextChange: PropTypes.func,
  onSelectionChange: PropTypes.func,
  onShowHistory: PropTypes.func,
  onAskAI: PropTypes.func,
  onWebLLMStatusChange: PropTypes.func,
  projectContext: PropTypes.object,
};

export { clearAllPreviewSessions };
export default Canvas;
